<!-- app-subdomain-creators\src\Components\NewsFeedFullPostSlug.vue -->

<template>
  <Transition
    enter-active-class="ease-out duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="ease-in duration-200"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="(props.inlineSidebar || props.isOpen) && props.post"
      :class="props.inlineSidebar ? 'h-full w-full' : 'fixed inset-0 bg-white bg-opacity-75 flex justify-center items-center py-4 z-50'"
      @click.self="props.inlineSidebar ? null : close()"
    >
      <!-- Modal -->
      <div
        class="relative w-full bg-white"
        :class="props.inlineSidebar ? 'h-full max-w-none mx-0 rounded-none border-0 shadow-none overflow-visible' : 'max-w-[700px] mx-4 rounded-2xl border border-zinc-200 shadow-[0_10px_60px_rgba(0,0,0,0.20)] overflow-hidden'"
      >
        <!-- Top bar -->
        <div v-if="!props.inlineSidebar" class="relative h-14 flex items-center justify-center border-b border-zinc-200 bg-white">
          <div class="text-[18px] font-semibold text-zinc-900">
            {{ props.post?.authorName ? `${props.post.authorName}'s Post` : 'Post' }}
          </div>

          <button
            type="button"
            class="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center"
            @click="close"
            aria-label="Close"
          >
            <XMarkIcon class="w-7 text-zinc-700"></XMarkIcon>
          </button>
        </div>

        <!-- Scroll body -->
        <div
          ref="scrollBodyEl"
          class="app-scroll flex flex-col pb-6"
          :class="props.inlineSidebar ? 'h-full overflow-y-auto overflow-x-visible' : 'max-h-[86vh] overflow-y-auto overflow-x-hidden'"
        >
          <!-- Post container (bigger version) -->
          <div class="relative w-full bg-white flex flex-col min-h-0">
            <!-- SCROLLING CONTENT -->
            <div class="flex-1 min-h-0">
              <!-- Header -->
              <div
                class="px-5 pt-2 pb-2 flex items-start justify-between"
                :class="!rawBodyTrimmed ? 'border-b border-zinc-200' : ''"
              >
                <div class="flex items-start gap-3">
                  <img
                    :src="authorAvatarSrc"
                    alt="Author avatar"
                    class="h-11 w-11 rounded-full object-cover"
                    @error="onAvatarLoadError"
                  />

                  <div>
                    <p class="text-[15px] font-semibold text-zinc-900 -mb-1">
                      {{ props.post?.authorName || 'User' }}
                    </p>

                    <div class="relative group/time inline-block">
                      <p class="text-xs font-medium text-zinc-500 cursor-pointer hover:underline">
                        {{ displayTime }}
                      </p>

                      <p
                        class="pointer-events-none absolute hidden group-hover/time:block px-2 py-3 text-xs rounded-2xl
                             bg-black/70 text-white whitespace-normal z-50 top-7 left-0 shadow-lg w-64 text-center"
                      >
                        {{ fullTimeTooltip }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Body -->
              <div
                v-if="rawBody"
                :class="hasTextBackground ? 'px-0 pb-3 pt-2' : 'px-5 pb-2 pt-2'"
              >
                <div
                  :class="hasTextBackground ? 'aspect-[16/9] min-h-[340px] flex items-center justify-center text-center px-8 py-6 bg-cover bg-center bg-no-repeat' : ''"
                  :style="textBackgroundStyle"
                >
                  <p
                    :class="[bodyTextClass, 'leading-snug whitespace-pre-line break-words', hasTextBackground ? backgroundTextToneClass : 'text-zinc-800']"
                  >
                    <span v-html="displayBodyHtml"></span>
                    <button
                      v-if="isBodyLong && !hasTextBackground"
                      type="button"
                      :class="hasTextBackground ? `inline text-sm font-semibold ml-1 hover:underline ${backgroundTextButtonClass}` : 'inline text-sm font-semibold text-zinc-800 hover:text-zinc-900 ml-1 hover:underline'"
                      @click.stop="showFullBody = !showFullBody"
                    >
                      {{ showFullBody ? 'Show less' : 'See more' }}
                    </button>
                  </p>
                </div>
              </div>

              <NewsfeedPostPollCard
                v-if="hasPoll"
                :post-id="post.id"
                :post="post"
                :api-proxy-url="apiProxyUrl"
                :can-manage="canManagePoll"
              />

              <!-- Media (mirrors your post logic) -->
              <div v-if="hasMedia && !hasPoll && !props.inlineSidebar" class="">
                <!-- Single video -->
                <div
                  v-if="displayMediaItems.length === 1 && displayMediaItems[0].type === 'video'"
                  class="w-full bg-black flex items-center justify-center overflow-hidden"
                >
                  <div class="w-full">
                    <NewsfeedInlineVideoPlayer
                      :file-id="displayMediaItems[0].fileId ? String(displayMediaItems[0].fileId) : null"
                      :src="displayMediaItems[0].src"
                      :poster="displayMediaItems[0].thumbnailSrc || displayMediaItems[0].poster || null"
                      :autoplay="true"
                      :muted="true"
                      :max-height="680"
                      @open-viewer="openViewerAt(0)"
                    />
                  </div>
                </div>

                <!-- Single image -->
                <div
                  v-else-if="displayMediaItems.length === 1"
                  class="w-full bg-black flex items-center justify-center overflow-hidden cursor-pointer"
                  @click="openViewerAt(0)"
                >
                  <img
                    :src="displayMediaItems[0].src"
                    alt="Post media"
                    class="max-h-[70vh] max-w-full w-auto object-contain"
                  />
                </div>

                <!-- Grid -->
                <div
                  v-else
                  class="w-full bg-black flex items-center justify-center overflow-hidden"
                >
                  <div class="w-full max-h-[70vh] overflow-hidden">
                    <div class="grid w-full gap-[3px] bg-white" :class="displayGridClass">
                      <div
                        v-for="(item, idx) in displayMediaItems"
                        :key="idx"
                        :class="['bg-black cursor-pointer', displayTileClass(idx)]"
                        @click="handleMediaTileClick(item, idx)"
                      >
                        <NewsfeedInlineVideoPlayer
                          v-if="item.type === 'video' && isInlinePlayingVideoTile(idx)"
                          :file-id="item.fileId ? String(item.fileId) : null"
                          :src="item.src"
                          :poster="item.thumbnailSrc || item.poster || null"
                          :autoplay="true"
                          :muted="true"
                          :max-height="680"
                          :fill-container="true"
                          @open-viewer="openViewerAt(idx)"
                        />

                        <img
                          v-else
                          :src="item.type === 'video' ? (item.thumbnailSrc || item.src) : item.src"
                          alt="Post media"
                          class="w-full h-full object-cover"
                        />

                        <div
                          v-if="item.type === 'video' && !isInlinePlayingVideoTile(idx)"
                          class="absolute inset-0 flex items-center justify-center pointer-events-none"
                        >
                          <div class="rounded-full bg-black/60 p-2">
                            <PlayIcon class="w-6 h-6 text-white" />
                          </div>
                        </div>

                        <button
                          v-if="item.type === 'video' && !isInlinePlayingVideoTile(idx)"
                          type="button"
                          class="absolute inset-0 z-10"
                          @click.stop="playInlineVideoAt(idx)"
                          aria-label="Play video"
                        ></button>

                        <div
                          v-if="idx === displayMediaItems.length - 1 && extraMediaCount > 0"
                          class="absolute inset-0 bg-black/45 flex items-center justify-center cursor-pointer"
                        >
                          <span class="text-white text-2xl font-semibold">
                            +{{ extraMediaCount }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Reactions summary (simple, mirrors your counts) -->
              <div
                v-if="hasEngagement"
                class="flex items-center mx-5 py-3 text-sm text-zinc-500"
                :class="hasReactions && hasComments ? 'justify-between' : 'justify-start'"
              >
                <div
                  v-if="hasReactions"
                  class="relative flex items-center gap-2"
                >
                  <div class="flex items-center -space-x-0.5">
                    <div
                      v-for="(type, index) in summaryTypesToShow"
                      :key="type"
                      class="relative group cursor-pointer"
                      @mouseenter="(e) => handleReactionTooltipEnter(type, e)"
                      :style="{ zIndex: summaryTypesToShow.length - index }"
                    >
                      <div class="flex items-center justify-center rounded-full h-5 w-5 ring-2 ring-white">
                        <img :src="REACTION_META[type].icon" :alt="REACTION_META[type].label" class="h-4.5 w-4.5" />
                      </div>

                      <!-- Tooltip per reaction type, auto-flips above/below -->
                      <div
                        v-if="tooltipNamesForType(type).length || tooltipFallbackText(type)"
                        class="pointer-events-none absolute z-40 px-3 py-2 rounded-xl bg-black/80 text-[11px] text-white shadow-lg w-40
                               opacity-0 group-hover:opacity-100 transition-all duration-150"
                        :class="reactionTooltipPosition[type] === 'above'
                          ? 'bottom-full mb-2'
                          : 'top-full mt-2'"
                        style="left: 0;"
                      >
                        <span class="block font-semibold mb-1">
                          {{ REACTION_META[type].label }}
                        </span>

                        <div class="leading-tight">
                          <div
                            v-for="name in tooltipNamesForType(type)"
                            :key="name"
                            class="truncate"
                          >
                            {{ name }}
                          </div>

                          <div v-if="!tooltipNamesForType(type).length && tooltipFallbackText(type)">
                            {{ tooltipFallbackText(type) }}
                          </div>

                          <div
                            v-if="tooltipExtraCountForType(type) > 0"
                            class="mt-1 text-[11px] text-zinc-300"
                          >
                            +{{ tooltipExtraCountForType(type) }} more
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <span class="text-base text-zinc-500">
                    {{ totalReactions }}
                  </span>
                </div>

                <div v-if="hasComments" class="flex items-center gap-4" :class="!hasReactions ? 'ml-auto' : ''">
                  <span class="text-base text-zinc-500">
                    {{ localCommentsCount }} comments
                  </span>
                </div>
              </div>

              <!-- Action bar -->
              <div class="px-4">
                <div class="border-t border-b border-zinc-200 py-[3px]">
                  <div class="flex items-center justify-between">
                    <!-- LIKE + hover reaction picker (wired) -->
                    <div
                      class="relative flex-1 flex justify-center nf-picker-anchor"
                      @mouseenter="handleReactionMouseEnter"
                      @mouseleave="handleReactionMouseLeave"
                    >
                      <button
                        type="button"
                        class="flex items-center justify-center gap-1.5 px-3 py-1 rounded-md
                               hover:bg-zinc-100 text-sm font-medium w-full"
                        @click="handleLikeButtonClick"
                      >
                        <span
                          :class="[
                            'newsfeed-like-sprite',
                            hasUserLiked ? 'newsfeed-like-sprite--active' : ''
                          ]"
                        ></span>

                        <span
                          :class="[
                            'text-sm',
                            hasUserLiked ? 'text-[#2D8CFF]' : 'text-zinc-600'
                          ]"
                        >
                          Like
                        </span>
                      </button>

                      <!-- Hover popover (desktop-style) -->
                      <NewsfeedReactionPicker
                        v-if="isReactionDockOpen"
                        :portal="props.inlineSidebar"
                        :position-class="props.inlineSidebar ? 'absolute bottom-full mb-2 right-0' : 'absolute bottom-full mb-2 left-0'"
                        @select="handleReactionSelect"
                        @hover-change="handlePostPickerHoverChange"
                      />
                    </div>

                    <!-- COMMENT -->
                    <button
                      type="button"
                      class="flex items-center justify-center gap-1.5 px-3 py-1 rounded-md hover:bg-zinc-100 text-sm font-medium flex-1"
                      @click="focusCommentBox"
                    >
                      <span class="newsfeed-comment-sprite opacity-50"></span>
                      <span class="text-zinc-600 text-sm">Comment</span>
                    </button>

                    <!-- SEND -->
                    <button
                      type="button"
                      class="flex items-center justify-center gap-1.5 px-3 py-1 rounded-md hover:bg-zinc-100 text-sm font-medium flex-1"
                      @click="noop"
                    >
                      <span class="newsfeed-send-sprite opacity-50"></span>
                      <span class="text-zinc-600 text-sm">Send</span>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Comments -->
              <div class=" px-5 pt-4 pb-32">
                <div class="flex items-center justify-between mb-3">
                  <div class="text-[15px] font-semibold text-zinc-500">
                    All Comments
                  </div>

                  <button
                    v-if="hasMoreComments"
                    type="button"
                    class="text-sm font-semibold text-zinc-500 hover:underline"
                    @click="loadMoreComments"
                    :disabled="commentsLoading"
                  >
                    {{ commentsLoading ? 'Loading…' : 'Load more' }}
                  </button>
                </div>

                <div
                  v-if="!comments.length && commentsLoaded"
                  class="text-sm text-zinc-500 py-6 flex justify-center"
                >
                  <div class="flex flex-col items-center text-center">
                    <DocumentTextIcon class="w-20 h-20 text-zinc-400 mb-3" />
                    <p class="text-xl text-zinc-500 font-semibold">No comments yet.</p>
                    <p class="text-base text-zinc-400">Be the first to comment.</p>
                  </div>
                </div>

                <div
                  v-for="c in sortedCommentsOldestFirst"
                  :key="c.id"
                  class="relative flex items-start gap-2 py-2 nf-comment-fade-in"
                  :ref="(el) => setCommentRowRef(c.id, el)"
                >

              
                  <!-- thread line (UI only, like FB) -->
                  <div
                    v-if="SHOW_COMMENT_REPLIES_UI && (getCommentReplyCount(c) > 0 || hasLocalLatestReply(c.id) || openReplyForCommentId === c.id)"
                    class="absolute left-4 top-[44px] pointer-events-none border-l-[2px] border-zinc-200"
                    :style="{ bottom: threadLineBottomStyle(c.id) }"
                  ></div>

                  <img
                    :src="resolveAvatarFromEntity(c, defaultAuthorAvatar)"
                    alt="Comment avatar"
                    class="h-8 w-8 rounded-full object-cover"
                    @error="onAvatarLoadError"
                  />

                  <div class="flex-1">
                    <div :class="isEditingItem('comment', c.id) ? 'block w-full max-w-full' : 'inline-block max-w-full'">
                      <div :class="isEditingItem('comment', c.id) ? 'relative bg-[#f0f3f8] rounded-2xl px-3 py-2 w-full max-w-full' : 'relative inline-block w-fit bg-[#f0f3f8] rounded-2xl px-3 py-2 max-w-[calc(100%-0.75rem)] min-w-44'">
                        <div
                          v-if="!isEditingItem('comment', c.id)"
                          class="text-[13px] font-semibold text-zinc-900 leading-tight"
                        >
                          {{ c.authorName || 'User' }}
                        </div>


                        <!-- Comment text body of bubble -->
                        <div class="text-sm text-zinc-800 whitespace-pre-line break-words leading-snug group/commentRow">

                              <!-- comment menu (edit/delete) -->
                                <div
                                    v-if="!isEditingItem('comment', c.id)"
                                    class="absolute -right-10 top-1 opacity-0
                                        group-hover/commentRow:opacity-100 transition-opacity duration-150"
                                >
                                    <NewsfeedCreatorCommentReplyMenu
                                    :target-id="c.id"
                                    target-type="comment"
                                    @edit="handleCommentReplyMenuEdit"
                                    @delete="handleCommentReplyMenuDelete"
                                    />
                                </div>

                        <template v-if="isEditingItem('comment', c.id)">
                          <div class="mt-1">
                            <textarea
                              :ref="setActiveEditTextareaRef"
                              v-model="editDraftBody"
                              rows="1"
                              class="inline-edit-textarea w-full bg-transparent resize-none overflow-y-hidden border-none outline-none text-sm text-zinc-900 placeholder:text-zinc-500 focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0"
                              @input="autoResizeInlineTextarea"
                              @keydown="onEditTextareaKeydown"
                            ></textarea>

                            <div v-if="inlineEditMediaPreviewItem" class="mt-2">
                              <div class="relative inline-block rounded-lg overflow-hidden border border-zinc-300 bg-white">
                                <img
                                  v-if="inlineEditMediaPreviewItem.type === 'image'"
                                  :src="inlineEditMediaPreviewItem.src"
                                  alt="Selected edit media"
                                  class="max-h-24 max-w-[180px] object-cover"
                                />
                                <video
                                  v-else
                                  :src="inlineEditMediaPreviewItem.src"
                                  class="max-h-24 max-w-[180px] object-cover"
                                  controls
                                  muted
                                ></video>
                                <button
                                  type="button"
                                  class="absolute top-1 right-1 rounded-full bg-zinc-200/95 hover:bg-zinc-300 p-1 shadow-sm"
                                  @mousedown.prevent
                                  @click.stop="clearInlineEditMediaSelection"
                                  aria-label="Remove media"
                                >
                                  <XMarkIcon class="w-4 h-4 text-zinc-700" />
                                </button>
                              </div>
                            </div>

                            <input
                              :ref="setInlineEditMediaInputRef"
                              type="file"
                              class="hidden"
                              accept="image/*,video/*"
                              @change="handleInlineEditMediaPicked"
                            />

                            <div class="mt-2 flex items-center justify-between gap-3">
                              <p
                                v-if="editErrorMessage"
                                class="text-xs text-red-600 font-medium"
                              >
                                {{ editErrorMessage }}
                              </p>
                              <p
                                v-else
                                class="text-xs text-zinc-500"
                              >
                                Press Esc to cancel
                              </p>

                              <div class="flex items-center gap-2">
                                <button
                                  type="button"
                                  class="rounded-full p-1 hover:bg-zinc-200"
                                  :disabled="isSubmittingEdit"
                                  @mousedown.prevent
                                  @click.stop="openInlineEditMediaPicker"
                                  aria-label="Attach media"
                                >
                                  <CameraIcon class="w-5 h-5 text-zinc-500" />
                                </button>
                                <button
                                  v-if="inlineEditMediaRemoved && !pendingInlineEditMedia"
                                  type="button"
                                  class="text-xs font-semibold text-zinc-600 hover:underline"
                                  :disabled="isSubmittingEdit"
                                  @click.stop="undoInlineEditMediaRemoval"
                                >
                                  Undo media remove
                                </button>
                                <button
                                  type="button"
                                  class="text-xs font-semibold text-zinc-600 hover:underline"
                                  :disabled="isSubmittingEdit"
                                  @click.stop="cancelInlineEdit"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  class="text-xs font-semibold text-[#2D8CFF] hover:underline disabled:text-zinc-300"
                                  :disabled="isSubmittingEdit || !canSubmitInlineEdit"
                                  @click.stop="submitInlineEdit"
                                >
                                  {{ isSubmittingEdit ? 'Saving...' : 'Save' }}
                                </button>
                              </div>
                            </div>
                          </div>
                        </template>
                        <template v-else>
                          {{
                              showFullCommentBodyById[c.id]
                              ? normalizeBodyText(c.body)
                              : (isBodyLongByLines(c.body) ? getPreviewTextByLines(c.body) : normalizeBodyText(c.body))
                          }}

                          <button
                              v-if="isBodyLongByLines(c.body)"
                              type="button"
                              class="inline text-sm font-semibold text-zinc-800 hover:text-zinc-900 ml-1 hover:underline"
                              @click.stop="showFullCommentBodyById[c.id] = !showFullCommentBodyById[c.id]"
                          >
                              {{ showFullCommentBodyById[c.id] ? 'Show less' : 'See more' }}
                          </button>

                          <div v-if="getCommentMediaItems(c).length" class="mt-2 space-y-2">
                            <div
                              v-for="(item, itemIndex) in getCommentMediaItems(c)"
                              :key="`comment-${c.id}-media-${itemIndex}`"
                              class="overflow-hidden rounded-xl bg-black/5 nf-media-shell"
                              :class="{ 'nf-media-shell--loading': !isCommentMediaLoaded(getCommentMediaKey(c.id, itemIndex, item)) }"
                              :ref="() => ensureCommentMediaSkeletonFallback(getCommentMediaKey(c.id, itemIndex, item))"
                            >
                              <NewsfeedInlineVideoPlayer
                                v-if="item.type === 'video'"
                                :file-id="item.fileId ? String(item.fileId) : null"
                                :feature-instance-id="resolvedMediaFeatureInstanceId"
                                :src="item.src"
                                :poster="item.poster || null"
                                :autoplay="false"
                                :muted="false"
                                :max-height="460"
                                class="nf-comment-video-frame"
                                :class="{ 'nf-media-content-hidden': !isCommentMediaLoaded(getCommentMediaKey(c.id, itemIndex, item)) }"
                                @loaded="markCommentMediaLoaded(getCommentMediaKey(c.id, itemIndex, item))"
                                @open-viewer="openViewerWithItems(getCommentMediaItems(c), itemIndex)"
                              />
                              <img
                                v-else
                                :src="item.src"
                                alt="Comment media"
                                class="max-h-60 w-auto max-w-full object-contain transition-opacity duration-300"
                                :class="{ 'nf-media-content-hidden': !isCommentMediaLoaded(getCommentMediaKey(c.id, itemIndex, item)) }"
                                @load="markCommentMediaLoaded(getCommentMediaKey(c.id, itemIndex, item))"
                                @error="markCommentMediaLoaded(getCommentMediaKey(c.id, itemIndex, item))"
                                @click.stop="openViewerWithItems(getCommentMediaItems(c), itemIndex)"
                              />
                            </div>
                          </div>
                        </template>
                        </div>



                      </div>

                      <div class="relative flex items-center justify-between w-full px-1">
                        <!-- left group -->
                        <div class="relative flex items-center gap-3 pl-1">
                          <!-- time + tooltip -->
                          <div class="relative group/commentTime z-[120] hover:z-[220]">
                            <div class="text-xs text-zinc-500 cursor-pointer font-semibold hover:underline ">
                              {{ formatCommentTime(c.createdAt || c.created_at) }}
                            </div>

                            <div
                              class="pointer-events-none absolute hidden group-hover/commentTime:block
                                     px-3 py-2 text-xs rounded-xl bg-black/70 text-white whitespace-nowrap
                                     z-[230] top-5 left-0 shadow-lg"
                            >
                              {{ formatCommentFullTooltip(c.createdAt || c.created_at) }}
                            </div>
                          </div>

                          <!-- Like (opens picker on hover) -->
                          <div
                            class="relative -mt-[2px] nf-picker-anchor"
                            @mouseenter="handleCommentReactionMouseEnter(c.id)"
                            @mouseleave="handleCommentReactionMouseLeave(c.id)"
                          >
                            <button
                              type="button"
                              class="text-xs font-semibold hover:underline"
                              :class="getCommentReactionType(c.id) ? 'text-[#2D8CFF]' : 'text-zinc-600'"
                            >
                              Like
                            </button>

                            <NewsfeedReactionPicker
                              v-if="openCommentReactionForId === c.id"
                              :portal="props.inlineSidebar"
                              :position-class="props.inlineSidebar ? 'absolute bottom-full mb-2 right-0' : 'absolute bottom-full left-10'"
                              @select="(type) => handleCommentReactionSelect(c.id, type)"
                              @hover-change="(hovering) => handleCommentPickerHoverChange(c.id, hovering)"
                            />
                          </div>

                          <!-- Reply -->
                          <button
                            type="button"
                            class="text-xs text-zinc-600 cursor-pointer font-semibold hover:underline"
                            @click="openReplyBoxReplyingToCommentAuthor(c)"

                          >
                            Reply
                          </button>
                        </div>

                        <!-- right badge: reaction icon + optional count + tooltip -->
                        <div
                          v-if="getCommentReactionCount(c.id) > 0"
                          class="relative group flex items-center gap-1"
                          @mouseenter="handleCommentReactionTooltipEnter(c.id, $event)"
                        >
                          <!-- KEEP: number only shows if > 1 -->
                          <span
                            v-if="getCommentReactionCount(c.id) > 1"
                            class="text-[11px] font-semibold text-zinc-600 leading-none"
                          >
                            {{ getCommentReactionCount(c.id) }}
                          </span>

                          <div class="flex items-center -space-x-1">
                            <img
                              v-for="(type, index) in getCommentTopReactionTypes(c.id)"
                              :key="`${c.id}-comment-reaction-${type}`"
                              :src="REACTION_META[type]?.icon"
                              :alt="REACTION_META[type]?.label || 'Reaction'"
                              class="h-[18px] w-[18px] rounded-full ring-2 ring-white cursor-pointer"
                              :style="{ zIndex: getCommentTopReactionTypes(c.id).length - index }"
                            />
                          </div>

                          <div
                            v-if="getCommentReactionTooltipRows(c.id).length || getCommentReactionTooltipText(c.id)"
                            class="pointer-events-none absolute z-[230] right-0
                                   px-3 py-2 rounded-xl bg-black/80 text-[11px] text-white shadow-lg
                                   opacity-0 translate-y-1
                                   group-hover:opacity-100 group-hover:translate-y-0
                                   transition-all duration-150
                                   whitespace-nowrap"
                            :class="commentReactionTooltipPosition[String(c.id)] === 'above' ? 'bottom-full mb-2' : 'top-full mt-2'"
                          >
                            <template v-if="getCommentReactionTooltipRows(c.id).length > 1">
                              <div
                                v-for="row in getCommentReactionTooltipRows(c.id)"
                                :key="`${c.id}-comment-tooltip-${row.type}`"
                                class="flex items-center gap-1 mb-1 last:mb-0"
                              >
                                <img
                                  :src="REACTION_META[row.type]?.icon"
                                  :alt="REACTION_META[row.type]?.label || 'Reaction'"
                                  class="h-4 w-4"
                                />
                                <span class="font-semibold">{{ row.count }}</span>
                              </div>
                            </template>
                            <template v-else>
                              <div class="flex items-center gap-1 mb-2">
                                <img
                                  :src="REACTION_META[getCommentTopReactionTypes(c.id)[0] || 'like']?.icon"
                                  :alt="REACTION_META[getCommentTopReactionTypes(c.id)[0] || 'like']?.label || 'Reaction'"
                                  class="h-4 w-4"
                                />
                                <span class="font-semibold">
                                  {{ getCommentReactionCount(c.id) }}
                                </span>
                              </div>

                              <div class="max-w-[220px] text-zinc-200 mt-1 leading-tight">
                                <div
                                  v-for="name in getCommentReactionTooltipNames(c.id)"
                                  :key="`${c.id}-comment-name-${name}`"
                                  class="truncate"
                                >
                                  {{ name }}
                                </div>
                                <div
                                  v-if="getCommentReactionTooltipExtraCount(c.id) > 0"
                                  class="mt-1 text-[11px] text-zinc-300"
                                >
                                  +{{ getCommentReactionTooltipExtraCount(c.id) }} more
                                </div>
                              </div>
                            </template>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- ✅ Everything below should be FULL width (NOT inside inline-block) -->

                    <!-- View replies row (only when we are NOT auto-expanding) -->
                    <div
                      v-if="SHOW_COMMENT_REPLIES_UI
                            && !autoExpandReplies
                            && getCommentReplyCount(c) > 0
                            && !isRepliesExpanded(c.id)"
                      @click.stop="expandReplies(c.id)"
                      class="relative mt-1 pt-2 pl-4 text-base font-medium text-zinc-500 cursor-pointer select-none hover:underline w-full"
                      :ref="(el) => setViewRepliesRowRef(c.id, el)"
                    >
                      <!-- elbow that connects the spine to this "View replies" row -->
                      <div
                        class="pointer-events-none absolute left-[-24px] top-[10px] w-6 h-3
                               border-l-[2px] border-b-[2px] border-zinc-200 rounded-bl-xl"
                      ></div>

                      View {{ getCommentReplyCount(c) }} {{ getCommentReplyCount(c) === 1 ? 'reply' : 'replies' }}
                    </div>

                    <!-- Replies list -->
                    <div
                      v-if="SHOW_COMMENT_REPLIES_UI
                            && (autoExpandReplies || isRepliesExpanded(c.id))
                            && getCommentReplyCount(c) > 0"
                      class="mt-2 pl-1 w-full"
                    >
                      <!-- loading -->
                      <div
                        v-if="isRepliesLoadingByCommentId[c.id]"
                        class="text-sm text-zinc-400 pl-9"
                      >
                        Loading…
                      </div>

                      <!-- replies -->
                      <div
                        v-else
                        v-for="r in (repliesByParentId[c.id] || [])"
                        :key="r.id"
                        class="relative flex items-start gap-2 w-full mt-2 nf-comment-fade-in"
                        :ref="(el) => setReplyRowRef(r.id, el)"
                        >

                        

                        <!-- branch elbow (hooks each reply into the vertical spine) -->
                        <div
                          class="pointer-events-none absolute left-[-28px] top-0 w-6 h-3
                                 border-l-[2px] border-b-[2px] border-zinc-200 rounded-bl-xl"
                        ></div>

                        <img
                          :src="resolveAvatarFromEntity(r, defaultAuthorAvatar)"
                          class="h-6 w-6 rounded-full object-cover"
                          alt="Reply avatar"
                          :ref="(el) => setReplyAvatarRef(c.id, r.id, el)"
                          @error="onAvatarLoadError"
                        />

                        <div class="flex-1 min-w-0">
                          <!-- ✅ constrain to bubble width like comments -->
                          <div :class="isEditingItem('reply', r.id) ? 'block w-full max-w-full' : 'inline-block max-w-[calc(100%-0.75rem)] min-w-44'">

                            <div :class="isEditingItem('reply', r.id) ? 'relative bg-[#f0f3f8] rounded-2xl px-3 py-1 w-full max-w-full group/replyRow' : 'relative inline-block w-fit bg-[#f0f3f8] rounded-2xl px-3 py-1 max-w-[calc(100%-0.75rem)] group/replyRow'">
                              <div
                                v-if="!isEditingItem('reply', r.id)"
                                class="text-[13px] font-semibold text-zinc-900 leading-tight"
                              >
                                {{ r.authorName || 'User' }}
                              </div>

                           <!-- Reply text body of bubble -->
                              <div class="text-sm text-zinc-800 whitespace-pre-line break-words leading-snug">

                                <!-- reply menu -->
                        <div
                            v-if="!isEditingItem('reply', r.id)"
                            class="absolute -right-10 top-0 opacity-0
                                group-hover/replyRow:opacity-100 transition-opacity duration-150"
                        >
                            <NewsfeedCreatorCommentReplyMenu
                            :target-id="r.id"
                            target-type="reply"
                            @edit="handleCommentReplyMenuEdit"
                            @delete="handleCommentReplyMenuDelete"
                            />
                        </div>

                                    <template v-if="isEditingItem('reply', r.id)">
                                      <div class="mt-1">
                                        <textarea
                                          :ref="setActiveEditTextareaRef"
                                          v-model="editDraftBody"
                                          rows="1"
                                          class="inline-edit-textarea w-full bg-transparent resize-none overflow-y-hidden border-none outline-none text-sm text-zinc-900 placeholder:text-zinc-500 focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0"
                                          @input="autoResizeInlineTextarea"
                                          @keydown="onEditTextareaKeydown"
                                        ></textarea>

                                        <div v-if="inlineEditMediaPreviewItem" class="mt-2">
                                          <div class="relative inline-block rounded-lg overflow-hidden border border-zinc-300 bg-white">
                                            <img
                                              v-if="inlineEditMediaPreviewItem.type === 'image'"
                                              :src="inlineEditMediaPreviewItem.src"
                                              alt="Selected edit media"
                                              class="max-h-24 max-w-[180px] object-cover"
                                            />
                                            <video
                                              v-else
                                              :src="inlineEditMediaPreviewItem.src"
                                              class="max-h-24 max-w-[180px] object-cover"
                                              controls
                                              muted
                                            ></video>
                                            <button
                                              type="button"
                                              class="absolute top-1 right-1 rounded-full bg-zinc-200/95 hover:bg-zinc-300 p-1 shadow-sm"
                                              @mousedown.prevent
                                              @click.stop="clearInlineEditMediaSelection"
                                              aria-label="Remove media"
                                            >
                                              <XMarkIcon class="w-4 h-4 text-zinc-700" />
                                            </button>
                                          </div>
                                        </div>

                                        <input
                                          :ref="setInlineEditMediaInputRef"
                                          type="file"
                                          class="hidden"
                                          accept="image/*,video/*"
                                          @change="handleInlineEditMediaPicked"
                                        />

                                        <div class="mt-2 flex items-center justify-between gap-3">
                                          <p
                                            v-if="editErrorMessage"
                                            class="text-xs text-red-600 font-medium"
                                          >
                                            {{ editErrorMessage }}
                                          </p>
                                          <p
                                            v-else
                                            class="text-xs text-zinc-500"
                                          >
                                            Press Esc to cancel
                                          </p>

                                          <div class="flex items-center gap-2">
                                            <button
                                              type="button"
                                              class="rounded-full p-1 hover:bg-zinc-200"
                                              :disabled="isSubmittingEdit"
                                              @mousedown.prevent
                                              @click.stop="openInlineEditMediaPicker"
                                              aria-label="Attach media"
                                            >
                                              <CameraIcon class="w-5 h-5 text-zinc-500" />
                                            </button>
                                            <button
                                              v-if="inlineEditMediaRemoved && !pendingInlineEditMedia"
                                              type="button"
                                              class="text-xs font-semibold text-zinc-600 hover:underline"
                                              :disabled="isSubmittingEdit"
                                              @click.stop="undoInlineEditMediaRemoval"
                                            >
                                              Undo media remove
                                            </button>
                                            <button
                                              type="button"
                                              class="text-xs font-semibold text-zinc-600 hover:underline"
                                              :disabled="isSubmittingEdit"
                                              @click.stop="cancelInlineEdit"
                                            >
                                              Cancel
                                            </button>
                                            <button
                                              type="button"
                                              class="text-xs font-semibold text-[#2D8CFF] hover:underline disabled:text-zinc-300"
                                              :disabled="isSubmittingEdit || !canSubmitInlineEdit"
                                              @click.stop="submitInlineEdit"
                                            >
                                              {{ isSubmittingEdit ? 'Saving...' : 'Save' }}
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    </template>
                                    <template v-else>
                                      {{
                                          showFullReplyBodyById[r.id]
                                          ? normalizeBodyText(r.body)
                                          : (isBodyLongByLines(r.body) ? getPreviewTextByLines(r.body) : normalizeBodyText(r.body))
                                      }}

                                        <button
                                          v-if="isBodyLongByLines(r.body)"
                                          type="button"
                                          class="inline text-sm font-semibold text-zinc-800 hover:text-zinc-900 ml-1 hover:underline"
                                          @click.stop="showFullReplyBodyById[r.id] = !showFullReplyBodyById[r.id]"
                                        >
                                          {{ showFullReplyBodyById[r.id] ? 'Show less' : 'See more' }}
                                        </button>

                                        <div v-if="getCommentMediaItems(r).length" class="mt-2 space-y-2">
                                          <div
                                            v-for="(item, itemIndex) in getCommentMediaItems(r)"
                                            :key="`reply-${r.id}-media-${itemIndex}`"
                                            class="overflow-hidden rounded-xl bg-black/5 nf-media-shell"
                                            :class="{ 'nf-media-shell--loading': !isCommentMediaLoaded(getCommentMediaKey(`reply-${r.id}`, itemIndex, item)) }"
                                            :ref="() => ensureCommentMediaSkeletonFallback(getCommentMediaKey(`reply-${r.id}`, itemIndex, item))"
                                          >
                                            <NewsfeedInlineVideoPlayer
                                              v-if="item.type === 'video'"
                                              :file-id="item.fileId ? String(item.fileId) : null"
                                              :feature-instance-id="resolvedMediaFeatureInstanceId"
                                              :src="item.src"
                                              :poster="item.poster || null"
                                              :autoplay="false"
                                              :muted="false"
                                              :max-height="460"
                                              class="nf-comment-video-frame"
                                              :class="{ 'nf-media-content-hidden': !isCommentMediaLoaded(getCommentMediaKey(`reply-${r.id}`, itemIndex, item)) }"
                                              @loaded="markCommentMediaLoaded(getCommentMediaKey(`reply-${r.id}`, itemIndex, item))"
                                              @open-viewer="openViewerWithItems(getCommentMediaItems(r), itemIndex)"
                                            />
                                            <img
                                              v-else
                                              :src="item.src"
                                              alt="Reply media"
                                              class="max-h-60 w-auto max-w-full object-contain transition-opacity duration-300"
                                              :class="{ 'nf-media-content-hidden': !isCommentMediaLoaded(getCommentMediaKey(`reply-${r.id}`, itemIndex, item)) }"
                                              @load="markCommentMediaLoaded(getCommentMediaKey(`reply-${r.id}`, itemIndex, item))"
                                              @error="markCommentMediaLoaded(getCommentMediaKey(`reply-${r.id}`, itemIndex, item))"
                                              @click.stop="openViewerWithItems(getCommentMediaItems(r), itemIndex)"
                                            />
                                          </div>
                                        </div>
                                    </template>
                              </div>



                            </div>

                            <!-- ✅ Reply meta row now matches wrapper width (NOT full row) -->
                            <div class="relative flex items-center justify-between w-full px-1">
                              <!-- left group -->
                              <div class="relative flex items-center gap-3 pl-1">
                                <!-- time + tooltip -->
                                <div class="relative group/replyTime">
                                  <div class="text-xs text-zinc-500 cursor-pointer font-semibold hover:underline">
                                    {{ formatCommentTime(r.createdAt || r.created_at) }}
                                  </div>

                                  <div
                                    class="pointer-events-none absolute hidden group-hover/replyTime:block
                                           px-3 py-2 text-xs rounded-xl bg-black/70 text-white whitespace-nowrap
                                           z-[230] top-5 left-0 shadow-lg"
                                  >
                                    {{ formatCommentFullTooltip(r.createdAt || r.created_at) }}
                                  </div>
                                </div>

                                <!-- Like (opens picker on hover) -->
                                <div
                                  class="relative -mt-[2px] nf-picker-anchor"
                                  @mouseenter="handleReplyReactionMouseEnter(r.id)"
                                  @mouseleave="handleReplyReactionMouseLeave(r.id)"
                                >
                                  <button
                                    type="button"
                                    class="text-xs font-semibold hover:underline"
                                    :class="getReplyReactionType(c.id, r.id) ? 'text-[#2D8CFF]' : 'text-zinc-600'"
                                  >
                                    Like
                                  </button>

                                  <NewsfeedReactionPicker
                                    v-if="openReplyReactionForId === r.id"
                                    :portal="props.inlineSidebar"
                                    :position-class="props.inlineSidebar ? 'absolute bottom-full mb-2 right-0' : 'absolute bottom-full left-10'"
                                    @select="(type) => handleReplyReactionSelect(c.id, r.id, type)"
                                    @hover-change="(hovering) => handleReplyPickerHoverChange(r.id, hovering)"
                                  />
                                </div>

                                <!-- Reply -->
                                <button
                                  type="button"
                                  class="text-xs text-zinc-600 cursor-pointer font-semibold hover:underline"
                                  @click.stop="openReplyReplyBox(c.id, r.id)"
                                >
                                  Reply
                                </button>
                              </div>

                              <!-- right badge -->
                              <div
                                v-if="getReplyReactionCount(c.id, r.id) > 0"
                                class="relative group flex items-center gap-1"
                                @mouseenter="handleReplyReactionTooltipEnter(r.id, $event)"
                              >
                                <span
                                  v-if="getReplyReactionCount(c.id, r.id) > 1"
                                  class="text-[11px] font-semibold text-zinc-600 leading-none"
                                >
                                  {{ getReplyReactionCount(c.id, r.id) }}
                                </span>

                                <div class="flex items-center -space-x-1">
                                  <img
                                    v-for="(type, index) in getReplyTopReactionTypes(c.id, r.id)"
                                    :key="`${r.id}-reply-reaction-${type}`"
                                    :src="REACTION_META[type]?.icon"
                                    :alt="REACTION_META[type]?.label || 'Reaction'"
                                    class="h-[18px] w-[18px] rounded-full ring-2 ring-white cursor-pointer"
                                    :style="{ zIndex: getReplyTopReactionTypes(c.id, r.id).length - index }"
                                  />
                                </div>

                                <div
                                  v-if="getReplyReactionTooltipRows(c.id, r.id).length || getReplyReactionTooltipText(c.id, r.id)"
                                  class="pointer-events-none absolute z-[230] right-0
                                         px-3 py-2 rounded-xl bg-black/80 text-[11px] text-white shadow-lg
                                         opacity-0 translate-y-1
                                         group-hover:opacity-100 group-hover:translate-y-0
                                         transition-all duration-150
                                         whitespace-nowrap"
                                  :class="replyReactionTooltipPosition[String(r.id)] === 'above' ? 'bottom-full mb-2' : 'top-full mt-2'"
                                >
                                  <template v-if="getReplyReactionTooltipRows(c.id, r.id).length > 1">
                                    <div
                                      v-for="row in getReplyReactionTooltipRows(c.id, r.id)"
                                      :key="`${r.id}-reply-tooltip-${row.type}`"
                                      class="flex items-center gap-1 mb-1 last:mb-0"
                                    >
                                      <img
                                        :src="REACTION_META[row.type]?.icon"
                                        :alt="REACTION_META[row.type]?.label || 'Reaction'"
                                        class="h-4 w-4"
                                      />
                                      <span class="font-semibold">{{ row.count }}</span>
                                    </div>
                                  </template>
                                  <template v-else>
                                    <div class="flex items-center gap-1 mb-2">
                                      <img
                                        :src="REACTION_META[getReplyTopReactionTypes(c.id, r.id)[0] || 'like']?.icon"
                                        :alt="REACTION_META[getReplyTopReactionTypes(c.id, r.id)[0] || 'like']?.label || 'Reaction'"
                                        class="h-4 w-4"
                                      />
                                      <span class="font-semibold">
                                        {{ getReplyReactionCount(c.id, r.id) }}
                                      </span>
                                    </div>

                                    <div class="max-w-[220px] text-zinc-200 mt-1 leading-tight">
                                      <div
                                        v-for="name in getReplyReactionTooltipNames(c.id, r.id)"
                                        :key="`${r.id}-reply-name-${name}`"
                                        class="truncate"
                                      >
                                        {{ name }}
                                      </div>
                                      <div
                                        v-if="getReplyReactionTooltipExtraCount(c.id, r.id) > 0"
                                        class="mt-1 text-[11px] text-zinc-300"
                                      >
                                        +{{ getReplyReactionTooltipExtraCount(c.id, r.id) }} more
                                      </div>
                                    </div>
                                  </template>
                                </div>
                              </div>
                            </div>
                          </div>

                          
                          <!-- ─────────────────────────────────────────────
     Reply Replies (depth 2) — nested under a reply
     ✅ CORRECT PLACEMENT: INSIDE reply's right column (flex-1)
     ───────────────────────────────────────────── -->
<div
  class="mt-2 pl-[2px] w-full"
  :ref="(el) => setReplyRepliesBlockRef(r.id, el)"
>

  <!-- ✅ depth-2 vertical spine thread line with dynamic bottom (mirrors comment spine logic) -->
  <div
    v-if="shouldShowReplyRepliesThread(r.id)"
    class="absolute left-[10px] top-[26px] pointer-events-none border-l-[2px] border-zinc-200"
    :style="{ bottom: replyThreadLineBottomStyle(r.id) }"
  ></div>

  <!-- loading nested -->
  <div
    v-if="isReplyRepliesLoadingByParentId[r.id]"
    class="text-sm text-zinc-400"
  >
    Loading…
  </div>

  <!-- ✅ View reply-replies row (depth 2) — only when > PREVIEW_MAX and NOT expanded -->
  <div
    v-else-if="SHOW_REPLY_REPLIES_UI
      && getReplyReplyCountForReply(r) > REPLY_REPLIES_PREVIEW_MAX
      && !isReplyRepliesExpanded(r.id)"
    @click.stop="expandReplyReplies(r.id)"
    class="relative mt-1 pt-2 pl-4 text-base font-medium text-zinc-500 cursor-pointer select-none hover:underline w-full"
    :ref="(el) => setViewReplyRepliesRowRef(r.id, el)"
  >
    <!-- elbow that connects the spine to this row -->
    <div
      class="pointer-events-none absolute left-[-24px] top-[10px] w-6 h-3
             border-l-[2px] border-b-[2px] border-zinc-200 rounded-bl-xl"
    ></div>

    View {{ getReplyReplyCountForReply(r) }} replies
  </div>

  <!-- ✅ nested replies list (only if <= PREVIEW_MAX OR expanded) -->
  <div v-else>
    <div
      v-for="rr in (replyRepliesByParentId[r.id] || [])"
      :key="rr.id"
      class="relative flex items-start gap-2 w-full mt-2 group/replyReplyRow nf-comment-fade-in"
    >


      <!-- reply reply branch elbows (hooks each reply reply into the vertical spine) -->
      <div
        class="pointer-events-none absolute left-[-24px] top-0 w-5 h-3 border-l-[2px] border-b-[2px] border-zinc-200 rounded-bl-xl">
      </div>

      <img
        :src="resolveAvatarFromEntity(rr, defaultAuthorAvatar)"
        class="h-6 w-6 rounded-full object-cover"
        alt="Reply reply avatar"
        :ref="(el) => { if (el && isLastReplyReplyForReply(r.id, rr.id)) setReplyReplyAvatarRef(r.id, el); }"
        @error="onAvatarLoadError"
      />

      <div class="flex-1 min-w-0">
        <div :class="isEditingItem('reply-reply', rr.id) ? 'block w-full max-w-full' : 'inline-block max-w-[calc(100%-0.75rem)] min-w-44'">

          <div :class="isEditingItem('reply-reply', rr.id) ? 'relative bg-[#f0f3f8] rounded-2xl px-3 py-1 w-full max-w-full' : 'relative inline-block w-fit bg-[#f0f3f8] rounded-2xl px-3 py-1 max-w-[calc(100%-0.75rem)]'">


              <!-- reply-reply menu -->
                <div
                    v-if="!isEditingItem('reply-reply', rr.id)"
                    class="absolute -right-10 top-0 opacity-0
                        group-hover/replyReplyRow:opacity-100 transition-opacity duration-150"
                >
                    <NewsfeedCreatorCommentReplyMenu
                    :target-id="rr.id"
                    target-type="reply-reply"
                    @edit="handleCommentReplyMenuEdit"
                    @delete="handleCommentReplyMenuDelete"
                    />
                </div>



            <div
              v-if="!isEditingItem('reply-reply', rr.id)"
              class="text-[13px] font-semibold text-zinc-900 leading-tight"
            >
              {{ rr.authorName || 'User' }}
            </div>

             <!-- Reply Replies text body of bubble -->

            <div class="text-sm text-zinc-800 whitespace-pre-line break-words leading-snug">
                <template v-if="isEditingItem('reply-reply', rr.id)">
                  <div class="mt-1">
                    <textarea
                      :ref="setActiveEditTextareaRef"
                      v-model="editDraftBody"
                      rows="1"
                      class="inline-edit-textarea w-full bg-transparent resize-none overflow-y-hidden border-none outline-none text-sm text-zinc-900 placeholder:text-zinc-500 focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0"
                      @input="autoResizeInlineTextarea"
                      @keydown="onEditTextareaKeydown"
                    ></textarea>

                    <div v-if="inlineEditMediaPreviewItem" class="mt-2">
                      <div class="relative inline-block rounded-lg overflow-hidden border border-zinc-300 bg-white">
                        <img
                          v-if="inlineEditMediaPreviewItem.type === 'image'"
                          :src="inlineEditMediaPreviewItem.src"
                          alt="Selected edit media"
                          class="max-h-24 max-w-[180px] object-cover"
                        />
                        <video
                          v-else
                          :src="inlineEditMediaPreviewItem.src"
                          class="max-h-24 max-w-[180px] object-cover"
                          controls
                          muted
                        ></video>
                        <button
                          type="button"
                          class="absolute top-1 right-1 rounded-full bg-zinc-200/95 hover:bg-zinc-300 p-1 shadow-sm"
                          @mousedown.prevent
                          @click.stop="clearInlineEditMediaSelection"
                          aria-label="Remove media"
                        >
                          <XMarkIcon class="w-4 h-4 text-zinc-700" />
                        </button>
                      </div>
                    </div>

                    <input
                      :ref="setInlineEditMediaInputRef"
                      type="file"
                      class="hidden"
                      accept="image/*,video/*"
                      @change="handleInlineEditMediaPicked"
                    />

                    <div class="mt-2 flex items-center justify-between gap-3">
                      <p
                        v-if="editErrorMessage"
                        class="text-xs text-red-600 font-medium"
                      >
                        {{ editErrorMessage }}
                      </p>
                      <p
                        v-else
                        class="text-xs text-zinc-500"
                      >
                        Press Esc to cancel
                      </p>

                      <div class="flex items-center gap-2">
                        <button
                          type="button"
                          class="rounded-full p-1 hover:bg-zinc-200"
                          :disabled="isSubmittingEdit"
                          @mousedown.prevent
                          @click.stop="openInlineEditMediaPicker"
                          aria-label="Attach media"
                        >
                          <CameraIcon class="w-5 h-5 text-zinc-500" />
                        </button>
                        <button
                          v-if="inlineEditMediaRemoved && !pendingInlineEditMedia"
                          type="button"
                          class="text-xs font-semibold text-zinc-600 hover:underline"
                          :disabled="isSubmittingEdit"
                          @click.stop="undoInlineEditMediaRemoval"
                        >
                          Undo media remove
                        </button>
                        <button
                          type="button"
                          class="text-xs font-semibold text-zinc-600 hover:underline"
                          :disabled="isSubmittingEdit"
                          @click.stop="cancelInlineEdit"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          class="text-xs font-semibold text-[#2D8CFF] hover:underline disabled:text-zinc-300"
                          :disabled="isSubmittingEdit || !canSubmitInlineEdit"
                          @click.stop="submitInlineEdit"
                        >
                          {{ isSubmittingEdit ? 'Saving...' : 'Save' }}
                        </button>
                      </div>
                    </div>
                  </div>
                </template>
                <template v-else>
                  {{
                      showFullReplyReplyBodyById[rr.id]
                      ? normalizeBodyText(rr.body)
                      : (isBodyLongByLines(rr.body) ? getPreviewTextByLines(rr.body) : normalizeBodyText(rr.body))
                  }}

                  <button
                      v-if="isBodyLongByLines(rr.body)"
                      type="button"
                      class="inline text-sm font-semibold text-zinc-800 hover:text-zinc-900 ml-1 hover:underline"
                      @click.stop="showFullReplyReplyBodyById[rr.id] = !showFullReplyReplyBodyById[rr.id]"
                  >
                      {{ showFullReplyReplyBodyById[rr.id] ? 'Show less' : 'See more' }}
                  </button>

                  <div v-if="getCommentMediaItems(rr).length" class="mt-2 space-y-2">
                    <div
                      v-for="(item, itemIndex) in getCommentMediaItems(rr)"
                      :key="`reply-reply-${rr.id}-media-${itemIndex}`"
                      class="overflow-hidden rounded-xl bg-black/5 nf-media-shell"
                      :class="{ 'nf-media-shell--loading': !isCommentMediaLoaded(getCommentMediaKey(`reply-reply-${rr.id}`, itemIndex, item)) }"
                      :ref="() => ensureCommentMediaSkeletonFallback(getCommentMediaKey(`reply-reply-${rr.id}`, itemIndex, item))"
                    >
                      <NewsfeedInlineVideoPlayer
                        v-if="item.type === 'video'"
                        :file-id="item.fileId ? String(item.fileId) : null"
                        :feature-instance-id="resolvedMediaFeatureInstanceId"
                        :src="item.src"
                        :poster="item.poster || null"
                        :autoplay="false"
                        :muted="false"
                        :max-height="460"
                        class="nf-comment-video-frame"
                        :class="{ 'nf-media-content-hidden': !isCommentMediaLoaded(getCommentMediaKey(`reply-reply-${rr.id}`, itemIndex, item)) }"
                        @loaded="markCommentMediaLoaded(getCommentMediaKey(`reply-reply-${rr.id}`, itemIndex, item))"
                        @open-viewer="openViewerWithItems(getCommentMediaItems(rr), itemIndex)"
                      />
                      <img
                        v-else
                        :src="item.src"
                        alt="Reply media"
                        class="max-h-60 w-auto max-w-full object-contain transition-opacity duration-300"
                        :class="{ 'nf-media-content-hidden': !isCommentMediaLoaded(getCommentMediaKey(`reply-reply-${rr.id}`, itemIndex, item)) }"
                        @load="markCommentMediaLoaded(getCommentMediaKey(`reply-reply-${rr.id}`, itemIndex, item))"
                        @error="markCommentMediaLoaded(getCommentMediaKey(`reply-reply-${rr.id}`, itemIndex, item))"
                        @click.stop="openViewerWithItems(getCommentMediaItems(rr), itemIndex)"
                      />
                    </div>
                  </div>
                </template>
                </div>



          </div>

          <!-- meta row -->
          <div class="relative flex items-center justify-between w-full px-1">
            <div class="relative flex items-center gap-3 pl-1">
              <div class="relative group/replyReplyTime">
                <div class="text-xs text-zinc-500 cursor-pointer font-semibold hover:underline">
                  {{ formatCommentTime(rr.createdAt || rr.created_at) }}
                </div>

                <div
                  class="pointer-events-none absolute hidden group-hover/replyReplyTime:block
                         px-3 py-2 text-xs rounded-xl bg-black/70 text-white whitespace-nowrap
                         z-[230] top-5 left-0 shadow-lg"
                >
                  {{ formatCommentFullTooltip(rr.createdAt || rr.created_at) }}
                </div>
              </div>

              <!-- Like (opens picker on hover) -->
              <div
                class="relative -mt-[2px] nf-picker-anchor"
                @mouseenter="handleReplyReplyReactionMouseEnter(rr.id)"
                @mouseleave="handleReplyReplyReactionMouseLeave(rr.id)"
              >
                <button
                  type="button"
                  class="text-xs font-semibold hover:underline"
                  :class="getReplyReplyReactionType(rr.id) ? 'text-[#2D8CFF]' : 'text-zinc-600'"
                >
                  Like
                </button>

                <NewsfeedReactionPicker
                  v-if="openReplyReplyReactionForId === rr.id"
                  :portal="props.inlineSidebar"
                  :position-class="props.inlineSidebar ? 'absolute bottom-full mb-2 right-0' : 'absolute bottom-full left-10'"
                  @select="(type) => handleReplyReplyReactionSelect(rr.id, type)"
                  @hover-change="(hovering) => handleReplyReplyPickerHoverChange(rr.id, hovering)"
                />
              </div>

              <!-- Reply (depth cap: stays depth-2, adds another reply-reply under the same parent reply) -->
                    <button
                    type="button"
                    class="text-xs text-zinc-600 cursor-pointer font-semibold hover:underline"
                    @click.stop="openReplyReplyBoxFromReplyReply(r.id, rr)"
                    >
                    Reply
                    </button>

            </div>

            <!-- right badge -->
            <div
              v-if="getReplyReplyReactionCount(rr.id) > 0"
              class="relative group flex items-center gap-1"
              @mouseenter="handleReplyReplyReactionTooltipEnter(rr.id, $event)"
            >
              <span
                v-if="getReplyReplyReactionCount(rr.id) > 1"
                class="text-[11px] font-semibold text-zinc-600 leading-none"
              >
                {{ getReplyReplyReactionCount(rr.id) }}
              </span>

              <div class="flex items-center -space-x-1">
                <img
                  v-for="(type, index) in getReplyReplyTopReactionTypes(rr.id)"
                  :key="`${rr.id}-reply-reply-reaction-${type}`"
                  :src="REACTION_META[type]?.icon"
                  :alt="REACTION_META[type]?.label || 'Reaction'"
                  class="h-[18px] w-[18px] rounded-full ring-2 ring-white cursor-pointer"
                  :style="{ zIndex: getReplyReplyTopReactionTypes(rr.id).length - index }"
                />
              </div>

              <div
                v-if="getReplyReplyReactionTooltipRows(rr.id).length || getReplyReplyReactionTooltipText(rr.id)"
                class="pointer-events-none absolute z-[230] right-0
                       px-3 py-2 rounded-xl bg-black/80 text-[11px] text-white shadow-lg
                       opacity-0 translate-y-1
                       group-hover:opacity-100 group-hover:translate-y-0
                       transition-all duration-150
                       whitespace-nowrap"
                :class="replyReplyReactionTooltipPosition[String(rr.id)] === 'above' ? 'bottom-full mb-2' : 'top-full mt-2'"
              >
                <template v-if="getReplyReplyReactionTooltipRows(rr.id).length > 1">
                  <div
                    v-for="row in getReplyReplyReactionTooltipRows(rr.id)"
                    :key="`${rr.id}-reply-reply-tooltip-${row.type}`"
                    class="flex items-center gap-1 mb-1 last:mb-0"
                  >
                    <img
                      :src="REACTION_META[row.type]?.icon"
                      :alt="REACTION_META[row.type]?.label || 'Reaction'"
                      class="h-4 w-4"
                    />
                    <span class="font-semibold">{{ row.count }}</span>
                  </div>
                </template>
                <template v-else>
                  <div class="flex items-center gap-1 mb-2">
                    <img
                      :src="REACTION_META[getReplyReplyTopReactionTypes(rr.id)[0] || 'like']?.icon"
                      :alt="REACTION_META[getReplyReplyTopReactionTypes(rr.id)[0] || 'like']?.label || 'Reaction'"
                      class="h-4 w-4"
                    />
                    <span class="font-semibold">
                      {{ getReplyReplyReactionCount(rr.id) }}
                    </span>
                  </div>

                  <div class="max-w-[220px] text-zinc-200 mt-1 leading-tight">
                    <div
                      v-for="name in getReplyReplyReactionTooltipNames(rr.id)"
                      :key="`${rr.id}-reply-reply-name-${name}`"
                      class="truncate"
                    >
                      {{ name }}
                    </div>
                    <div
                      v-if="getReplyReplyReactionTooltipExtraCount(rr.id) > 0"
                      class="mt-1 text-[11px] text-zinc-300"
                    >
                      +{{ getReplyReplyReactionTooltipExtraCount(rr.id) }} more
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

<!-- nested composer (reply-to-reply) -->
<div
  v-if="openReplyReplyForReplyId === r.id"
  class="mt-2 w-full"
>
  <div class="relative flex items-start gap-2 w-full min-w-0">
    <div
      class="pointer-events-none absolute left-[-24px] top-[0px] w-5 h-4
             border-l-[2px] border-b-[2px] border-zinc-200 rounded-bl-xl"
    ></div>

    <img
      :src="commenterAvatarSrc"
      alt="Your avatar"
      class="h-6 w-6 rounded-full object-cover"
      :ref="(el) => setReplyReplyComposerAvatarRef(r.id, el)"
      @error="onAvatarLoadError"
    />

    <div class="flex-1 bg-[#f0f3f8] rounded-2xl px-3 py-2 w-full min-w-0">

      <!-- Bold "replying to" prefix token (FB/LinkedIn style) -->
      <div v-if="replyReplyPrefixIsActive(r.id)" class="mb-1">
        <span class="font-base text-sm text-zinc-900 bg-blue-200 p-1 rounded-md">
          {{ replyReplyingToByReplyId[r.id]?.name }}
        </span>
      </div>

      <textarea
        v-model="replyReplyTextByReplyId[r.id]"
        rows="1"
        class="w-full bg-transparent border-none outline-none focus:outline-none focus:ring-0
               text-sm text-zinc-900 placeholder:text-zinc-500 resize-none overflow-y-auto app-scroll"
        :placeholder="replyReplyComposerPlaceholder(r.id)"
        @keydown="(e) => handleReplyReplyComposerKeydown(r.id, e)"
        @keydown.enter.exact.prevent="submitReplyReply(c.id, r.id)"
        @input="autoResizeInlineTextarea"
        style="max-height: 320px;"
      ></textarea>

      <div v-if="pendingReplyReplyMediaByReplyId[r.id]" class="mt-2">
        <div class="relative inline-block rounded-lg overflow-hidden border border-zinc-300 bg-white">
          <img
            v-if="pendingReplyReplyMediaByReplyId[r.id]?.type === 'image'"
            :src="pendingReplyReplyMediaByReplyId[r.id]?.previewUrl"
            alt="Selected reply media"
            class="max-h-24 max-w-[180px] object-cover"
          />
          <video
            v-else
            :src="pendingReplyReplyMediaByReplyId[r.id]?.previewUrl"
            class="max-h-24 max-w-[180px] object-cover"
            controls
            muted
          ></video>
          <button
            type="button"
            class="absolute top-1 right-1 rounded-full bg-zinc-200/95 hover:bg-zinc-300 p-1 shadow-sm"
            @mousedown.prevent
            @click.prevent="clearPendingReplyReplyMedia(r.id)"
            aria-label="Remove media"
          >
            <XMarkIcon class="w-4 h-4 text-zinc-700" />
          </button>
        </div>
      </div>

      <input
        :ref="(el) => setReplyReplyMediaInputRef(r.id, el)"
        type="file"
        class="hidden"
        accept="image/*,video/*"
        @change="(e) => handleReplyReplyMediaPicked(r.id, e)"
      />

      <div class="flex items-center justify-between mt-1">
        <div class="relative flex items-center gap-1">
          <button
            type="button"
            class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-zinc-200 p-[2px]"
            @mousedown.prevent
            @click.prevent="toggleReplyReplyEmojiPicker(r.id)"
          >
            <FaceSmileIcon class="w-6 h-6 text-zinc-400" />
          </button>
          <div
            v-if="openReplyReplyEmojiForReplyId === r.id"
            class="absolute bottom-full left-0 mb-2 z-40"
          >
            <EmojiPicker @select="(emoji) => addReplyReplyEmoji(r.id, emoji)" />
          </div>
          <button
            type="button"
            class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-zinc-200 p-[2px]"
            @mousedown.prevent
            @click.prevent="triggerReplyReplyMediaPicker(r.id)"
          >
            <CameraIcon class="w-6 h-6 text-zinc-400" />
          </button>
        </div>
        <button
            type="button"
            class="flex items-center justify-center w-7 h-7 rounded-full hover:bg-zinc-200 p-[2px]"
            @click="submitReplyReply(c.id, r.id)"
            :disabled="isSubmittingReplyReply || !hasReplyReplyText(r.id)"
        >
            <PaperAirplaneIcon
            class="w-5 h-5"
            :class="hasReplyReplyText(r.id) && !isSubmittingReplyReply ? 'text-[#2D8CFF]' : 'text-zinc-300'"
            />
        </button>
        </div>

    </div>
  </div>
</div>


</div>
<!-- END depth2 block -->




                          
                        </div>
                      </div>
                    </div>

                    <!-- Reply box (MVP) -->
                <div v-if="openReplyForCommentId === c.id" class="mt-2 pl-1 w-full">
                <div class="relative flex items-start gap-2 w-full min-w-0">
                    <!-- branch elbow for composer -->
                    <div
                    class="pointer-events-none absolute left-[-28px] top-[0px] w-6 h-4
                            border-l-[2px] border-b-[2px] border-zinc-200 rounded-bl-xl"
                    ></div>

                    <img
                    :src="commenterAvatarSrc"
                    alt="Your avatar"
                    class="h-6 w-6 rounded-full object-cover"
                    :ref="(el) => setReplyComposerAvatarRef(c.id, el)"
                    @error="onAvatarLoadError"
                    />

                    <div class="flex-1 bg-[#f0f3f8] rounded-2xl px-3 py-2 w-full min-w-0">

                    <!-- Bold "replying to" prefix token (same as reply-to-reply) -->
                    <div v-if="replyPrefixIsActive(c.id)" class="mb-1">
                        <span class="font-base text-sm text-zinc-900 bg-blue-200 p-1 rounded-md">
                        {{ replyReplyingToByCommentId?.[c.id]?.name }}
                        </span>
                    </div>

      <textarea
        v-model="replyTextByCommentId[c.id]"
                        rows="1"
                        class="w-full bg-transparent border-none outline-none focus:outline-none focus:ring-0
                            text-sm text-zinc-900 placeholder:text-zinc-500 resize-none overflow-y-auto app-scroll"
                        :placeholder="replyComposerPlaceholder(c.id)"
                        @focus="setReplyingToForCommentIfMissing(c.id)"
                        @keydown="(e) => onReplyTextareaKeydown(c.id, e)"
                        @input="autoResizeInlineTextarea"
        style="max-height: 320px;"
      ></textarea>

      <div v-if="pendingReplyMediaByCommentId[c.id]" class="mt-2">
        <div class="relative inline-block rounded-lg overflow-hidden border border-zinc-300 bg-white">
          <img
            v-if="pendingReplyMediaByCommentId[c.id]?.type === 'image'"
            :src="pendingReplyMediaByCommentId[c.id]?.previewUrl"
            alt="Selected reply media"
            class="max-h-24 max-w-[180px] object-cover"
          />
          <video
            v-else
            :src="pendingReplyMediaByCommentId[c.id]?.previewUrl"
            class="max-h-24 max-w-[180px] object-cover"
            controls
            muted
          ></video>
          <button
            type="button"
            class="absolute top-1 right-1 rounded-full bg-zinc-200/95 hover:bg-zinc-300 p-1 shadow-sm"
            @mousedown.prevent
            @click.prevent="clearPendingReplyMedia(c.id)"
            aria-label="Remove media"
          >
            <XMarkIcon class="w-4 h-4 text-zinc-700" />
          </button>
        </div>
      </div>

      <input
        :ref="(el) => setReplyMediaInputRef(c.id, el)"
        type="file"
        class="hidden"
        accept="image/*,video/*"
        @change="(e) => handleReplyMediaPicked(c.id, e)"
      />

      <div class="flex items-center justify-between mt-1">
        <div class="relative flex items-center gap-1">
          <button
            type="button"
            class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-zinc-200 p-[2px]"
            @mousedown.prevent
            @click.prevent="toggleReplyEmojiPicker(c.id)"
          >
            <FaceSmileIcon class="w-6 h-6 text-zinc-400" />
          </button>
          <div
            v-if="openReplyEmojiForCommentId === c.id"
            class="absolute bottom-full left-0 mb-2 z-40"
          >
            <EmojiPicker @select="(emoji) => addReplyEmoji(c.id, emoji)" />
          </div>
          <button
            type="button"
            class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-zinc-200 p-[2px]"
            @mousedown.prevent
            @click.prevent="triggerReplyMediaPicker(c.id)"
          >
            <CameraIcon class="w-6 h-6 text-zinc-400" />
          </button>
        </div>
        <button
            type="button"
            class="flex items-center justify-center w-7 h-7 rounded-full hover:bg-zinc-200 p-[2px]"
            @click="submitReply(c.id)"
            :disabled="isSubmittingReply || !hasReplyText(c.id)"
                    >
                        <PaperAirplaneIcon
                        class="w-5 h-5"
                        :class="hasReplyText(c.id) && !isSubmittingReply ? 'text-[#2D8CFF]' : 'text-zinc-300'"
                        />
                    </button>
                    </div>

                    </div>
                </div>
                </div>


                  </div>
                </div>
              </div>

            </div>

            <!-- Media viewer (reuse your existing component) -->
            <NewsFeedMediaPostViewer
              v-if="!props.inlineSidebar"
              :show="viewerOpen"
              :media-items="viewerMediaItems"
              :initial-index="viewerIndex"
              :feature-instance-id="resolvedMediaFeatureInstanceId"
              :slug-component="slugSidebarComponent"
              :post="props.post"
              :author-avatar-src="authorAvatarSrc"
              :commenter-avatar-src="commenterAvatarSrc"
              :display-time="displayTime"
              @close="viewerOpen = false"
              @open-full-post="(payload) => emit('open-full-post', payload)"
            />
          </div>
        </div>

        <!-- Quick reply (bottom-anchored so it grows UP) -->
        <div
          ref="composerEl"
          class="absolute left-0 right-0 bottom-0 bg-white border-t border-zinc-200 px-5 py-4 z-40"
        >
          <div class="flex items-start gap-2">
            <img :src="commenterAvatarSrc" alt="Your avatar" class="h-8 w-8 rounded-full object-cover" @error="onAvatarLoadError" />

            <div
              class="flex-1 bg-[#f0f3f8] px-3 text-sm text-zinc-500 transition-all duration-150 rounded-2xl py-2 flex flex-col"
            >
              <div class="flex items-center gap-2" :class="isCommentActive ? '' : 'flex-1'">
                <textarea
                  ref="commentTextarea"
                  v-model="commentText"
                  rows="1"
                  class="flex-1 bg-transparent border-none outline-none focus:outline-none focus:ring-0
                        text-sm text-zinc-900 placeholder:text-zinc-500 resize-none overflow-y-auto px-0 app-scroll"
                  :placeholder="`Comment as ${postingAsNameComputed}`"
                  @focus="isCommentActive = true"
                  @blur="handleCommentBlur"
                  style="max-height: 480px;"
                  @input="autoResize"
                  @keydown.enter.exact.prevent="submitComment"
                  @keydown.enter.shift.exact.stop
                ></textarea>

                <div v-if="!isCommentActive" class="relative flex items-center gap-1 text-zinc-400 text-lg ml-2">
                  <button type="button" class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-zinc-200 p-[2px]" @click.prevent="toggleCommentEmojiPicker">
                    <FaceSmileIcon class="w-6 h-6 text-zinc-400" />
                  </button>
                  <div v-if="showCommentEmojiPicker" class="absolute bottom-full left-0 mb-2 z-40">
                    <EmojiPicker @select="addCommentEmoji" />
                  </div>
                  <button type="button" class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-zinc-200 p-[2px]" @click.prevent="triggerCommentMediaPicker">
                    <CameraIcon class="w-6 h-6 text-zinc-400" />
                  </button>
                </div>
              </div>

              <div v-if="pendingCommentMedia" class="mt-2">
                <div class="relative inline-block rounded-lg overflow-hidden border border-zinc-300 bg-white">
                  <img
                    v-if="pendingCommentMedia.type === 'image'"
                    :src="pendingCommentMedia.previewUrl"
                    alt="Selected comment media"
                    class="max-h-24 max-w-[180px] object-cover"
                  />
                  <video
                    v-else
                    :src="pendingCommentMedia.previewUrl"
                    class="max-h-24 max-w-[180px] object-cover"
                    controls
                    muted
                  ></video>
                  <button
                    type="button"
                    class="absolute top-1 right-1 rounded-full bg-zinc-200/95 hover:bg-zinc-300 p-1 shadow-sm"
                    @mousedown.prevent
                    @click.prevent="clearPendingCommentMedia"
                    aria-label="Remove media"
                  >
                    <XMarkIcon class="w-4 h-4 text-zinc-700" />
                  </button>
                </div>
              </div>

              <input
                ref="commentMediaInput"
                type="file"
                class="hidden"
                accept="image/*,video/*"
                @change="handleCommentMediaPicked"
              />

              <div v-if="isCommentActive" class="flex items-center justify-between text-zinc-400 text-lg mt-2">
                <div class="relative flex items-center gap-1">
                  <button type="button" class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-zinc-200 p-[2px]" @click.prevent="toggleCommentEmojiPicker">
                    <FaceSmileIcon class="w-6 h-6 text-zinc-400" />
                  </button>
                  <div v-if="showCommentEmojiPicker" class="absolute bottom-full left-0 mb-2 z-40">
                    <EmojiPicker @select="addCommentEmoji" />
                  </div>
                  <button type="button" class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-zinc-200 p-[2px]" @click.prevent="triggerCommentMediaPicker">
                    <CameraIcon class="w-6 h-6 text-zinc-400" />
                  </button>
                </div>

                <button
                type="button"
                class="flex items-center justify-center w-7 h-7 rounded-full hover:bg-zinc-200 p-[2px]"
                @click="submitComment"
                :disabled="isSubmittingComment || !hasCommentText"
                >
                <PaperAirplaneIcon
                    class="w-5 h-5"
                    :class="hasCommentText && !isSubmittingComment ? 'text-[#2D8CFF]' : 'text-zinc-300'"
                />
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </Transition>
</template>

<script setup>
import { computed, defineProps, defineEmits, ref, watch, nextTick, onMounted, onBeforeUnmount, reactive, getCurrentInstance } from 'vue';
import { FaceSmileIcon, CameraIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import { DocumentTextIcon, PaperAirplaneIcon, PlayIcon } from '@heroicons/vue/24/solid';

import NewsFeedMediaPostViewer from '@/Components/NewsFeedMediaPostViewer.vue';
import NewsfeedInlineVideoPlayer from '@/Components/NewsfeedInlineVideoPlayer.vue';
import NewsfeedPostPollCard from '@/Components/NewsfeedPostPollCard.vue';
import NewsfeedReactionPicker from '@/Components/NewsfeedReactionPicker.vue';
import NewsfeedCreatorCommentReplyMenu from '@/Components/NewsfeedCreatorCommentReplyMenu.vue';
import EmojiPicker from 'vue3-emoji-picker';
import 'vue3-emoji-picker/css';
import { useMediaUploadStore } from '@/stores/mediaUploadStore';
import { useWebsocketStore } from '@/stores/websocketStore';
import processingPoster from '@/assets/media/images/chat-video-thumb.gif';
import {
  detectNewsfeedBackgroundTone,
  getNewsfeedBackgroundById,
  guessNewsfeedBackgroundTone,
} from '@/utils/newsfeedPostBackgrounds';


// ⬇️ BFF proxy base URL
const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;
const mediaUploadStore = useMediaUploadStore();
const websocketStore = useWebsocketStore();
const app = getCurrentInstance();
const notify = app?.appContext?.config?.globalProperties?.$notify;
const slugSidebarComponent = app?.type || null;

const props = defineProps({
  isOpen: { type: Boolean, default: null },
  post: { type: Object, required: null },
  postingAsName: { type: String, default: 'You' },
  postingAsAvatarUrl: { type: String, default: null },
  featureInstanceId: { type: [Number, String], default: null },
  inlineSidebar: { type: Boolean, default: false },
});

const resolvedMediaFeatureInstanceId = computed(() => {
  const rawValue =
    props.featureInstanceId ??
    props.post?.featureInstanceId ??
    props.post?.feature_instance_id ??
    props.post?.instanceId ??
    props.post?.instance_id ??
    null;

  if (rawValue == null) return null;
  const normalized = String(rawValue).trim();
  return normalized ? normalized : null;
});

const buildNewsfeedImageProxyUrl = (fileId, options = {}) => {
  const normalizedFileId = String(fileId || '').trim();
  if (!normalizedFileId) return null;

  const params = new URLSearchParams();
  if (resolvedMediaFeatureInstanceId.value) {
    params.set('featureInstanceId', resolvedMediaFeatureInstanceId.value);
  }

  const variant = String(options?.variant || '').trim().toLowerCase();
  if (variant) {
    params.set('variant', variant);
  }

  const query = params.toString();
  return `${apiProxyUrl}/api/creator-newsfeed/image/${encodeURIComponent(normalizedFileId)}${
    query ? `?${query}` : ''
  }`;
};

const extractFileIdFromLegacyImageUrl = (rawUrl) => {
  const value = String(rawUrl || '').trim();
  if (!value) return null;

  const match = value.match(
    /\/api\/(?:creator-newsfeed\/image|newsfeed\/image|image)\/([0-9a-fA-F-]{36})(?:[/?#]|$)/
  );
  return match?.[1] ? String(match[1]) : null;
};

const isKnownDefaultAvatarPlaceholder = (rawUrl) => {
  const value = String(rawUrl || '').trim().toLowerCase();
  if (!value) return false;

  return (
    value.startsWith('@/assets/') ||
    value.startsWith('src/assets/') ||
    value.startsWith('/src/assets/') ||
    value.includes('/media/images/default-avatar.png') ||
    value.endsWith('/default-avatar.png') ||
    value === 'default-avatar.png' ||
    value.includes('/src/assets/media/images/default-avatar') ||
    value.includes('/src/assets/media/images/woman-avatar')
  );
};

const normalizeAvatarUrlForCreatorProxy = (rawUrl) => {
  const value = String(rawUrl || '').trim();
  if (!value) return null;
  if (isKnownDefaultAvatarPlaceholder(value)) return null;

  const legacyFileId = extractFileIdFromLegacyImageUrl(value);
  if (legacyFileId) {
    return buildNewsfeedImageProxyUrl(legacyFileId) || value;
  }

  return value;
};

const resolveAvatarFromEntity = (entity, fallback = defaultAuthorAvatar) => {
  if (!entity || typeof entity !== 'object') return fallback;

  const avatarFileId =
    entity.authorAvatarFileId ||
    entity.author_avatar_file_id ||
    entity.avatarFileId ||
    entity.avatar_file_id ||
    entity.postedAsAvatarFileId ||
    entity.posted_as_avatar_file_id ||
    null;
  if (avatarFileId) {
    const proxy = buildNewsfeedImageProxyUrl(avatarFileId);
    if (proxy) return proxy;
  }

  const raw =
    entity.authorAvatarUrl ||
    entity.author_avatar_url ||
    entity.avatarUrl ||
    entity.avatar_url ||
    entity.authorAvatar ||
    entity.author_avatar ||
    entity.postedAsAvatarUrl ||
    entity.posted_as_avatar_url ||
    null;
  const normalized = normalizeAvatarUrlForCreatorProxy(raw);
  return normalized || fallback;
};

const onAvatarLoadError = (event) => {
  const imgEl = event?.target;
  if (!(imgEl instanceof HTMLImageElement)) return;
  if (imgEl.dataset.avatarFallbackApplied === '1') return;
  imgEl.dataset.avatarFallbackApplied = '1';
  imgEl.src = defaultAuthorAvatar;
};

const emit = defineEmits(['close', 'comment-created', 'react']);


// assets
const defaultAuthorAvatar = new URL('@/assets/media/images/default-avatar.png', import.meta.url).href;

const likeThumbIcon = new URL('@/assets/media/images/platforma-like-thumb.svg', import.meta.url).href;
const loveIcon  = new URL('@/assets/media/images/newsfeed-heart.svg', import.meta.url).href;
const careIcon  = new URL('@/assets/media/images/newsfeed-care.svg', import.meta.url).href;
const wowIcon   = new URL('@/assets/media/images/newsfeed-wow.svg', import.meta.url).href;
const sadIcon   = new URL('@/assets/media/images/newsfeed-sad.svg', import.meta.url).href;
const angryIcon = new URL('@/assets/media/images/newsfeed-angry.svg', import.meta.url).href;
const hahaIcon  = new URL('@/assets/media/images/newsfeed-laugh.svg', import.meta.url).href;

const REACTION_META = {
  like:  { label: 'Like',  icon: likeThumbIcon },
  love:  { label: 'Love',  icon: loveIcon },
  care:  { label: 'Care',  icon: careIcon },
  haha:  { label: 'Haha',  icon: hahaIcon },
  wow:   { label: 'Wow',   icon: wowIcon },
  sad:   { label: 'Sad',   icon: sadIcon },
  angry: { label: 'Angry', icon: angryIcon },
};

const REACTION_KEYS = ['like', 'love', 'care', 'haha', 'wow', 'sad', 'angry'];

const normalizeReactionUserName = (entry) => {
  if (typeof entry === 'string') return entry.trim();
  if (!entry || typeof entry !== 'object') return '';
  return String(
    entry.name ||
    entry.displayName ||
    entry.authorName ||
    entry.author_name ||
    ''
  ).trim();
};

const resolveReactionTypeFromEntity = (entity) => {
  if (!entity || typeof entity !== 'object') return null;
  const explicit = String(
    entity.myReactionType ||
    entity.my_reaction_type ||
    ''
  ).toLowerCase().trim();
  if (explicit && REACTION_KEYS.includes(explicit)) return explicit;
  return null;
};

const getSortedReactionRowsFromEntity = (entity) => {
  const summary = entity?.reactionsSummary || entity?.reactions_summary || {};
  return REACTION_KEYS
    .map((type, index) => ({
      type,
      count: Number(summary?.[type]) || 0,
      index,
    }))
    .filter((row) => row.count > 0)
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.index - b.index;
    });
};

// derived
const postingAsNameComputed = computed(() => props.post?.currentUserName || props.postingAsName || 'You');

const hasPoll = computed(() => {
  return !!(
    props.post?.poll ||
    props.post?.pollData ||
    props.post?.postPoll ||
    props.post?.postType === 'poll' ||
    props.post?.post_type === 'poll' ||
    Array.isArray(props.post?.pollOptions)
  );
});

const canManagePoll = computed(() => {
  const currentId = props.post?.currentUserId || props.post?.current_user_id || null;
  const currentType = props.post?.currentUserType || props.post?.current_user_type || null;
  const ownerId = props.post?.authorUserId || props.post?.author_user_id || null;
  const ownerType = props.post?.authorUserType || props.post?.author_user_type || null;

  if (currentId != null && ownerId != null) {
    if (String(currentId) !== String(ownerId)) return false;
    if (currentType != null && ownerType != null) {
      return String(currentType) === String(ownerType);
    }
    return true;
  }

  const me = String(postingAsNameComputed.value || '').trim().toLowerCase();
  const author = String(props.post?.authorName || '').trim().toLowerCase();
  return !!me && !!author && me === author;
});

const authorAvatarSrc = computed(() => {
  const postAvatar = resolveAvatarFromEntity(props.post || {}, null);
  return postAvatar || defaultAuthorAvatar;
});

const commenterAvatarSrc = computed(() => {
  const postingAsAvatar = normalizeAvatarUrlForCreatorProxy(props.postingAsAvatarUrl);
  const postAvatar = resolveAvatarFromEntity(props.post || {}, null);
  return postingAsAvatar || postAvatar || defaultAuthorAvatar;
});

// time
const createdAtDate = computed(() => {
  const src = props.post?.createdAt || props.post?.timeAgo;
  if (!src) return null;
  const d = new Date(src);
  if (Number.isNaN(d.getTime())) return null;
  return d;
});

const displayTime = computed(() => {
  const d = createdAtDate.value;
  if (!d) return props.post?.timeAgo || '';

  const now = new Date();
  let diffMs = now.getTime() - d.getTime();
  if (diffMs < 0) diffMs = 0;

  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'Just now';

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min`;

  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours} hr`;

  return formatShortDate(d);
});

const fullTimeTooltip = computed(() => {
  const d = createdAtDate.value;
  if (!d) return props.post?.timeAgo || '';
  return formatFullDate(d);
});

function formatShortDate(d) {
  const months = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
  ];
  const month = months[d.getMonth()];
  const day = d.getDate();
  const { hour12, minutes, ampm } = formatTimeParts(d);
  return `${month} ${day} at ${hour12}:${minutes} ${ampm}`;
}

function formatFullDate(d) {
  const weekdays = [
    'Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday',
  ];
  const months = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
  ];

  const weekday = weekdays[d.getDay()];
  const month = months[d.getMonth()];
  const day = d.getDate();
  const year = d.getFullYear();
  const { hour12, minutes, ampm } = formatTimeParts(d);

  return `${weekday}, ${month} ${day}, ${year} at ${hour12}:${minutes} ${ampm}`;
}

function formatTimeParts(d) {
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return { hour12: hours, minutes, ampm };
}

// body
const rawBody = computed(() => props.post?.body || props.post?.content || '');
const backgroundKey = computed(() => {
  return (
    props.post?.backgroundKey ||
    props.post?.backgroundColor ||
    props.post?.background_color ||
    null
  );
});
const resolvedBackground = computed(() => getNewsfeedBackgroundById(backgroundKey.value));
const hasAnyAttachedMedia = computed(() => {
  const atts = Array.isArray(props.post?.attachments) ? props.post.attachments : [];
  const coverMediaFileId =
    props.post?.coverMediaFileId ||
    props.post?.cover_media_file_id ||
    null;
  return atts.length > 0 || !!coverMediaFileId;
});
const hasTextBackground = computed(
  () => !!resolvedBackground.value && !hasPoll.value && !hasAnyAttachedMedia.value
);
const textBackgroundStyle = computed(() => {
  if (!hasTextBackground.value) return {};
  return {
    backgroundImage: `url(${resolvedBackground.value.backgroundUrl})`,
  };
});
const textBackgroundTone = ref('dark');
const backgroundTextToneClass = computed(() => {
  return textBackgroundTone.value === 'light'
    ? 'text-[#1f2937] font-semibold drop-shadow-[0_1px_8px_rgba(255,255,255,0.35)]'
    : 'text-white font-semibold drop-shadow-[0_1px_8px_rgba(0,0,0,0.45)]';
});
const backgroundTextButtonClass = computed(() => {
  return textBackgroundTone.value === 'light' ? 'text-[#1f2937]' : 'text-white';
});
watch(
  () => resolvedBackground.value?.backgroundUrl || '',
  async (url) => {
    if (!url) {
      textBackgroundTone.value = 'dark';
      return;
    }
    textBackgroundTone.value = guessNewsfeedBackgroundTone(url);
    textBackgroundTone.value = await detectNewsfeedBackgroundTone(url);
  },
  { immediate: true }
);
const rawBodyTrimmed = computed(() => String(rawBody.value || '').trim());

const showFullBody = ref(false);

const PREVIEW_MAX_LINES = 3;
const PREVIEW_MAX_CHARS = 220;

const bodyPreviewText = computed(() => {
  const body = rawBody.value;
  if (!body) return '';

  const lines = body.split(/\r?\n/);
  let result = '';
  let linesUsed = 0;

  for (let i = 0; i < lines.length && linesUsed < PREVIEW_MAX_LINES; i++) {
    const line = lines[i];
    const prefix = linesUsed === 0 ? '' : '\n';

    const projectedLength = result.length + prefix.length + line.length;

    if (projectedLength > PREVIEW_MAX_CHARS) {
      const remaining = PREVIEW_MAX_CHARS - result.length - prefix.length;
      if (remaining > 0) result += prefix + line.slice(0, remaining);
      linesUsed++;
      break;
    } else {
      result += prefix + line;
      linesUsed++;
    }
  }

  result = result.replace(/[\s\n]+$/g, '');
  if (result.length < body.length) return `${result}…`;
  return result;
});

const isBodyLong = computed(() => {
  if (hasTextBackground.value) return false;
  return bodyPreviewText.value.length < rawBody.value.length;
});

const displayBodyText = computed(() => {
  const body = rawBody.value;
  if (hasTextBackground.value) return body;
  if (!isBodyLong.value || showFullBody.value) return body;
  return bodyPreviewText.value;
});

function escapeHtml(raw) {
  return String(raw || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function linkifyText(raw) {
  const escaped = escapeHtml(raw);
  const urlRegex = /((https?:\/\/|www\.)[^\s<]+)/gi;

  return escaped.replace(urlRegex, (fullMatch) => {
    let match = fullMatch;
    let trailing = '';
    while (/[),.!?:;]+$/.test(match)) {
      trailing = match.slice(-1) + trailing;
      match = match.slice(0, -1);
    }

    const href = match.startsWith('www.') ? `https://${match}` : match;
    return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-[#2D8CFF] font-medium hover:underline break-all">${match}</a>${trailing}`;
  });
}

const displayBodyHtml = computed(() => linkifyText(displayBodyText.value));

const bodyTextClass = computed(() => {
  const body = rawBody.value || '';
  if (body.length > 0 && body.length < 85) return 'text-[22px]';
  return 'text-[16px]';
});

const imageFileExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg', '.avif'];
const videoFileExtensions = ['.mp4', '.mov', '.m4v', '.webm', '.mkv', '.avi', '.ogv'];

const inferAttachmentKind = (att) => {
  const mime = String(att?.mimeType || att?.mime || att?.contentType || '').toLowerCase().trim();
  const fileType = String(att?.fileType || att?.type || '').toLowerCase().trim();
  const url = String(att?.url || att?.secureVideoUrl || att?.videoUrl || att?.secureImageUrl || att?.imageUrl || '').toLowerCase();

  if (mime.startsWith('video/') || fileType === 'video') return 'video';
  if (mime.startsWith('image/') || fileType === 'image') return 'image';
  if (videoFileExtensions.some((ext) => url.includes(ext))) return 'video';
  if (imageFileExtensions.some((ext) => url.includes(ext))) return 'image';
  if (att?.videoUrl || att?.secureVideoUrl || att?.thumbnailUrl || att?.posterUrl) return 'video';
  return 'image';
};

const parseCommentAttachmentJson = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') return [raw];
  if (typeof raw !== 'string') return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && typeof parsed === 'object') return [parsed];
    return [];
  } catch {
    return [];
  }
};

const getCommentAttachmentList = (entity) => {
  if (!entity || typeof entity !== 'object') return [];

  const direct = entity.attachments ?? entity.mediaAttachments ?? entity.media ?? null;
  if (Array.isArray(direct)) return direct.filter(Boolean);
  if (direct && typeof direct === 'object') return [direct];

  const single = entity.attachment ?? null;
  if (Array.isArray(single)) return single.filter(Boolean);
  if (single && typeof single === 'object') return [single];

  const jsonSource = entity.attachment_json ?? entity.attachments_json ?? null;
  return parseCommentAttachmentJson(jsonSource).filter(Boolean);
};

const normalizeCommentAttachmentPayload = (uploadMeta) => {
  if (!uploadMeta || typeof uploadMeta !== 'object') return null;
  const fileId = String(uploadMeta.fileId || '').trim();
  if (!fileId) return null;
  return {
    fileId,
    file_id: fileId,
    type: uploadMeta.fileType || uploadMeta.type || 'image',
    fileType: uploadMeta.fileType || uploadMeta.type || 'image',
    mimeType: uploadMeta.mimeType || null,
    mime_type: uploadMeta.mimeType || null,
    url: uploadMeta.permanentUrl || null,
    fileUrl: uploadMeta.permanentUrl || null,
    thumbnailUrl: uploadMeta.thumbnailUrl || null,
    hlsPlaylistUrl: uploadMeta.hlsPlaylistUrl || null,
    hlsStatus: uploadMeta.hlsStatus || null,
  };
};

const buildCommentVideoPlaylistUrl = (fileId) => {
  const normalizedFileId = String(fileId || '').trim();
  if (!normalizedFileId) return '';
  const featureId = String(resolvedMediaFeatureInstanceId.value || '').trim();
  if (!featureId) return `${apiProxyUrl}/api/stream-hls/playlist/${encodeURIComponent(normalizedFileId)}`;
  return `${apiProxyUrl}/api/creator-newsfeed/stream-hls/playlist/${encodeURIComponent(normalizedFileId)}?featureInstanceId=${encodeURIComponent(featureId)}`;
};

const getCommentMediaItems = (entity) => {
  const atts = getCommentAttachmentList(entity);
  const items = [];

  for (const att of atts) {
    const fileId = att?.fileId || att?.file_id || att?.mediaFileId || null;
    const kind = inferAttachmentKind(att);
    const imageSrc =
      buildNewsfeedImageProxyUrl(fileId) ||
      att?.imageUrl ||
      att?.secureImageUrl ||
      att?.thumbnailUrl ||
      att?.fileUrl ||
      att?.file_url ||
      att?.url ||
      null;
    const videoSrc =
      att?.hlsPlaylistUrl ||
      att?.hls_playlist_url ||
      att?.secureVideoUrl ||
      att?.videoUrl ||
      att?.fileUrl ||
      att?.file_url ||
      att?.url ||
      (fileId ? buildCommentVideoPlaylistUrl(fileId) : null);
    const poster =
      att?.thumbnailUrl ||
      att?.posterUrl ||
      buildNewsfeedImageProxyUrl(fileId, { variant: 'thumbnail' }) ||
      imageSrc ||
      null;

    if (kind === 'video') {
      if (!videoSrc) continue;
      items.push({
        type: 'video',
        fileId: fileId ? String(fileId) : null,
        src: videoSrc,
        poster,
      });
    } else if (imageSrc) {
      items.push({
        type: 'image',
        fileId: fileId ? String(fileId) : null,
        src: imageSrc,
      });
    }
  }

  const hasProcessingVideo = Boolean(entity?.mediaProcessing ?? entity?.media_processing);
  if (hasProcessingVideo && !items.some((item) => item?.type === 'video' || item?.fileId === 'processing-video')) {
    items.push({
      type: 'image',
      fileId: 'processing-video',
      src: processingPoster,
    });
  }

  return items;
};

const loadedCommentMediaKeys = ref({});
const commentMediaSkeletonFallbackTimers = new Map();

const getCommentMediaKey = (entityId, itemIndex, item) => {
  const idPart = String(entityId ?? 'comment');
  const srcPart = String(item?.fileId || item?.src || item?.poster || itemIndex || 'media');
  return `${idPart}:${srcPart}:${itemIndex}`;
};

const isCommentMediaLoaded = (mediaKey) => !!loadedCommentMediaKeys.value?.[mediaKey];

const scheduleThreadRecalcForMediaKey = (mediaKey) => {
  const key = String(mediaKey || '');
  if (!key) return;
  const idPart = key.split(':')[0] || '';
  if (!idPart) return;

  // Comment media key: "123:..."
  if (/^\d+$/.test(idPart)) {
    const commentId = Number(idPart);
    if (commentId > 0) scheduleRecalcThreadLine(commentId);
    return;
  }

  // Reply media key: "reply-456:..."
  if (idPart.startsWith('reply-')) {
    const replyId = Number(idPart.replace(/^reply-/, ''));
    if (replyId > 0) {
      scheduleRecalcReplyThreadLine(replyId);
      const parentCommentId = findParentCommentIdForReply(replyId);
      if (parentCommentId > 0) scheduleRecalcThreadLine(parentCommentId);
    }
    return;
  }

  // Reply-reply media key: "reply-reply-789:..."
  if (idPart.startsWith('reply-reply-')) {
    const replyReplyId = Number(idPart.replace(/^reply-reply-/, ''));
    if (replyReplyId > 0) {
      const parentReplyId = findParentReplyIdForReplyReply(replyReplyId);
      if (parentReplyId > 0) {
        scheduleRecalcReplyThreadLine(parentReplyId);
        const parentCommentId = findParentCommentIdForReply(parentReplyId);
        if (parentCommentId > 0) scheduleRecalcThreadLine(parentCommentId);
      }
    }
  }
};

const markCommentMediaLoaded = (mediaKey) => {
  if (!mediaKey) return;
  if (loadedCommentMediaKeys.value?.[mediaKey]) {
    void nextTick(() => scheduleThreadRecalcForMediaKey(mediaKey));
    return;
  }
  loadedCommentMediaKeys.value = {
    ...(loadedCommentMediaKeys.value || {}),
    [mediaKey]: true,
  };
  const timer = commentMediaSkeletonFallbackTimers.get(mediaKey);
  if (timer) {
    clearTimeout(timer);
    commentMediaSkeletonFallbackTimers.delete(mediaKey);
  }
  void nextTick(() => scheduleThreadRecalcForMediaKey(mediaKey));
};

const ensureCommentMediaSkeletonFallback = (mediaKey) => {
  if (!mediaKey || isCommentMediaLoaded(mediaKey) || commentMediaSkeletonFallbackTimers.has(mediaKey)) return;
  const timer = setTimeout(() => {
    markCommentMediaLoaded(mediaKey);
    commentMediaSkeletonFallbackTimers.delete(mediaKey);
  }, 1400);
  commentMediaSkeletonFallbackTimers.set(mediaKey, timer);
};

const mediaDisplayAllItems = computed(() => {
  const items = [];
  const atts = Array.isArray(props.post?.attachments) ? props.post.attachments : [];

  for (const att of atts) {
    if (!att) continue;
    const fileId = att.fileId || att.file_id || att.mediaFileId || null;
    const kind = inferAttachmentKind(att);
    const imageSrc =
      buildNewsfeedImageProxyUrl(fileId) ||
      att.imageUrl ||
      att.secureImageUrl ||
      att.url ||
      null;
    const videoSrc = att.secureVideoUrl || att.videoUrl || att.url || null;
    const thumbnailSrc =
      att.thumbnailUrl ||
      att.posterUrl ||
      buildNewsfeedImageProxyUrl(fileId, { variant: 'thumbnail' }) ||
      imageSrc;

    if (kind === 'video') {
      const resolvedVideoSrc = videoSrc || imageSrc;
      if (!resolvedVideoSrc) continue;
      items.push({
        type: 'video',
        fileId: fileId ? String(fileId) : null,
        src: resolvedVideoSrc,
        thumbnailSrc: thumbnailSrc || imageSrc,
        poster: att.posterUrl || att.thumbnailUrl || null,
      });
    } else if (imageSrc) {
      items.push({
        type: 'image',
        fileId: fileId ? String(fileId) : null,
        src: imageSrc,
      });
    }
  }

  if (!items.length) {
    const cover =
      props.post?.secureCoverImageUrl ||
      props.post?.imageUrl ||
      props.post?.coverImageUrl ||
      (props.post?.coverMediaFileId
        ? buildNewsfeedImageProxyUrl(props.post.coverMediaFileId)
        : null);

    if (cover) {
      items.push({
        type: 'image',
        fileId: props.post?.coverMediaFileId ? String(props.post.coverMediaFileId) : null,
        src: cover,
      });
    }
  }

  return items;
});

const hasMedia = computed(() => mediaDisplayAllItems.value.length > 0);

const MAX_DISPLAY_TILES = 5;
const displayMediaItems = computed(() => mediaDisplayAllItems.value.slice(0, MAX_DISPLAY_TILES));
const extraMediaCount = computed(() =>
  mediaDisplayAllItems.value.length > MAX_DISPLAY_TILES
    ? mediaDisplayAllItems.value.length - MAX_DISPLAY_TILES
    : 0
);

const displayGridClass = computed(() => {
  const count = displayMediaItems.value.length;
  if (count === 1) return 'grid-cols-1';
  if (count === 2) return 'grid-cols-2';
  if (count === 3) return 'grid-cols-2 grid-rows-2';
  if (count === 4) return 'grid-cols-2 grid-rows-2';
  return 'grid-cols-3';
});

function displayTileClass(index) {
  const count = displayMediaItems.value.length;

  if (count === 1) return 'relative';
  if (count === 2) return 'relative aspect-[4/3]';

  if (count === 3) {
    if (index === 0) return 'relative row-span-2';
    return 'relative';
  }

  if (count === 4) return 'relative aspect-[4/3]';

  if (count >= 5) {
    if (index === 0) return 'relative col-span-2 aspect-[4/3] overflow-hidden';
    if (index === 1) return 'relative aspect-[2/3] overflow-hidden';
    return 'relative aspect-[4/3] overflow-hidden';
  }

  return 'relative';
}

const firstVideoTileIndex = computed(() =>
  displayMediaItems.value.findIndex((item) => item?.type === 'video')
);
const activeInlineVideoTileIndex = ref(null);

function isInlinePlayingVideoTile(idx) {
  const item = displayMediaItems.value[idx];
  if (!item || item.type !== 'video') return false;
  if (activeInlineVideoTileIndex.value != null) {
    return Number(activeInlineVideoTileIndex.value) === Number(idx);
  }
  return firstVideoTileIndex.value === idx;
}

function playInlineVideoAt(idx) {
  const item = displayMediaItems.value[idx];
  if (!item || item.type !== 'video') return;
  activeInlineVideoTileIndex.value = idx;
}

function handleMediaTileClick(item, idx) {
  if (item?.type === 'video') {
    if (isInlinePlayingVideoTile(idx)) {
      return;
    }
    openViewerAt(idx);
    return;
  }
  openViewerAt(idx);
}

watch(
  () => displayMediaItems.value.map((item) => `${item?.type || ''}:${item?.fileId || item?.src || ''}`).join('|'),
  () => {
    activeInlineVideoTileIndex.value = null;
  }
);

// viewer
const viewerOpen = ref(false);
const viewerIndex = ref(0);
const viewerMediaOverrideItems = ref(null);

const normalizeViewerMediaItems = (items) => {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => ({
      type: item?.type,
      src: item?.src,
      fileId: item?.fileId || null,
      poster: item?.poster || item?.thumbnailSrc || null,
      thumbnailSrc: item?.thumbnailSrc || null,
    }))
    .filter((item) => item.type && item.src);
};

const viewerMediaItems = computed(() => {
  if (Array.isArray(viewerMediaOverrideItems.value) && viewerMediaOverrideItems.value.length) {
    return viewerMediaOverrideItems.value;
  }
  return normalizeViewerMediaItems(mediaDisplayAllItems.value);
});

const openViewerAt = (idx) => {
  viewerMediaOverrideItems.value = null;
  openViewerWithItems(mediaDisplayAllItems.value, idx);
};

const openViewerWithItems = (items, idx = 0) => {
  const normalizedItems = normalizeViewerMediaItems(items);
  if (!normalizedItems.length) return;
  viewerMediaOverrideItems.value = normalizedItems;
  const max = normalizedItems.length - 1;
  const safeIndex = Math.min(Math.max(idx, 0), max);
  viewerIndex.value = safeIndex;
  viewerOpen.value = true;
};

// ─────────────────────────────────────────────
// Reactions summary (LOCAL, MUTABLE) ✅
// ─────────────────────────────────────────────
const localReactionsSummary = ref(
  (props.post?.reactionsSummary && typeof props.post.reactionsSummary === 'object')
    ? { ...props.post.reactionsSummary }
    : {
        total: Number(props.post?.reactionsCount) || 0,
        like: 0, love: 0, care: 0, haha: 0, wow: 0, sad: 0, angry: 0,
      }
);

watch(
  () => props.post?.reactionsSummary,
  (val) => {
    if (val && typeof val === 'object') {
      localReactionsSummary.value = { ...val };
    } else {
      localReactionsSummary.value = {
        total: Number(props.post?.reactionsCount) || 0,
        like: 0, love: 0, care: 0, haha: 0, wow: 0, sad: 0, angry: 0,
      };
    }
  }
);

watch(
  () => props.post?.reactionsCount,
  (val) => {
    // legacy fallback if summary isn't provided
    if (!props.post?.reactionsSummary || typeof props.post.reactionsSummary !== 'object') {
      const nextTotal = Number(val) || 0;
      localReactionsSummary.value = {
        ...localReactionsSummary.value,
        total: nextTotal,
      };
    }
  }
);

const totalReactions = computed(() => Number(localReactionsSummary.value?.total) || 0);
const hasReactions = computed(() => totalReactions.value > 0);

const activeReactionTypes = computed(() => {
  const s = localReactionsSummary.value || {};
  return REACTION_KEYS
    .map((type, index) => ({
      type,
      count: Number(s[type]) || 0,
      index,
    }))
    .filter((row) => row.count > 0)
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.index - b.index;
    })
    .map((row) => row.type);
});

const summaryTypesToShow = computed(() => activeReactionTypes.value.slice(0, 3));

// comments
const comments = ref([]);
const commentsLoaded = ref(false);
const commentsLoading = ref(false);
const isSubmittingComment = ref(false);

const COMMENTS_PAGE_SIZE = 25;
const commentsOffset = ref(0);
const hasMoreComments = ref(false);

const scrollBodyEl = ref(null);
const composerEl = ref(null);


const localCommentsCount = ref(Number(props.post?.commentsCount) || 0);
const hasComments = computed(() => localCommentsCount.value > 0);

const hasEngagement = computed(() => hasReactions.value || hasComments.value);

const sortedCommentsOldestFirst = computed(() => {
  const list = Array.isArray(comments.value) ? [...comments.value] : [];
  list.sort((a, b) => {
    const ta = new Date(a?.createdAt || a?.created_at || 0).getTime();
    const tb = new Date(b?.createdAt || b?.created_at || 0).getTime();
    return ta - tb;
  });
  return list;
});

async function loadCommentsPage({ append }) {
  const postId = props.post?.id;
  if (!postId) return;

  if (commentsLoading.value) return;
  commentsLoading.value = true;

  try {
    const offset = append ? commentsOffset.value : 0;
    const url = `${apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(postId)}/comments?limit=${COMMENTS_PAGE_SIZE}&offset=${offset}`;

    const resp = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      console.error('[NewsFeedFullPostSlug] loadComments NOT OK:', resp.status, text);
      return;
    }

    const data = await resp.json().catch(() => ({}));
    const list = Array.isArray(data?.comments) ? data.comments : [];

    const normalized = list.map((c) => ({
  ...c,
  // tolerate older field names just in case:
  reactionsSummary: c.reactionsSummary || c.reactions_summary || null,
  reactionsCount: typeof c.reactionsCount === 'number' ? c.reactionsCount : (Number(c.reactions_count) || 0),
  reactionUsersByType: c.reactionUsersByType || c.reaction_users_by_type || c.reactionUsers || null,
  myReactionType: c.myReactionType || c.my_reaction_type || null,
}));


    if (append) comments.value = [...comments.value, ...normalized];
else comments.value = normalized;

    commentsLoaded.value = true;

    await nextTick();
    await maybeAutoExpandReplies();


    // Offset + hasMore (supports either data.hasMore or infer by page size)
    commentsOffset.value = offset + list.length;
    hasMoreComments.value = typeof data?.hasMore === 'boolean'
      ? data.hasMore
      : list.length === COMMENTS_PAGE_SIZE;
  } catch (err) {
    console.error('[NewsFeedFullPostSlug] loadComments error:', err);
  } finally {
    commentsLoading.value = false;
  }
}

const loadMoreComments = () => loadCommentsPage({ append: true });

watch(
  () => [props.isOpen, props.inlineSidebar, props.post?.id],
  ([open, inlineSidebar]) => {
    const active = Boolean(open || inlineSidebar);
    if (active) {
      // fresh open: reset and load
      comments.value = [];
      commentsLoaded.value = false;
      commentsOffset.value = 0;
      hasMoreComments.value = false;
      loadCommentsPage({ append: false });

    } 
  },
  { immediate: true }
);

watch(
  () => props.post?.commentsCount,
  (v) => {
    localCommentsCount.value = Number(v) || 0;
  }
);

// comment box
const isCommentActive = ref(true);
const commentText = ref('');
const commentTextarea = ref(null);
const commentMediaInput = ref(null);
const pendingCommentMedia = ref(null); // { file, type, previewUrl }
const isUploadingCommentMedia = ref(false);
const showCommentEmojiPicker = ref(false);

const clearPendingCommentMedia = () => {
  const pending = pendingCommentMedia.value;
  if (pending?.previewUrl && String(pending.previewUrl).startsWith('blob:')) {
    try {
      URL.revokeObjectURL(pending.previewUrl);
    } catch {
      // no-op
    }
  }
  pendingCommentMedia.value = null;
  if (commentMediaInput.value) {
    commentMediaInput.value.value = '';
  }
};

const triggerCommentMediaPicker = () => {
  showCommentEmojiPicker.value = false;
  isCommentActive.value = true;
  commentMediaInput.value?.click?.();
};

const toggleCommentEmojiPicker = () => {
  isCommentActive.value = true;
  showCommentEmojiPicker.value = !showCommentEmojiPicker.value;
};

const addCommentEmoji = (emoji) => {
  const value = String(emoji?.i || emoji?.emoji || '');
  if (!value) return;
  commentText.value = `${commentText.value || ''}${value}`;
  showCommentEmojiPicker.value = false;
  nextTick(() => {
    commentTextarea.value?.focus?.();
    autoResize({ target: commentTextarea.value });
  });
};

const handleCommentMediaPicked = (event) => {
  const file = event?.target?.files?.[0];
  if (!file) return;

  const mime = String(file.type || '').toLowerCase();
  const isImage = mime.startsWith('image/');
  const isVideo = mime.startsWith('video/');
  if (!isImage && !isVideo) {
    console.warn('[NewsFeedFullPostSlug] Unsupported comment media type:', mime);
    if (commentMediaInput.value) commentMediaInput.value.value = '';
    return;
  }

  clearPendingCommentMedia();
  pendingCommentMedia.value = {
    file,
    type: isVideo ? 'video' : 'image',
    previewUrl: URL.createObjectURL(file),
  };
};

const uploadPendingCommentMedia = async () => {
  const file = pendingCommentMedia.value?.file || null;
  if (!file) return [];
  isUploadingCommentMedia.value = true;
  try {
    const uploaded = await mediaUploadStore.uploadSingleFile(
      file,
      null,
      { origin: 'newsfeedComment' }
    );
    const attachment = normalizeCommentAttachmentPayload(uploaded);
    return attachment ? [attachment] : null;
  } finally {
    isUploadingCommentMedia.value = false;
  }
};

const uploadCommentMediaFile = async (file) => {
  if (!file) return [];
  isUploadingCommentMedia.value = true;
  try {
    const uploaded = await mediaUploadStore.uploadSingleFile(
      file,
      null,
      { origin: 'newsfeedComment' }
    );
    const attachment = normalizeCommentAttachmentPayload(uploaded);
    return attachment ? [attachment] : null;
  } finally {
    isUploadingCommentMedia.value = false;
  }
};

const normalizeVideoReadyAttachments = (raw) => {
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (raw && typeof raw === 'object') return [raw];
  return [];
};

const applyVideoReadyToList = (list, targetCommentId, readyAttachments) => {
  let changed = false;
  const next = (Array.isArray(list) ? list : []).map((entry) => {
    if (Number(entry?.id || 0) !== targetCommentId) return entry;
    changed = true;
    return {
      ...entry,
      mediaProcessing: false,
      media_processing: false,
      attachments: readyAttachments,
      attachment: readyAttachments,
      attachment_json: JSON.stringify(readyAttachments),
    };
  });
  return { changed, next };
};

const onCommentVideoReady = (payload) => {
  const currentPostId = Number(props.post?.id || 0);
  const payloadPostId = Number(payload?.postId || 0);
  if (currentPostId > 0 && payloadPostId > 0 && currentPostId !== payloadPostId) return;

  const targetCommentId = Number(payload?.commentId || 0);
  if (!Number.isFinite(targetCommentId) || targetCommentId <= 0) return;

  const readyAttachments = normalizeVideoReadyAttachments(payload?.attachments ?? payload?.attachment);
  if (!readyAttachments.length) return;

  const commentIdsToRecalc = new Set();
  const replyIdsToRecalc = new Set();
  let changedAny = false;
  {
    const out = applyVideoReadyToList(comments.value, targetCommentId, readyAttachments);
    comments.value = out.next;
    changedAny = changedAny || out.changed;
  }

  const latestReplyEntries = Object.entries(latestReplyByParentId.value || {});
  if (latestReplyEntries.length) {
    const nextLatest = { ...(latestReplyByParentId.value || {}) };
    for (const [parentId, entry] of latestReplyEntries) {
      if (Number(entry?.id || 0) !== targetCommentId) continue;
      nextLatest[parentId] = {
        ...entry,
        mediaProcessing: false,
        media_processing: false,
        attachments: readyAttachments,
        attachment: readyAttachments,
        attachment_json: JSON.stringify(readyAttachments),
      };
      changedAny = true;
      commentIdsToRecalc.add(Number(parentId));
    }
    latestReplyByParentId.value = nextLatest;
  }

  const replyEntries = Object.entries(repliesByParentId.value || {});
  if (replyEntries.length) {
    const nextMap = { ...(repliesByParentId.value || {}) };
    for (const [parentId, rows] of replyEntries) {
      const out = applyVideoReadyToList(rows, targetCommentId, readyAttachments);
      if (out.changed) {
        nextMap[parentId] = out.next;
        changedAny = true;
        commentIdsToRecalc.add(Number(parentId));
      }
    }
    repliesByParentId.value = nextMap;
  }

  const replyReplyEntries = Object.entries(replyRepliesByParentId.value || {});
  if (replyReplyEntries.length) {
    const nextMap = { ...(replyRepliesByParentId.value || {}) };
    for (const [parentId, rows] of replyReplyEntries) {
      const out = applyVideoReadyToList(rows, targetCommentId, readyAttachments);
      if (out.changed) {
        nextMap[parentId] = out.next;
        changedAny = true;
        replyIdsToRecalc.add(Number(parentId));
      }
    }
    replyRepliesByParentId.value = nextMap;
  }

  if (changedAny) {
    const parentCommentId = Number(payload?.parentCommentId || 0);
    if (parentCommentId > 0) commentIdsToRecalc.add(parentCommentId);

    void nextTick(() => {
      commentIdsToRecalc.forEach((id) => {
        if (Number.isFinite(id) && id > 0) scheduleRecalcThreadLine(id);
      });
      replyIdsToRecalc.forEach((id) => {
        if (Number.isFinite(id) && id > 0) scheduleRecalcReplyThreadLine(id);
      });
    });
  }

  if (changedAny && payload?.selfReady && typeof notify === 'function') {
    notify({
      title: 'Video ready',
      body: 'Your video is now ready to be viewed.',
      variant: 'success',
    });
  }
};

let unsubscribeCommentVideoReady = null;
let unsubscribeCommentCreated = null;
let unsubscribeCommentUpdated = null;
let unsubscribeCommentDeleted = null;
let unsubscribeCommentReactionUpdated = null;
let unsubscribePostReactionUpdated = null;
let unsubscribePostReactionToast = null;
const seenWsCommentReactionKeys = new Set();
const seenWsCommentIds = new Set();
const seenWsCommentMutationKeys = new Set();
const seenWsPostReactionKeys = new Set();
let postReactionSyncInFlight = false;
let postReactionSyncQueued = false;

const onCommentCreated = async (payload) => {
  const currentPostId = Number(props.post?.id || 0);
  const payloadPostId = Number(payload?.postId || 0);
  if (currentPostId > 0 && payloadPostId > 0 && currentPostId !== payloadPostId) return;

  const incomingCommentId = Number(payload?.commentId || 0);
  if (!Number.isFinite(incomingCommentId) || incomingCommentId <= 0) return;
  const incomingKey = String(incomingCommentId);
  if (seenWsCommentIds.has(incomingKey)) return;
  seenWsCommentIds.add(incomingKey);

  const parentCommentId = Number(payload?.parentCommentId || 0);
  const topLevelExists = (comments.value || []).some((c) => Number(c?.id || 0) === incomingCommentId);
  if (topLevelExists) return;

  localCommentsCount.value = (Number(localCommentsCount.value) || 0) + 1;

  if (parentCommentId > 0) {
    comments.value = (comments.value || []).map((c) => {
      if (Number(c?.id || 0) !== parentCommentId) return c;
      const currentReplyCount = Number(c?.replyCount ?? c?.repliesCount ?? c?.replies_count ?? 0) || 0;
      const nextReplyCount = currentReplyCount + 1;
      return {
        ...c,
        replyCount: nextReplyCount,
        repliesCount: typeof c?.repliesCount !== 'undefined' ? nextReplyCount : c?.repliesCount,
        replies_count: typeof c?.replies_count !== 'undefined' ? nextReplyCount : c?.replies_count,
      };
    });

    const shouldHydrateReplies =
      isRepliesExpanded(parentCommentId) ||
      autoExpandReplies.value ||
      Array.isArray(repliesByParentId.value?.[parentCommentId]);

    if (shouldHydrateReplies) {
      repliesByParentId.value = {
        ...(repliesByParentId.value || {}),
        [parentCommentId]: [],
      };
      await fetchRepliesForComment(parentCommentId);
    }

    scheduleRecalcThreadLine(parentCommentId);
    return;
  }

  if (commentsLoaded.value) {
    await loadCommentsPage({ append: false });
  }
};

const onCommentUpdated = async (payload) => {
  const currentPostId = Number(props.post?.id || 0);
  const payloadPostId = Number(payload?.postId || 0);
  if (currentPostId > 0 && payloadPostId > 0 && currentPostId !== payloadPostId) return;

  const commentId = Number(payload?.commentId || payload?.comment?.id || 0);
  if (!Number.isFinite(commentId) || commentId <= 0) return;
  const mutationKey = `upd:${commentId}:${String(payload?.updatedAt || payload?.comment?.updatedAt || '')}`;
  if (seenWsCommentMutationKeys.has(mutationKey)) return;
  seenWsCommentMutationKeys.add(mutationKey);

  if (!commentsLoaded.value) return;
  await loadCommentsPage({ append: false });

  const parentCommentId = Number(payload?.parentCommentId || payload?.comment?.parentCommentId || 0);
  if (parentCommentId > 0) {
    const shouldHydrateReplies =
      isRepliesExpanded(parentCommentId) ||
      autoExpandReplies.value ||
      Array.isArray(repliesByParentId.value?.[parentCommentId]);
    if (shouldHydrateReplies) {
      repliesByParentId.value = {
        ...(repliesByParentId.value || {}),
        [parentCommentId]: [],
      };
      await fetchRepliesForComment(parentCommentId);
    }
    scheduleRecalcThreadLine(parentCommentId);
  }
};

const onCommentDeleted = async (payload) => {
  const currentPostId = Number(props.post?.id || 0);
  const payloadPostId = Number(payload?.postId || 0);
  if (currentPostId > 0 && payloadPostId > 0 && currentPostId !== payloadPostId) return;

  const commentId = Number(payload?.commentId || 0);
  if (!Number.isFinite(commentId) || commentId <= 0) return;
  const mutationKey = `del:${commentId}:${String(payload?.deletedAt || '')}:${Number(payload?.deletedCount || 0)}`;
  if (seenWsCommentMutationKeys.has(mutationKey)) return;
  seenWsCommentMutationKeys.add(mutationKey);

  const deletedCount = Math.max(1, Number(payload?.deletedCount || 0));
  localCommentsCount.value = Math.max(0, Number(localCommentsCount.value || 0) - deletedCount);

  if (!commentsLoaded.value) return;
  await loadCommentsPage({ append: false });

  const parentCommentId = Number(payload?.parentCommentId || 0);
  if (parentCommentId > 0) {
    const shouldHydrateReplies =
      isRepliesExpanded(parentCommentId) ||
      autoExpandReplies.value ||
      Array.isArray(repliesByParentId.value?.[parentCommentId]);
    if (shouldHydrateReplies) {
      repliesByParentId.value = {
        ...(repliesByParentId.value || {}),
        [parentCommentId]: [],
      };
      await fetchRepliesForComment(parentCommentId);
    }
    scheduleRecalcThreadLine(parentCommentId);
  }
};

const normalizeReactionNameMap = (source) => {
  const out = {};
  for (const type of REACTION_KEYS) {
    const arr = Array.isArray(source?.[type]) ? source[type] : [];
    out[type] = arr
      .map((entry) => normalizeReactionUserName(entry))
      .filter((name) => name.length > 0);
  }
  return out;
};

const applyLivePostReactionSnapshot = (rawPost) => {
  const summary = rawPost?.reactionsSummary || rawPost?.reactions || {};
  localReactionsSummary.value = {
    total: Number(summary?.total) || 0,
    like: Number(summary?.like) || 0,
    love: Number(summary?.love) || 0,
    care: Number(summary?.care) || 0,
    haha: Number(summary?.haha) || 0,
    wow: Number(summary?.wow) || 0,
    sad: Number(summary?.sad) || 0,
    angry: Number(summary?.angry) || 0,
  };
  localReactionsCount.value = Number(localReactionsSummary.value.total) || 0;

  const nextSource =
    (rawPost?.reactionNamesByType && typeof rawPost.reactionNamesByType === 'object')
      ? rawPost.reactionNamesByType
      : rawPost?.reactionUsersByType;
  if (nextSource && typeof nextSource === 'object') {
    localReactionNamesByType.value = normalizeReactionNameMap(nextSource);
  }
};

const syncPostReactionSnapshot = async () => {
  if (postReactionSyncInFlight) {
    postReactionSyncQueued = true;
    return;
  }
  postReactionSyncInFlight = true;

  try {
    const safeSlug = String(props.post?.slug || '').trim();
    if (!safeSlug) return;

    const resp = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/posts/by-slug/${encodeURIComponent(safeSlug)}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }
    );
    if (!resp.ok) return;

    const data = await resp.json().catch(() => ({}));
    const rawPost = data?.post || null;
    if (!rawPost) return;
    applyLivePostReactionSnapshot(rawPost);
  } catch (err) {
    console.error('[NewsFeedFullPostSlug] syncPostReactionSnapshot error:', err);
  } finally {
    postReactionSyncInFlight = false;
    if (postReactionSyncQueued) {
      postReactionSyncQueued = false;
      void syncPostReactionSnapshot();
    }
  }
};

const onPostReactionUpdated = (payload) => {
  const currentPostId = Number(props.post?.id || 0);
  const payloadPostId = Number(payload?.postId || 0);
  if (currentPostId > 0 && payloadPostId > 0 && currentPostId !== payloadPostId) return;

  const dedupeKey = `${String(payloadPostId || '')}:${String(payload?.createdAt || '')}`;
  if (dedupeKey !== ':' && seenWsPostReactionKeys.has(dedupeKey)) return;
  if (dedupeKey !== ':') seenWsPostReactionKeys.add(dedupeKey);

  void syncPostReactionSnapshot();
};

// const resetCommentTextareaHeight = async () => {
//   await nextTick();
//   const el = commentTextarea.value;
//   if (!el) return;
//   el.style.height = '';
//   if (!isCommentActive.value) el.style.height = '36px';
// };

const handleCommentBlur = () => {
    showCommentEmojiPicker.value = false;
};


const autoResize = (event) => {
  const el = event?.target || commentTextarea.value;
  if (!el) return;

  el.style.height = 'auto';

  const max = 480; // must match textarea max-height
  const next = Math.min(el.scrollHeight, max);

  el.style.height = next + 'px';
  el.style.overflowY = el.scrollHeight > max ? 'auto' : 'hidden';

  syncScrollPaddingForComposer();
};


const focusCommentBox = async () => {
  isCommentActive.value = true;
  await nextTick();
  commentTextarea.value?.focus?.();
  autoResize({ target: commentTextarea.value });
};

async function submitComment() {
  const postId = props.post?.id;
  if (!postId) return;

  const body = String(commentText.value || '').trim();
  const hasMedia = !!pendingCommentMedia.value?.file;
  if (!body && !hasMedia) return;

  if (isSubmittingComment.value) return;
  isSubmittingComment.value = true;

  try {
    const pendingMediaSnapshot = pendingCommentMedia.value
      ? {
          file: pendingCommentMedia.value.file,
          type: pendingCommentMedia.value.type,
        }
      : null;
    const shouldOptimisticVideo = !!(pendingMediaSnapshot?.file && pendingMediaSnapshot?.type === 'video');
    let optimisticTempId = null;

    if (shouldOptimisticVideo) {
      optimisticTempId = `temp-video-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const optimisticComment = {
        id: optimisticTempId,
        postId,
        parentCommentId: null,
        userId: props.post?.currentUserId || null,
        userType: props.post?.currentUserType || 'creator',
        body,
        attachments: [],
        mediaProcessing: true,
        createdAt: new Date().toISOString(),
        authorName: postingAsNameComputed.value || 'You',
        authorAvatarUrl: commenterAvatarSrc.value || defaultAuthorAvatar,
      };

      comments.value = [...(comments.value || []), optimisticComment];
      localCommentsCount.value = (Number(localCommentsCount.value) || 0) + 1;
      commentText.value = '';
      showCommentEmojiPicker.value = false;
      clearPendingCommentMedia();
      await nextTick();
      autoResize({ target: commentTextarea.value });

      void (async () => {
        try {
          const uploaded = await uploadCommentMediaFile(pendingMediaSnapshot.file);
          if (uploaded === null) throw new Error('upload_failed');
          const commentAttachments = uploaded;

          const targetUrl = `${apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(postId)}/comments`;
          const payload = { body };
          if (commentAttachments.length) {
            payload.attachments = commentAttachments;
            payload.attachment = commentAttachments;
            payload.attachment_json = JSON.stringify(commentAttachments);
          }

          let resp = await fetch(targetUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(payload),
          });

          if (!resp.ok && commentAttachments.length) {
            resp = await fetch(targetUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ body }),
            });
          }
          if (!resp.ok) throw new Error('comment_create_failed');

          const data = await resp.json().catch(() => ({}));
          const newComment = data?.comment || null;
          if (!newComment) throw new Error('comment_missing');

          const normalizedComment = {
            ...newComment,
            authorName: newComment.authorName || newComment.author_name || newComment.name || 'User',
            authorAvatarUrl: newComment.authorAvatarUrl || newComment.author_avatar_url || newComment.avatarUrl || defaultAuthorAvatar,
          };
          if (!Array.isArray(normalizedComment.attachments) && commentAttachments.length) {
            normalizedComment.attachments = commentAttachments;
          }

          comments.value = (comments.value || []).map((c) =>
            String(c?.id) === String(optimisticTempId) ? normalizedComment : c
          );
          emit('comment-created', { postId, comment: normalizedComment });
        } catch (error) {
          comments.value = (comments.value || []).filter((c) => String(c?.id) !== String(optimisticTempId));
          localCommentsCount.value = Math.max(0, (Number(localCommentsCount.value) || 0) - 1);
          console.error('[NewsFeedFullPostSlug] optimistic video comment failed:', error);
        }
      })();

      return;
    }

    let commentAttachments = [];
    if (hasMedia) {
      const uploaded = await uploadPendingCommentMedia();
      if (uploaded === null) {
        console.error('[NewsFeedFullPostSlug] uploadPendingCommentMedia failed');
        return;
      }
      commentAttachments = uploaded;
    }

    const targetUrl = `${apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(postId)}/comments`;
    const payload = { body };
    if (commentAttachments.length) {
      payload.attachments = commentAttachments;
      payload.attachment = commentAttachments;
      payload.attachment_json = JSON.stringify(commentAttachments);
    }

    let resp = await fetch(targetUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!resp.ok && commentAttachments.length) {
      // Backward-compatible fallback for backends that only accept { body }.
      resp = await fetch(targetUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ body }),
      });
    }

    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      console.error('[NewsFeedFullPostSlug] submitComment NOT OK:', resp.status, text);
      return;
    }

    const data = await resp.json().catch(() => ({}));
    const newComment = data?.comment || null;

    if (newComment) {
      const normalizedComment = {
        ...newComment,
        authorName: newComment.authorName || newComment.author_name || newComment.name || 'User',
        authorAvatarUrl: newComment.authorAvatarUrl || newComment.author_avatar_url || newComment.avatarUrl || defaultAuthorAvatar,
      };
      if (commentAttachments.length && !Array.isArray(normalizedComment.attachments)) {
        normalizedComment.attachments = commentAttachments;
      }

      comments.value = [...(comments.value || []), normalizedComment];
      localCommentsCount.value = (Number(localCommentsCount.value) || 0) + 1;

      emit('comment-created', { postId, comment: normalizedComment });
    } else {
      // fallback refresh
      await loadCommentsPage({ append: false });
      localCommentsCount.value = Math.max(
        Number(localCommentsCount.value) || 0,
        Array.isArray(comments.value) ? comments.value.length : 0
      );
    }

    commentText.value = '';
    showCommentEmojiPicker.value = false;
    clearPendingCommentMedia();
    await nextTick();
    autoResize({ target: commentTextarea.value });
  } catch (err) {
    console.error('[NewsFeedFullPostSlug] submitComment error:', err);
  } finally {
    isSubmittingComment.value = false;
  }
}

function formatCommentTime(src) {
  if (!src) return '';
  const d = new Date(src);
  if (Number.isNaN(d.getTime())) return '';

  const now = new Date();
  let diffMs = now.getTime() - d.getTime();
  if (diffMs < 0) diffMs = 0;

  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'Just now';

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min`;

  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr`;

  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay} d`;

  const diffWk = Math.floor(diffDay / 7);
  if (diffWk < 4) return `${diffWk} w`;

  const diffMo = Math.floor(diffDay / 30);
  if (diffMo < 12) return `${diffMo} mo`;

  const diffYr = Math.floor(diffDay / 365);
  return `${diffYr} y`;
}

function formatCommentFullTooltip(src) {
  if (!src) return '';
  const d = new Date(src);
  if (Number.isNaN(d.getTime())) return '';
  return formatFullDate(d); // you already have formatFullDate() in this file
}


const syncScrollPaddingForComposer = async () => {
  await nextTick();
  const scrollEl = scrollBodyEl.value;
  const compEl = composerEl.value;
  if (!scrollEl || !compEl) return;

  // Pad the scrollable area so last comment isn't hidden behind the composer
  scrollEl.style.paddingBottom = compEl.offsetHeight + 'px';
};

// ─────────────────────────────────────────────
// Local comment reactions (mirrors NewsfeedCreatorPost)
// ─────────────────────────────────────────────
const openCommentReactionForId = ref(null);
const hoveredCommentPickerId = ref(null);


// hover open/close timers (same feel as post)
let commentReactionOpenTimer = null;
let commentReactionCloseTimer = null;

const handleCommentReactionMouseEnter = (commentId) => {
  if (commentReactionCloseTimer) {
    clearTimeout(commentReactionCloseTimer);
    commentReactionCloseTimer = null;
  }

  if (openCommentReactionForId.value === commentId) return;

  if (commentReactionOpenTimer) clearTimeout(commentReactionOpenTimer);
  commentReactionOpenTimer = setTimeout(() => {
    openCommentReactionForId.value = commentId;
    commentReactionOpenTimer = null;
  }, 200);
};

const handleCommentReactionMouseLeave = (commentId) => {
  if (commentReactionOpenTimer) {
    clearTimeout(commentReactionOpenTimer);
    commentReactionOpenTimer = null;
  }

  if (openCommentReactionForId.value !== commentId) return;

  if (commentReactionCloseTimer) clearTimeout(commentReactionCloseTimer);
  commentReactionCloseTimer = setTimeout(() => {
    if (props.inlineSidebar && Number(hoveredCommentPickerId.value) === Number(commentId)) return;
    if (openCommentReactionForId.value === commentId) {
      openCommentReactionForId.value = null;
    }
    commentReactionCloseTimer = null;
  }, 250);
};

const handleCommentPickerHoverChange = (commentId, hovering) => {
  hoveredCommentPickerId.value = hovering ? Number(commentId) : null;
  if (hovering) {
    handleCommentReactionMouseEnter(commentId);
  } else {
    handleCommentReactionMouseLeave(commentId);
  }
};

// click-away close (same as post)
const handleGlobalClickToCloseCommentPicker = () => {
  if (openCommentReactionForId.value !== null) {
    openCommentReactionForId.value = null;
  }
};

onMounted(() => {
  window.addEventListener('click', handleGlobalClickToCloseCommentPicker);
});

onBeforeUnmount(() => {
  window.removeEventListener('click', handleGlobalClickToCloseCommentPicker);

  if (commentReactionOpenTimer) clearTimeout(commentReactionOpenTimer);
  if (commentReactionCloseTimer) clearTimeout(commentReactionCloseTimer);
});



// ─────────────────────────────────────────────
// Comment reactions (SERVER-BACKED, MUTABLE)
// Comments returned from backend already include:
//   reactionsCount, reactionsSummary, reactionUsersByType, myReactionType
// ─────────────────────────────────────────────
const findCommentIndexById = (commentId) => {
  const list = Array.isArray(comments.value) ? comments.value : [];
  return list.findIndex((c) => Number(c?.id) === Number(commentId));
};

const getCommentById = (commentId) => {
  const idx = findCommentIndexById(commentId);
  if (idx < 0) return null;
  return comments.value[idx] || null;
};

// helper getters used by template
const getCommentReactionType = (commentId) => {
  const c = getCommentById(commentId);
  return resolveReactionTypeFromEntity(c);
};

const getCommentReactionCount = (commentId) => {
  const c = getCommentById(commentId);
  const total = Number(c?.reactionsSummary?.total);
  if (Number.isFinite(total)) return total;
  return Number(c?.reactionsCount) || 0;
};

const getCommentReactionRows = (commentId) => {
  const c = getCommentById(commentId);
  return getSortedReactionRowsFromEntity(c);
};

const getCommentTopReactionTypes = (commentId) =>
  getCommentReactionRows(commentId).slice(0, 2).map((row) => row.type);

const getCommentReactionTooltipRows = (commentId) =>
  getCommentReactionRows(commentId);

const MAX_COMMENT_TOOLTIP_NAMES = 20;

const getCommentReactionTooltipNames = (commentId) => {
  const rows = getCommentReactionRows(commentId);
  if (rows.length !== 1) return [];
  const c = getCommentById(commentId);
  const reactionType = rows[0].type;

  const usersByType = c?.reactionUsersByType || c?.reaction_users_by_type || {};
  const arr = usersByType?.[reactionType];
  const users = Array.isArray(arr) ? arr : [];

  const names = users
    .map((u) => normalizeReactionUserName(u))
    .filter((n) => n.length);

  return names.slice(0, MAX_COMMENT_TOOLTIP_NAMES);
};

const getCommentReactionTooltipText = (commentId) => {
  const total = getCommentReactionCount(commentId);
  if (total <= 0) return '';
  if (getCommentReactionRows(commentId).length > 1) return '';

  const names = getCommentReactionTooltipNames(commentId);

  if (!names.length) return total === 1 ? '1 person' : `${total} people`;
  if (total <= names.length) return names.join(', ');

  const extra = total - names.length;
  return `${names.join(', ')} and ${extra} more`;
};

const getCommentReactionTooltipExtraCount = (commentId) => {
  const total = getCommentReactionCount(commentId);
  const shown = getCommentReactionTooltipNames(commentId).length;
  const extra = total - shown;
  return extra > 0 ? extra : 0;
};

const isSubmittingCommentReaction = ref(false);

const patchCommentFromReactionResponse = (commentId, data) => {
  // Support either response shape:
  // (A) { commentId, reactionType, reactions, reactionUsersByType, myReactionType }
  // (B) { success:true, commentId, ... } etc.
  const idx = findCommentIndexById(commentId);
  if (idx < 0) return;

  const existing = comments.value[idx] || {};

  // Prefer "reactions" summary from backend, else keep existing
  const nextSummary = data?.reactions && typeof data.reactions === 'object'
    ? {
        total: data.reactions.total || 0,
        like: data.reactions.like || 0,
        love: data.reactions.love || 0,
        care: data.reactions.care || 0,
        haha: data.reactions.haha || 0,
        wow: data.reactions.wow || 0,
        sad: data.reactions.sad || 0,
        angry: data.reactions.angry || 0,
      }
    : (existing.reactionsSummary || existing.reactions_summary || null);

  const nextMyReactionType =
    typeof data?.myReactionType !== 'undefined'
      ? data.myReactionType
      : existing.myReactionType;

  const nextUsersByType =
  (data?.reactionUsersByType && typeof data.reactionUsersByType === 'object')
    ? data.reactionUsersByType
    : (existing.reactionUsersByType || {
        like: [], love: [], care: [], haha: [], wow: [], sad: [], angry: [],
      });


  const next = {
    ...existing,

    // ✅ summary
    reactionsSummary: nextSummary || existing.reactionsSummary || {
      total: Number(existing.reactionsCount) || 0,
      like: 0, love: 0, care: 0, haha: 0, wow: 0, sad: 0, angry: 0,
    },

    // ✅ back-compat total
    reactionsCount:
      (nextSummary && typeof nextSummary.total === 'number')
        ? nextSummary.total
        : (Number(existing.reactionsCount) || 0),

    // ✅ tooltip names
    reactionUsersByType: nextUsersByType || existing.reactionUsersByType || {
      like: [], love: [], care: [], haha: [], wow: [], sad: [], angry: [],
    },

    // ✅ selected state
    myReactionType: nextMyReactionType || null,
  };

  // Vue-friendly replace
  const list = Array.isArray(comments.value) ? [...comments.value] : [];
  list[idx] = next;
  comments.value = list;
};


const handleCommentReactionSelect = async (commentId, type) => {
  console.log('[NewsFeedFullPostSlug] comment reaction selected:', type, 'for comment', commentId);

  // close picker immediately
  openCommentReactionForId.value = null;

  if (!REACTION_META[type]) {
    console.warn('[NewsFeedFullPostSlug] unknown comment reaction type:', type);
    return;
  }

  if (isSubmittingCommentReaction.value) return;
  isSubmittingCommentReaction.value = true;

  let prevType = null;
  let optimisticApply = () => {};

  try {
    prevType = getCommentReactionType(commentId);

    // ✅ 2C) optimistic local update so tooltip shows name immediately
    const me = String(postingAsNameComputed.value || 'You');

    optimisticApply = (newType) => {
      const idx = findCommentIndexById(commentId);
      if (idx < 0) return;

      const list = Array.isArray(comments.value) ? [...comments.value] : [];
      const c = list[idx] || {};

      const usersByType = (c.reactionUsersByType && typeof c.reactionUsersByType === 'object')
        ? JSON.parse(JSON.stringify(c.reactionUsersByType))
        : { like: [], love: [], care: [], haha: [], wow: [], sad: [], angry: [] };

      // remove me from all types
      for (const k of REACTION_KEYS) {
        const arr = Array.isArray(usersByType[k]) ? usersByType[k] : [];
        usersByType[k] = arr.filter((u) => String(u?.name || u) !== me);
      }

      // update summary counts optimistically
      const summary = (c.reactionsSummary && typeof c.reactionsSummary === 'object')
        ? { ...c.reactionsSummary }
        : {
            total: Number(c.reactionsCount) || 0,
            like: 0, love: 0, care: 0, haha: 0, wow: 0, sad: 0, angry: 0,
          };

      // remove remembered previous type count (if any)
      if (prevType && summary[prevType] > 0) summary[prevType] = Number(summary[prevType] || 0) - 1;

      if (newType) {
        summary[newType] = Number(summary[newType] || 0) + 1;

        // ensure total is correct (simple rule: if we had no prev, +1; if switching types, total stays)
        if (!prevType) summary.total = Number(summary.total || 0) + 1;
      } else {
        // removing reaction
        if (prevType) summary.total = Math.max(0, Number(summary.total || 0) - 1);
      }

      // add me to selected type list (shape: [{name}] or string; your getter supports both)
      if (newType) {
        const arr = Array.isArray(usersByType[newType]) ? usersByType[newType] : [];
        usersByType[newType] = [{ name: me }, ...arr];
      }

      list[idx] = {
        ...c,
        myReactionType: newType || null,
        reactionsSummary: summary,
        reactionsCount: Number(summary.total) || 0,
        reactionUsersByType: usersByType,
      };

      comments.value = list;
    };

    // Toggle off if selecting same type
    if (prevType && prevType === type) {
      // ✅ optimistic apply (remove)
      optimisticApply(null);

      const resp = await fetch(
        `${apiProxyUrl}/api/creator-newsfeed/comments/${encodeURIComponent(commentId)}/react`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        console.error('[NewsFeedFullPostSlug] DELETE comment react NOT OK:', resp.status, text);
        optimisticApply(prevType || null);
        return;
      }

      // ✅ 2D) safe empty-body parsing (handles 204 / empty JSON)
      let data = {};
      try {
        const text = await resp.text();
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        data = {};
      }
      console.log('[NewsFeedFullPostSlug] DELETE comment react response:', data);

      patchCommentFromReactionResponse(commentId, {
        ...data,
        myReactionType: null,
        reactionType: null,
      });

      return;
    }

    // Otherwise POST (create/update)
    // ✅ optimistic apply (set)
    optimisticApply(type);

    const resp = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/comments/${encodeURIComponent(commentId)}/react`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reactionType: type }),
      }
    );

    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      console.error('[NewsFeedFullPostSlug] POST comment react NOT OK:', resp.status, text);
      optimisticApply(prevType || null);
      return;
    }

    // ✅ 2D) safe empty-body parsing (handles 204 / empty JSON)
    let data = {};
    try {
      const text = await resp.text();
      data = text ? JSON.parse(text) : {};
    } catch (e) {
      data = {};
    }
    console.log('[NewsFeedFullPostSlug] POST comment react response:', data);

    patchCommentFromReactionResponse(commentId, {
      ...data,
      myReactionType: type,
      reactionType: type,
    });
  } catch (err) {
    console.error('[NewsFeedFullPostSlug] handleCommentReactionSelect error:', err);
    optimisticApply(prevType || null);
  } finally {
    isSubmittingCommentReaction.value = false;
  }
};







/* ─────────────────────────────────────────────
 * Post reactions (wired like NewsfeedCreatorPost)
 * ───────────────────────────────────────────*/

// local total + per-type counts
const localReactionsCount = ref(
  typeof props.post?.reactionsSummary?.total === 'number'
    ? props.post.reactionsSummary.total
    : (typeof props.post?.reactionsCount === 'number' ? props.post.reactionsCount : 0)
);


// tooltip names (if backend provides them)
const localReactionNamesByType = ref(
  (props.post?.reactionNamesByType && typeof props.post.reactionNamesByType === 'object')
    ? JSON.parse(JSON.stringify(props.post.reactionNamesByType))
    : (props.post?.reactionUsersByType && typeof props.post.reactionUsersByType === 'object')
      ? JSON.parse(JSON.stringify(props.post.reactionUsersByType))
    : { like: [], love: [], care: [], haha: [], wow: [], sad: [], angry: [] }
);

const currentUserDisplayName = computed(() => {
  return props.post?.currentUserName || props.postingAsName || 'You';
});

/**
 * Current user's reaction type on this post:
 * 1) prefer explicit backend field
 * 2) else derive from reactionNamesByType
 */
const currentUserReactionType = computed(() => {
  if (props.post?.currentUserReactionType) return props.post.currentUserReactionType;

  const me = currentUserDisplayName.value;
  if (!me) return null;

  const meLower = String(me).toLowerCase();
  const source = localReactionNamesByType.value || {};

  for (const t of REACTION_KEYS) {
    const arr = Array.isArray(source[t]) ? source[t] : [];
    if (arr.some((n) => normalizeReactionUserName(n).toLowerCase() === meLower)) return t;
  }

  return null;
});

// highlight like button if ANY reaction
const hasUserLiked = computed(() => !!currentUserReactionType.value);


/* ─────────────────────────────
 * Reaction picker open/close
 * ─────────────────────────────*/
const isReactionDockOpen = ref(false);
const isPostPickerHovered = ref(false);

let reactionOpenTimer = null;
let reactionCloseTimer = null;

const handleReactionMouseEnter = () => {
  if (reactionCloseTimer) {
    clearTimeout(reactionCloseTimer);
    reactionCloseTimer = null;
  }

  if (isReactionDockOpen.value) return;

  if (reactionOpenTimer) clearTimeout(reactionOpenTimer);
  reactionOpenTimer = setTimeout(() => {
    isReactionDockOpen.value = true;
    reactionOpenTimer = null;
  }, 200);
};

const handleReactionMouseLeave = () => {
  if (reactionOpenTimer) {
    clearTimeout(reactionOpenTimer);
    reactionOpenTimer = null;
  }

  if (!isReactionDockOpen.value) return;

  if (reactionCloseTimer) clearTimeout(reactionCloseTimer);
  reactionCloseTimer = setTimeout(() => {
    if (props.inlineSidebar && isPostPickerHovered.value) return;
    isReactionDockOpen.value = false;
    reactionCloseTimer = null;
  }, 250);
};

const handlePostPickerHoverChange = (hovering) => {
  isPostPickerHovered.value = !!hovering;
  if (hovering) {
    handleReactionMouseEnter();
  } else {
    handleReactionMouseLeave();
  }
};

const isSubmittingReaction = ref(false);

const handleReactionSelect = async (type) => {
  console.log('[NewsFeedFullPostSlug] reaction selected:', type, 'for post', props.post?.id);

  // close dock immediately
  isReactionDockOpen.value = false;

  if (!REACTION_META[type]) {
    console.warn('[NewsFeedFullPostSlug] unknown reaction type:', type);
    return;
  }

  if (isSubmittingReaction.value) return;
  isSubmittingReaction.value = true;

  try {
    const postId = props.post?.id;
    if (!postId) return;

    const resp = await fetch(`${apiProxyUrl}/api/creator-newsfeed/posts/${postId}/react`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ reactionType: type }),
    });

    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      console.error('[NewsFeedFullPostSlug] reaction API error:', resp.status, text);
      return;
    }

    let data = {};
try {
  const text = await resp.text();
  data = text ? JSON.parse(text) : {};
} catch (e) {
  data = {};
}
console.log('[NewsFeedFullPostSlug] POST comment react response:', data);
    console.log('[NewsFeedFullPostSlug] reaction API response:', data);

    // update counts from backend response
    if (data?.reactions && typeof data.reactions.total === 'number') {
  localReactionsCount.value = data.reactions.total;

  localReactionsSummary.value = {
    total: data.reactions.total || 0,
    like: data.reactions.like || 0,
    love: data.reactions.love || 0,
    care: data.reactions.care || 0,
    haha: data.reactions.haha || 0,
    wow: data.reactions.wow || 0,
    sad: data.reactions.sad || 0,
    angry: data.reactions.angry || 0,
  };
}


    // update tooltip names locally (instant UI response)
    const me = currentUserDisplayName.value;
    if (me) {
      const next = { ...(localReactionNamesByType.value || {}) };

      // remove me from all
      for (const k of REACTION_KEYS) {
        const arr = Array.isArray(next[k]) ? next[k] : [];
        next[k] = arr.filter((n) => String(n) !== String(me));
      }

      // add me to selected type
      const selectedArr = Array.isArray(next[type]) ? next[type] : [];
      next[type] = [me, ...selectedArr];

      localReactionNamesByType.value = next;
    }

    emit('react', {
      postId,
      reactionType: type,
      summary: data?.reactions || null,
    });
  } catch (err) {
    console.error('[NewsFeedFullPostSlug] error sending reaction:', err);
  } finally {
    isSubmittingReaction.value = false;
  }
};

const handleLikeButtonClick = async () => {
  console.log('[NewsFeedFullPostSlug] Like button clicked for post', props.post?.id);

  if (isSubmittingReaction.value) return;

  // no existing reaction → Like
  if (!currentUserReactionType.value) {
    return handleReactionSelect('like');
  }

  // already reacted → toggle off
  isSubmittingReaction.value = true;

  try {
    const postId = props.post?.id;
    if (!postId) return;

    const resp = await fetch(`${apiProxyUrl}/api/creator-newsfeed/posts/${postId}/react`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      console.error('[NewsFeedFullPostSlug] unlike API error:', resp.status, text);
      return;
    }

    const data = await resp.json().catch(() => ({}));
    console.log('[NewsFeedFullPostSlug] unlike API response:', data);

    if (data?.reactions && typeof data.reactions.total === 'number') {
  localReactionsCount.value = data.reactions.total;

  localReactionsSummary.value = {
    total: data.reactions.total || 0,
    like: data.reactions.like || 0,
    love: data.reactions.love || 0,
    care: data.reactions.care || 0,
    haha: data.reactions.haha || 0,
    wow: data.reactions.wow || 0,
    sad: data.reactions.sad || 0,
    angry: data.reactions.angry || 0,
  };
}


    // remove me from tooltip names
    const me = currentUserDisplayName.value;
    if (me) {
      const next = { ...(localReactionNamesByType.value || {}) };
      for (const k of REACTION_KEYS) {
        const arr = Array.isArray(next[k]) ? next[k] : [];
        next[k] = arr.filter((n) => String(n) !== String(me));
      }
      localReactionNamesByType.value = next;
    }

    emit('react', {
      postId,
      reactionType: null,
      summary: data?.reactions || null,
      removed: true,
    });
  } catch (err) {
    console.error('[NewsFeedFullPostSlug] error removing reaction:', err);
  } finally {
    isSubmittingReaction.value = false;
  }
};

const reactionTooltipPosition = ref({}); // { like: 'above', ... }
const commentReactionTooltipPosition = ref({});
const replyReactionTooltipPosition = ref({});
const replyReplyReactionTooltipPosition = ref({});

const handleReactionTooltipEnter = (type, event) => {
  if (typeof window === 'undefined' || !event?.currentTarget) return;

  const rect = event.currentTarget.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  const estimatedTooltipHeight = 380;

  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;

  const showAbove = spaceBelow < estimatedTooltipHeight && spaceAbove > spaceBelow;

  reactionTooltipPosition.value = {
    ...reactionTooltipPosition.value,
    [type]: showAbove ? 'above' : 'below',
  };
};

const resolveTooltipPlacement = (event, estimatedTooltipHeight = 380) => {
  if (typeof window === 'undefined' || !event?.currentTarget) return 'below';
  const rect = event.currentTarget.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;
  const showAbove = spaceBelow < estimatedTooltipHeight && spaceAbove > spaceBelow;
  return showAbove ? 'above' : 'below';
};

const handleCommentReactionTooltipEnter = (commentId, event) => {
  if (!commentId) return;
  commentReactionTooltipPosition.value = {
    ...commentReactionTooltipPosition.value,
    [String(commentId)]: resolveTooltipPlacement(event),
  };
};

const handleReplyReactionTooltipEnter = (replyId, event) => {
  if (!replyId) return;
  replyReactionTooltipPosition.value = {
    ...replyReactionTooltipPosition.value,
    [String(replyId)]: resolveTooltipPlacement(event),
  };
};

const handleReplyReplyReactionTooltipEnter = (replyReplyId, event) => {
  if (!replyReplyId) return;
  replyReplyReactionTooltipPosition.value = {
    ...replyReplyReactionTooltipPosition.value,
    [String(replyReplyId)]: resolveTooltipPlacement(event),
  };
};

const MAX_TOOLTIP_NAMES = 20;

const tooltipNamesForType = (type) => {
  const rawNames = Array.isArray(localReactionNamesByType.value?.[type])
    ? localReactionNamesByType.value[type]
    : [];

  return rawNames
    .map((n) => normalizeReactionUserName(n))
    .filter((n) => n.length > 0)
    .slice(0, MAX_TOOLTIP_NAMES);
};

const tooltipExtraCountForType = (type) => {
  const total = Number(localReactionsSummary.value?.[type]) || 0;
  const shown = tooltipNamesForType(type).length;
  const extra = total - shown;
  return extra > 0 ? extra : 0;
};

const tooltipFallbackText = (type) => {
  const total = Number(localReactionsSummary.value?.[type]) || 0;
  if (!total || tooltipNamesForType(type).length) return '';
  return total === 1 ? '1 person reacted' : `${total} people reacted`;
};

const SHOW_COMMENT_REPLIES_UI = true; // UI only (same as main post)

// Local-only: latest reply preview (mirrors main post behavior)
const latestReplyByParentId = ref({}); // { [commentId]: replyObj }

// Reply composer state
const openReplyForCommentId = ref(null);
const replyTextByCommentId = ref({}); // { [commentId]: 'text' }
const isSubmittingReply = ref(false);
const openReplyEmojiForCommentId = ref(null);
const pendingReplyMediaByCommentId = ref({});
const replyMediaInputRefs = new Map();

const setReplyMediaInputRef = (commentId, el) => {
  const id = Number(commentId);
  if (!id) return;
  if (el) replyMediaInputRefs.set(id, el);
  else replyMediaInputRefs.delete(id);
};

const clearPendingReplyMedia = (commentId) => {
  const id = Number(commentId);
  if (!id) return;
  const current = pendingReplyMediaByCommentId.value?.[id];
  if (current?.previewUrl && String(current.previewUrl).startsWith('blob:')) {
    try {
      URL.revokeObjectURL(current.previewUrl);
    } catch {
      // no-op
    }
  }
  const next = { ...(pendingReplyMediaByCommentId.value || {}) };
  delete next[id];
  pendingReplyMediaByCommentId.value = next;
  const input = replyMediaInputRefs.get(id);
  if (input) input.value = '';
};

const triggerReplyMediaPicker = (commentId) => {
  const id = Number(commentId);
  if (!id) return;
  openReplyEmojiForCommentId.value = null;
  const input = replyMediaInputRefs.get(id);
  input?.click?.();
};

const toggleReplyEmojiPicker = (commentId) => {
  const id = Number(commentId);
  if (!id) return;
  openReplyEmojiForCommentId.value =
    Number(openReplyEmojiForCommentId.value) === id ? null : id;
};

const addReplyEmoji = (commentId, emoji) => {
  const id = Number(commentId);
  if (!id) return;
  const value = String(emoji?.i || emoji?.emoji || '');
  if (!value) return;
  replyTextByCommentId.value = {
    ...(replyTextByCommentId.value || {}),
    [id]: `${replyTextByCommentId.value?.[id] || ''}${value}`,
  };
  openReplyEmojiForCommentId.value = null;
};

const handleReplyMediaPicked = (commentId, event) => {
  const id = Number(commentId);
  if (!id) return;
  const file = event?.target?.files?.[0];
  if (!file) return;

  const mime = String(file.type || '').toLowerCase();
  const isImage = mime.startsWith('image/');
  const isVideo = mime.startsWith('video/');
  if (!isImage && !isVideo) {
    if (typeof notify === 'function') {
      notify({
        title: 'Unsupported file',
        body: 'Please choose an image or video.',
        variant: 'error',
      });
    }
    const input = replyMediaInputRefs.get(id);
    if (input) input.value = '';
    return;
  }

  clearPendingReplyMedia(id);
  pendingReplyMediaByCommentId.value = {
    ...(pendingReplyMediaByCommentId.value || {}),
    [id]: {
      file,
      type: isVideo ? 'video' : 'image',
      previewUrl: URL.createObjectURL(file),
    },
  };
};

const getCommentReplyCount = (comment) => {
  const c = comment || {};
  // tolerate backend naming
  const v =
    c.replyCount ??
    c.repliesCount ??
    c.replies_count ??
    c.reply_count ??
    0;

  return Number(v) || 0;
};

const hasLocalLatestReply = (commentId) => {
  return !!latestReplyByParentId.value?.[commentId];
};

// DOM refs for thread line bottom calculations
const commentRowRefs = new Map();           // commentId -> el
const replyAvatarRefs = new Map();          // commentId -> el (latest reply avatar)
const replyComposerAvatarRefs = new Map();  // commentId -> el (composer avatar)

// ✅ prevents render/ref/recalc loops (1 recalc per comment per frame)
const _threadLineRafById = new Map(); // commentId -> rafId

const scheduleRecalcThreadLine = (commentId) => {
  const id = Number(commentId);
  if (!id || typeof window === 'undefined') return;

  const existing = _threadLineRafById.get(id);
  if (existing) cancelAnimationFrame(existing);

  _threadLineRafById.set(
    id,
    requestAnimationFrame(() => {
      _threadLineRafById.delete(id);
      recalcThreadLineForComment(id);
    })
  );
};


// reactive bottom px per commentId
const threadLineBottomById = ref({}); // { [commentId]: '12px' }
const lastReplyAvatarIdByComment = new Map(); // commentId -> replyId (current anchor)

const setCommentRowRef = (commentId, el) => {
  if (!commentId) return;
  const id = Number(commentId);

  if (el) commentRowRefs.set(id, el);
  else {
    commentRowRefs.delete(id);
    removeMapKey(threadLineBottomById, id);
  }

  scheduleRecalcThreadLine(id);
};

const setReplyAvatarRef = (commentId, replyId, el) => {
  if (!commentId || !replyId) return;
  const id = Number(commentId);
  const rid = Number(replyId);
  const currentlyAnchoredReplyId = Number(lastReplyAvatarIdByComment.get(id) || 0);
  const replies = repliesByParentId.value?.[id];
  const lastReply = Array.isArray(replies) && replies.length ? replies[replies.length - 1] : null;
  const isCurrentLastReply = Number(lastReply?.id) === rid;

  if (el && isCurrentLastReply) {
    replyAvatarRefs.set(id, el);
    lastReplyAvatarIdByComment.set(id, rid);
    scheduleRecalcThreadLine(id); // ✅ fixes tails when last reply mounts
    return;
  }

  // Clear stale anchor when this reply is no longer the last (or unmounted)
  if ((!el || !isCurrentLastReply) && currentlyAnchoredReplyId === rid) {
    replyAvatarRefs.delete(id);
    lastReplyAvatarIdByComment.delete(id);
    scheduleRecalcThreadLine(id);
  }
};


const setReplyComposerAvatarRef = (commentId, el) => {
  if (!commentId) return;
  const id = Number(commentId);

  if (el) replyComposerAvatarRefs.set(id, el);
  else replyComposerAvatarRefs.delete(id);

  scheduleRecalcThreadLine(id); // ✅ spine snaps to composer when opened
};

const recalcThreadLineForComment = async (commentId) => {
  await nextTick();

  const id = Number(commentId);
  const rowEl = commentRowRefs.get(id);
  if (!rowEl) return;

  // Choose the lowest visible anchor inside the row:
  // 1) reply composer avatar if open
  // 2) latest reply avatar if present
  const composerEl = replyComposerAvatarRefs.get(id);
  const replyEl = replyAvatarRefs.get(id);
  const viewRepliesEl = viewRepliesRowRefs.get(id);

  // anchor priority: composer > last reply avatar > "View replies" row
  const anchorEl = composerEl || replyEl || viewRepliesEl;

  // ✅ no anchor → default bottom, but don't rewrite if it's already the same
  if (!anchorEl) {
    const nextVal = '12px';
    if ((threadLineBottomById.value?.[id] || '12px') !== nextVal) {
      threadLineBottomById.value = { ...threadLineBottomById.value, [id]: nextVal };
    }
    return;
  }

  const rowRect = rowEl.getBoundingClientRect();
  const anchorRect = anchorEl.getBoundingClientRect();

  // If the anchor is the "View replies" row, stop at its midline (looks clean)
  let bottomPx;

  if (anchorEl === viewRepliesEl) {
    const targetY = anchorRect.top + (anchorRect.height / 2);
    bottomPx = Math.max(8, Math.round(rowRect.bottom - targetY));
  } else {
    // your tuned avatar logic
    bottomPx = Math.max(8, Math.round(rowRect.bottom - anchorRect.bottom + 23));
  }

  // ✅ dedupe write (prevents render/ref/recalc loops)
  const nextVal = `${bottomPx}px`;
  if ((threadLineBottomById.value?.[id] || '12px') === nextVal) return;

  threadLineBottomById.value = {
    ...threadLineBottomById.value,
    [id]: nextVal,
  };
};




const threadLineBottomStyle = (commentId) => {
  const id = Number(commentId);
  return threadLineBottomById.value?.[id] || '12px';
};

const openReplyBox = async (commentId) => {
  const id = Number(commentId);
  openReplyForCommentId.value = id;

  // ensure reactive key exists
  if (!replyTextByCommentId.value[id]) {
    replyTextByCommentId.value = { ...replyTextByCommentId.value, [id]: '' };
  }

  // ✅ reset removed-state each time composer opens
  replyPrefixRemovedByCommentId.value = {
    ...(replyPrefixRemovedByCommentId.value || {}),
    [id]: false,
  };

  // ✅ NEW: default the blue token to the comment author (if missing)
  setReplyingToForCommentIfMissing(id);

  await recalcThreadLineForComment(id);
};


const bumpLocalReplyCount = (commentId) => {
  const idx = findCommentIndexById(commentId);
  if (idx < 0) return;

  const list = Array.isArray(comments.value) ? [...comments.value] : [];
  const c = list[idx] || {};

  const current = getCommentReplyCount(c);
  const next = current + 1;

  // write into the most common field, but keep old too if it exists
  list[idx] = {
    ...c,
    replyCount: next,
    repliesCount: typeof c.repliesCount !== 'undefined' ? next : c.repliesCount,
    replies_count: typeof c.replies_count !== 'undefined' ? next : c.replies_count,
  };

  comments.value = list;
};

async function submitReply(parentCommentId) {
  const commentId = Number(parentCommentId);
  if (!commentId) return;

  const body = String(replyTextByCommentId.value?.[commentId] || '').trim();
  const pendingMedia = pendingReplyMediaByCommentId.value?.[commentId] || null;
  if (!body && !pendingMedia?.file) return;

  if (isSubmittingReply.value) return;
  isSubmittingReply.value = true;

  try {
    // ✅ ensure the "replying to" target is set (so prefix logic has a name to use)
    setReplyingToForCommentIfMissing(commentId);

    const targetName = String(replyReplyingToByCommentId.value?.[commentId]?.name || '').trim();

    // ✅ If prefix token is active, prepend the name (mirrors depth-2 behavior)
    let finalBody = body;
    if (body && targetName && replyPrefixIsActive(commentId)) {
      finalBody = `${targetName} ${body}`;
    }

    // ✅ endpoint assumption (adjust if your backend differs)
    let replyAttachments = [];
    if (pendingMedia?.file) {
      const uploaded = await uploadCommentMediaFile(pendingMedia.file);
      if (uploaded === null) throw new Error('upload_failed');
      replyAttachments = uploaded;
    }

    const payload = { body: finalBody };
    if (replyAttachments.length) {
      payload.attachments = replyAttachments;
      payload.attachment = replyAttachments;
      payload.attachment_json = JSON.stringify(replyAttachments);
    }

    const resp = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/comments/${encodeURIComponent(commentId)}/replies`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      }
    );

    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      console.error('[NewsFeedFullPostSlug] submitReply NOT OK:', resp.status, text);
      return;
    }

    const data = await resp.json().catch(() => ({}));
    const newReply = data?.reply || data?.comment || data?.data || null;

    if (newReply) {
      const normalized = {
        ...newReply,
        id: newReply.id ?? newReply.replyId ?? Date.now(),
        authorName: newReply.authorName || newReply.author_name || newReply.name || postingAsNameComputed.value || 'You',
        authorAvatarUrl: newReply.authorAvatarUrl || newReply.author_avatar_url || newReply.avatarUrl || commenterAvatarSrc.value || defaultAuthorAvatar,
        body: newReply.body || '',
      };
      if (replyAttachments.length && !Array.isArray(normalized.attachments)) {
        normalized.attachments = replyAttachments;
      }

      const current = Array.isArray(repliesByParentId.value?.[commentId])
        ? [...repliesByParentId.value[commentId]]
        : [];

      // append newest at end (keeps “thread” chronological feel)
      current.push(normalized);

      repliesByParentId.value = {
        ...(repliesByParentId.value || {}),
        [commentId]: current,
      };

      bumpLocalReplyCount(commentId);

      // ensure replies stay visible after posting (both modes)
      expandedRepliesIds.value = new Set(expandedRepliesIds.value);
      expandedRepliesIds.value.add(commentId);
    }

    // clear + close composer
    replyTextByCommentId.value = { ...replyTextByCommentId.value, [commentId]: '' };
    clearPendingReplyMedia(commentId);
    openReplyEmojiForCommentId.value = null;
    openReplyForCommentId.value = null;

    // ✅ reset removed flag so next time the prefix token shows again by default
    replyPrefixRemovedByCommentId.value = {
      ...(replyPrefixRemovedByCommentId.value || {}),
      [commentId]: false,
    };

    await recalcThreadLineForComment(commentId);
  } catch (err) {
    console.error('[NewsFeedFullPostSlug] submitReply error:', err);
  } finally {
    isSubmittingReply.value = false;
  }
}


const openReplyBoxReplyingToCommentAuthor = async (commentObj) => {
  const cid = Number(commentObj?.id);
  if (!cid) return;

  const name = String(commentObj?.authorName || commentObj?.author_name || '').trim();

  if (name) {
    replyReplyingToByCommentId.value = {
      ...(replyReplyingToByCommentId.value || {}),
      [cid]: { id: cid, name },
    };
  }

  await openReplyBox(cid);
};


watch(openReplyForCommentId, async (newId, oldId) => {
  if (oldId) await recalcThreadLineForComment(oldId);
  if (newId) await recalcThreadLineForComment(newId);
});

watch(
  () => latestReplyByParentId.value,
  async (val) => {
    // recalc any comment that now has a latest reply
    const keys = Object.keys(val || {});
    for (const k of keys) {
      await recalcThreadLineForComment(Number(k));
    }
  },
  { deep: true }
);

const handleResizeForThreadLines = async () => {
  const commentIds = new Set([...Array.from(commentRowRefs.keys())]);
  for (const id of commentIds) {
    await recalcThreadLineForComment(id);
  }

  const replyIds = new Set([...Array.from(replyRowRefs.keys())]);
  for (const rid of replyIds) {
    await recalcThreadLineForReply(rid);
  }
};


const autoExpandReplies = computed(() => {
  // Your requirement: less than 11 comments
  return (Array.isArray(comments.value) ? comments.value.length : 0) < 11;
});

const repliesByParentId = ref({}); // { [commentId]: [reply, reply, ...] }
const expandedRepliesIds = ref(new Set()); // Set(commentId)
const isRepliesLoadingByCommentId = ref({}); // { [commentId]: true/false }

const isRepliesExpanded = (commentId) => {
  return expandedRepliesIds.value.has(Number(commentId));
};

const fetchRepliesForComment = async (commentId) => {
  const id = Number(commentId);
  if (!id) return;

  // already loaded
  if (Array.isArray(repliesByParentId.value?.[id]) && repliesByParentId.value[id].length) return;

  // mark loading
  isRepliesLoadingByCommentId.value = { ...isRepliesLoadingByCommentId.value, [id]: true };

  try {
    const resp = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/comments/${encodeURIComponent(id)}/replies`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }
    );

    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      console.error('[NewsFeedFullPostSlug] fetchReplies NOT OK:', resp.status, text);
      return;
    }

    const data = await resp.json().catch(() => ({}));
    const list = Array.isArray(data?.replies) ? data.replies : (Array.isArray(data) ? data : []);

    const normalized = list.map((r) => ({
      ...r,
      id: r.id ?? r.replyId ?? r.comment_id ?? Math.random(),
      authorName: r.authorName || r.author_name || r.name || 'User',
      authorAvatarUrl: r.authorAvatarUrl || r.author_avatar_url || r.avatarUrl || defaultAuthorAvatar,
      body: r.body || '',
    }));

    repliesByParentId.value = {
      ...(repliesByParentId.value || {}),
      [id]: normalized,
    };

    // ✅ Load depth-2 immediately so they render without clicking "Reply"
    prefetchReplyRepliesForReplies(normalized);

  } catch (err) {
    console.error('[NewsFeedFullPostSlug] fetchReplies error:', err);
  } finally {
    isRepliesLoadingByCommentId.value = { ...isRepliesLoadingByCommentId.value, [id]: false };
  }
};

const expandReplies = async (commentId) => {
  const id = Number(commentId);
  if (!id) return;

  expandedRepliesIds.value = new Set(expandedRepliesIds.value);
  expandedRepliesIds.value.add(id);

  await fetchRepliesForComment(id);
  await recalcThreadLineForComment(id);
};

const maybeAutoExpandReplies = async () => {
  if (!autoExpandReplies.value) return;

  const list = Array.isArray(comments.value) ? comments.value : [];
  for (const c of list) {
    const id = Number(c?.id);
    if (!id) continue;

    if (getCommentReplyCount(c) > 0) {
      expandedRepliesIds.value = new Set(expandedRepliesIds.value);
      expandedRepliesIds.value.add(id);
      await fetchRepliesForComment(id);
      await recalcThreadLineForComment(id);
    }
  }
};

const viewRepliesRowRefs = new Map(); // commentId -> el


const setViewRepliesRowRef = (commentId, el) => {
  if (!commentId) return;
  const id = Number(commentId);

  if (el) viewRepliesRowRefs.set(id, el);
  else viewRepliesRowRefs.delete(id);

  scheduleRecalcThreadLine(id); // ✅ spine stops at “View replies” without freezing
};

watch(
  () => [props.isOpen, props.inlineSidebar, comments.value.length],
  async ([open, inlineSidebar]) => {
    if (!(open || inlineSidebar)) return;
    await nextTick();
    await handleResizeForThreadLines();
  }
);

// ─────────────────────────────────────────────
// Reply reactions (SERVER-BACKED, MUTABLE) ✅
// Each reply object should support:
//   reactionsCount, reactionsSummary, reactionUsersByType, myReactionType
// ─────────────────────────────────────────────
const openReplyReactionForId = ref(null);
const hoveredReplyPickerId = ref(null);

// hover open/close timers (same feel as comments)
let replyReactionOpenTimer = null;
let replyReactionCloseTimer = null;

const handleReplyReactionMouseEnter = (replyId) => {
  if (replyReactionCloseTimer) {
    clearTimeout(replyReactionCloseTimer);
    replyReactionCloseTimer = null;
  }

  if (openReplyReactionForId.value === replyId) return;

  if (replyReactionOpenTimer) clearTimeout(replyReactionOpenTimer);
  replyReactionOpenTimer = setTimeout(() => {
    openReplyReactionForId.value = replyId;
    replyReactionOpenTimer = null;
  }, 200);
};

const handleReplyReactionMouseLeave = (replyId) => {
  if (replyReactionOpenTimer) {
    clearTimeout(replyReactionOpenTimer);
    replyReactionOpenTimer = null;
  }

  if (openReplyReactionForId.value !== replyId) return;

  if (replyReactionCloseTimer) clearTimeout(replyReactionCloseTimer);
  replyReactionCloseTimer = setTimeout(() => {
    if (props.inlineSidebar && Number(hoveredReplyPickerId.value) === Number(replyId)) return;
    if (openReplyReactionForId.value === replyId) {
      openReplyReactionForId.value = null;
    }
    replyReactionCloseTimer = null;
  }, 250);
};

const handleReplyPickerHoverChange = (replyId, hovering) => {
  hoveredReplyPickerId.value = hovering ? Number(replyId) : null;
  if (hovering) {
    handleReplyReactionMouseEnter(replyId);
  } else {
    handleReplyReactionMouseLeave(replyId);
  }
};

const findReplyIndex = (parentCommentId, replyId) => {
  const pid = Number(parentCommentId);
  const rid = Number(replyId);
  const arr = Array.isArray(repliesByParentId.value?.[pid]) ? repliesByParentId.value[pid] : [];
  return arr.findIndex((r) => Number(r?.id) === rid);
};

const getReplyById = (parentCommentId, replyId) => {
  const pid = Number(parentCommentId);
  const idx = findReplyIndex(pid, replyId);
  if (idx < 0) return null;
  return repliesByParentId.value[pid][idx] || null;
};

const getReplyReactionType = (parentCommentId, replyId) => {
  const r = getReplyById(parentCommentId, replyId);
  return resolveReactionTypeFromEntity(r);
};

const getReplyReactionCount = (parentCommentId, replyId) => {
  const r = getReplyById(parentCommentId, replyId);
  const total = Number(r?.reactionsSummary?.total);
  if (Number.isFinite(total)) return total;
  return Number(r?.reactionsCount) || 0;
};

const getReplyReactionRows = (parentCommentId, replyId) => {
  const r = getReplyById(parentCommentId, replyId);
  return getSortedReactionRowsFromEntity(r);
};

const getReplyTopReactionTypes = (parentCommentId, replyId) =>
  getReplyReactionRows(parentCommentId, replyId).slice(0, 2).map((row) => row.type);

const getReplyReactionTooltipRows = (parentCommentId, replyId) =>
  getReplyReactionRows(parentCommentId, replyId);

const MAX_REPLY_TOOLTIP_NAMES = 20;

const getReplyReactionTooltipNames = (parentCommentId, replyId) => {
  const rows = getReplyReactionRows(parentCommentId, replyId);
  if (rows.length !== 1) return [];
  const r = getReplyById(parentCommentId, replyId);
  const reactionType = rows[0].type;

  const usersByType = r?.reactionUsersByType || r?.reaction_users_by_type || {};
  const arr = usersByType?.[reactionType];
  const users = Array.isArray(arr) ? arr : [];

  const names = users
    .map((u) => normalizeReactionUserName(u))
    .filter((n) => n.length);

  return names.slice(0, MAX_REPLY_TOOLTIP_NAMES);
};

const getReplyReactionTooltipText = (parentCommentId, replyId) => {
  const total = getReplyReactionCount(parentCommentId, replyId);
  if (total <= 0) return '';
  if (getReplyReactionRows(parentCommentId, replyId).length > 1) return '';

  const names = getReplyReactionTooltipNames(parentCommentId, replyId);

  if (!names.length) return total === 1 ? '1 person' : `${total} people`;
  if (total <= names.length) return names.join(', ');

  const extra = total - names.length;
  return `${names.join(', ')} and ${extra} more`;
};

const getReplyReactionTooltipExtraCount = (parentCommentId, replyId) => {
  const total = getReplyReactionCount(parentCommentId, replyId);
  const shown = getReplyReactionTooltipNames(parentCommentId, replyId).length;
  const extra = total - shown;
  return extra > 0 ? extra : 0;
};

const isSubmittingReplyReaction = ref(false);

const patchReplyFromReactionResponse = (parentCommentId, replyId, data) => {
  const pid = Number(parentCommentId);
  const rid = Number(replyId);

  const idx = findReplyIndex(pid, rid);
  if (idx < 0) return;

  const existing = repliesByParentId.value?.[pid]?.[idx] || {};

  const nextSummary = data?.reactions && typeof data.reactions === 'object'
    ? {
        total: data.reactions.total || 0,
        like: data.reactions.like || 0,
        love: data.reactions.love || 0,
        care: data.reactions.care || 0,
        haha: data.reactions.haha || 0,
        wow: data.reactions.wow || 0,
        sad: data.reactions.sad || 0,
        angry: data.reactions.angry || 0,
      }
    : (existing.reactionsSummary || null);

  const nextMyReactionType =
    typeof data?.myReactionType !== 'undefined'
      ? data.myReactionType
      : existing.myReactionType;

  const nextUsersByType =
    (data?.reactionUsersByType && typeof data.reactionUsersByType === 'object')
      ? data.reactionUsersByType
      : (existing.reactionUsersByType || { like: [], love: [], care: [], haha: [], wow: [], sad: [], angry: [] });

  const next = {
    ...existing,
    reactionsSummary: nextSummary || existing.reactionsSummary || {
      total: Number(existing.reactionsCount) || 0,
      like: 0, love: 0, care: 0, haha: 0, wow: 0, sad: 0, angry: 0,
    },
    reactionsCount:
      (nextSummary && typeof nextSummary.total === 'number')
        ? nextSummary.total
        : (Number(existing.reactionsCount) || 0),
    reactionUsersByType: nextUsersByType,
    myReactionType: nextMyReactionType || null,
  };

  const nextArr = Array.isArray(repliesByParentId.value?.[pid])
    ? [...repliesByParentId.value[pid]]
    : [];

  nextArr[idx] = next;

  repliesByParentId.value = {
    ...(repliesByParentId.value || {}),
    [pid]: nextArr,
  };
};

const handleReplyReactionSelect = async (parentCommentId, replyId, type) => {
  openReplyReactionForId.value = null;

  if (!REACTION_META[type]) return;
  if (isSubmittingReplyReaction.value) return;

  isSubmittingReplyReaction.value = true;

  let prevType = null;
  let optimisticApply = () => {};

  try {
    const pid = Number(parentCommentId);
    const rid = Number(replyId);
    prevType = getReplyReactionType(pid, rid);
    const me = String(postingAsNameComputed.value || 'You');

    optimisticApply = (newType) => {
      const idx = findReplyIndex(pid, rid);
      if (idx < 0) return;

      const list = Array.isArray(repliesByParentId.value?.[pid])
        ? [...repliesByParentId.value[pid]]
        : [];

      const r = list[idx] || {};

      const usersByType = (r.reactionUsersByType && typeof r.reactionUsersByType === 'object')
        ? JSON.parse(JSON.stringify(r.reactionUsersByType))
        : { like: [], love: [], care: [], haha: [], wow: [], sad: [], angry: [] };

      for (const k of REACTION_KEYS) {
        const arr = Array.isArray(usersByType[k]) ? usersByType[k] : [];
        usersByType[k] = arr.filter((u) => String(u?.name || u) !== me);
      }

      const summary = (r.reactionsSummary && typeof r.reactionsSummary === 'object')
        ? { ...r.reactionsSummary }
        : {
            total: Number(r.reactionsCount) || 0,
            like: 0, love: 0, care: 0, haha: 0, wow: 0, sad: 0, angry: 0,
          };

      if (prevType && summary[prevType] > 0) summary[prevType] = Number(summary[prevType] || 0) - 1;

      if (newType) {
        summary[newType] = Number(summary[newType] || 0) + 1;
        if (!prevType) summary.total = Number(summary.total || 0) + 1;
      } else {
        if (prevType) summary.total = Math.max(0, Number(summary.total || 0) - 1);
      }

      if (newType) {
        const arr = Array.isArray(usersByType[newType]) ? usersByType[newType] : [];
        usersByType[newType] = [{ name: me }, ...arr];
      }

      list[idx] = {
        ...r,
        myReactionType: newType || null,
        reactionsSummary: summary,
        reactionsCount: Number(summary.total) || 0,
        reactionUsersByType: usersByType,
      };

      repliesByParentId.value = { ...(repliesByParentId.value || {}), [pid]: list };
    };

    // toggle off
    if (prevType && prevType === type) {
      optimisticApply(null);

      // ✅ endpoint assumption — adjust if yours differs
      const resp = await fetch(
        // RIGHT
`${apiProxyUrl}/api/creator-newsfeed/comments/${encodeURIComponent(rid)}/react`
,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      if (!resp.ok) {
        optimisticApply(prevType || null);
        return;
      }

      let data = {};
      try {
        const text = await resp.text();
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        data = {};
      }

      patchReplyFromReactionResponse(pid, rid, { ...data, myReactionType: null, reactionType: null });
      return;
    }

    // set reaction
    optimisticApply(type);

    const resp = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/comments/${encodeURIComponent(rid)}/react`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reactionType: type }),
      }
    );

    if (!resp.ok) {
      optimisticApply(prevType || null);
      return;
    }

    let data = {};
    try {
      const text = await resp.text();
      data = text ? JSON.parse(text) : {};
    } catch (e) {
      data = {};
    }

    patchReplyFromReactionResponse(pid, rid, { ...data, myReactionType: type, reactionType: type });
  } catch (err) {
    console.error('[NewsFeedFullPostSlug] handleReplyReactionSelect error:', err);
    optimisticApply(prevType || null);
  } finally {
    isSubmittingReplyReaction.value = false;
  }
};

// ─────────────────────────────────────────────
// Reply Replies (depth 2) state
// ─────────────────────────────────────────────
const replyRepliesByParentId = ref({}); // { [replyId]: [replyReply, ...] }
const isReplyRepliesLoadingByParentId = ref({}); // { [replyId]: true/false }
// ✅ Treat "empty list" as loaded so we don't refetch forever
const replyRepliesLoadedByParentId = ref({}); // { [replyId]: true/false }

const openReplyReplyForReplyId = ref(null); // replyId whose nested composer is open
const replyReplyTextByReplyId = ref({}); // { [replyId]: 'text' }
const isSubmittingReplyReply = ref(false);
const openReplyReplyEmojiForReplyId = ref(null);
const pendingReplyReplyMediaByReplyId = ref({});
const replyReplyMediaInputRefs = new Map();

const setReplyReplyMediaInputRef = (replyId, el) => {
  const id = Number(replyId);
  if (!id) return;
  if (el) replyReplyMediaInputRefs.set(id, el);
  else replyReplyMediaInputRefs.delete(id);
};

const clearPendingReplyReplyMedia = (replyId) => {
  const id = Number(replyId);
  if (!id) return;
  const current = pendingReplyReplyMediaByReplyId.value?.[id];
  if (current?.previewUrl && String(current.previewUrl).startsWith('blob:')) {
    try {
      URL.revokeObjectURL(current.previewUrl);
    } catch {
      // no-op
    }
  }
  const next = { ...(pendingReplyReplyMediaByReplyId.value || {}) };
  delete next[id];
  pendingReplyReplyMediaByReplyId.value = next;
  const input = replyReplyMediaInputRefs.get(id);
  if (input) input.value = '';
};

const triggerReplyReplyMediaPicker = (replyId) => {
  const id = Number(replyId);
  if (!id) return;
  openReplyReplyEmojiForReplyId.value = null;
  const input = replyReplyMediaInputRefs.get(id);
  input?.click?.();
};

const toggleReplyReplyEmojiPicker = (replyId) => {
  const id = Number(replyId);
  if (!id) return;
  openReplyReplyEmojiForReplyId.value =
    Number(openReplyReplyEmojiForReplyId.value) === id ? null : id;
};

const addReplyReplyEmoji = (replyId, emoji) => {
  const id = Number(replyId);
  if (!id) return;
  const value = String(emoji?.i || emoji?.emoji || '');
  if (!value) return;
  replyReplyTextByReplyId.value = {
    ...(replyReplyTextByReplyId.value || {}),
    [id]: `${replyReplyTextByReplyId.value?.[id] || ''}${value}`,
  };
  openReplyReplyEmojiForReplyId.value = null;
};

const handleReplyReplyMediaPicked = (replyId, event) => {
  const id = Number(replyId);
  if (!id) return;
  const file = event?.target?.files?.[0];
  if (!file) return;

  const mime = String(file.type || '').toLowerCase();
  const isImage = mime.startsWith('image/');
  const isVideo = mime.startsWith('video/');
  if (!isImage && !isVideo) {
    if (typeof notify === 'function') {
      notify({
        title: 'Unsupported file',
        body: 'Please choose an image or video.',
        variant: 'error',
      });
    }
    const input = replyReplyMediaInputRefs.get(id);
    if (input) input.value = '';
    return;
  }

  clearPendingReplyReplyMedia(id);
  pendingReplyReplyMediaByReplyId.value = {
    ...(pendingReplyReplyMediaByReplyId.value || {}),
    [id]: {
      file,
      type: isVideo ? 'video' : 'image',
      previewUrl: URL.createObjectURL(file),
    },
  };
};

const SHOW_REPLY_REPLIES_UI = true;
const REPLY_REPLIES_PREVIEW_MAX = 5;

// depth2 expanded state (mirrors comment expandedRepliesIds)
const expandedReplyRepliesIds = ref(new Set()); // Set(replyId)

const isReplyRepliesExpanded = (replyId) => {
  return expandedReplyRepliesIds.value.has(Number(replyId));
};


// Load nested replies for a reply (depth 2)
const fetchReplyRepliesForReply = async (replyId) => {
  const rid = Number(replyId);
  if (!rid) return;

  // ✅ NEW: if we already loaded once (even empty), don't refetch
  if (replyRepliesLoadedByParentId.value?.[rid]) return;

  isReplyRepliesLoadingByParentId.value = { ...isReplyRepliesLoadingByParentId.value, [rid]: true };

  try {
    const resp = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/comments/${encodeURIComponent(rid)}/replies`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }
    );

    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      console.error('[NewsFeedFullPostSlug] fetchReplyReplies NOT OK:', resp.status, text);
      return;
    }

    const data = await resp.json().catch(() => ({}));
    const list = Array.isArray(data?.replies) ? data.replies : (Array.isArray(data) ? data : []);

    const normalized = list.map((rr) => ({
      ...rr,
      id: rr.id ?? rr.replyId ?? rr.comment_id ?? Math.random(),
      authorName: rr.authorName || rr.author_name || rr.name || 'User',
      authorAvatarUrl: rr.authorAvatarUrl || rr.author_avatar_url || rr.avatarUrl || defaultAuthorAvatar,
      body: rr.body || '',
      reactionsSummary: rr.reactionsSummary || rr.reactions_summary || null,
      reactionsCount: typeof rr.reactionsCount === 'number' ? rr.reactionsCount : (Number(rr.reactions_count) || 0),
      reactionUsersByType: rr.reactionUsersByType || rr.reaction_users_by_type || rr.reactionUsers || null,
      myReactionType: rr.myReactionType || rr.my_reaction_type || null,
    }));

    replyRepliesByParentId.value = {
      ...(replyRepliesByParentId.value || {}),
      [rid]: normalized,
    };

    // ✅ NEW: mark loaded even if normalized is []
    replyRepliesLoadedByParentId.value = {
      ...(replyRepliesLoadedByParentId.value || {}),
      [rid]: true,
    };

    await nextTick();
    scheduleRecalcReplyThreadLine(rid);

  } catch (err) {
    console.error('[NewsFeedFullPostSlug] fetchReplyReplies error:', err);
  } finally {
    isReplyRepliesLoadingByParentId.value = { ...isReplyRepliesLoadingByParentId.value, [rid]: false };
  }
};

const openReplyReplyBox = async (rootCommentId, replyId) => {
  const rid = Number(replyId);
  if (!rid) return;

  // ✅ IMPORTANT: do NOT overwrite an existing "replying to" target.
  // This is critical for the "Reply" click on a reply-reply (rr) path,
  // where openReplyReplyBoxFromReplyReply(...) already set the correct target.
  const existingName = String(replyReplyingToByReplyId.value?.[rid]?.name || '').trim();

  if (!existingName) {
    // ✅ set the "replying to" target for this replyId (so token can show)
    const cid = Number(rootCommentId);
    const arr = Array.isArray(repliesByParentId.value?.[cid]) ? repliesByParentId.value[cid] : [];
    const replyObj = arr.find(x => Number(x?.id) === rid) || null;

    const name = String(
      replyObj?.authorName ||
      replyObj?.author_name ||
      replyObj?.name ||
      ''
    ).trim();

    if (name) {
      replyReplyingToByReplyId.value = {
        ...(replyReplyingToByReplyId.value || {}),
        [rid]: { id: rid, name },
      };
    } else {
      // if we can't resolve a name, clear any stale target
      const next = { ...(replyReplyingToByReplyId.value || {}) };
      delete next[rid];
      replyReplyingToByReplyId.value = next;
    }
  }

  openReplyForCommentId.value = null;
  openReplyReplyForReplyId.value = rid;

  // reset removed-state each time composer opens
  replyReplyPrefixRemovedByReplyId.value = {
    ...(replyReplyPrefixRemovedByReplyId.value || {}),
    [rid]: false,
  };

  expandedReplyRepliesIds.value = new Set(expandedReplyRepliesIds.value);
  expandedReplyRepliesIds.value.add(rid);

  if (typeof replyReplyTextByReplyId.value[rid] === 'undefined') {
    replyReplyTextByReplyId.value = { ...replyReplyTextByReplyId.value, [rid]: '' };
  }

  await fetchReplyRepliesForReply(rid);
  await nextTick();
  scheduleRecalcReplyThreadLine(rid);
};







async function submitReplyReply(rootCommentId, parentReplyId) {
  const rid = Number(parentReplyId);
  if (!rid) return;

  let body = String(replyReplyTextByReplyId.value?.[rid] || '').trim();
  const pendingMedia = pendingReplyReplyMediaByReplyId.value?.[rid] || null;
  if (!body && !pendingMedia?.file) return;

// If prefix token is still active, prepend the FULL NAME (no @)
const targetName = String(replyReplyingToByReplyId.value?.[rid]?.name || '').trim();
const prefixActive = replyReplyPrefixIsActive(rid);

if (body && targetName && prefixActive) {
  const prefix = `${targetName} `;
  if (!body.toLowerCase().startsWith(prefix.toLowerCase())) {
    body = prefix + body;
  }
}



  if (isSubmittingReplyReply.value) return;
  isSubmittingReplyReply.value = true;

  try {
    let replyAttachments = [];
    if (pendingMedia?.file) {
      const uploaded = await uploadCommentMediaFile(pendingMedia.file);
      if (uploaded === null) throw new Error('upload_failed');
      replyAttachments = uploaded;
    }

    const payload = { body };
    if (replyAttachments.length) {
      payload.attachments = replyAttachments;
      payload.attachment = replyAttachments;
      payload.attachment_json = JSON.stringify(replyAttachments);
    }

    // same endpoint pattern as comment replies — parent is the replyId
    const resp = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/comments/${encodeURIComponent(rid)}/replies`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      }
    );

    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      console.error('[NewsFeedFullPostSlug] submitReplyReply NOT OK:', resp.status, text);
      return;
    }

    const data = await resp.json().catch(() => ({}));
    const newRR = data?.reply || data?.comment || data?.data || null;

    if (newRR) {
      const normalized = {
        ...newRR,
        id: newRR.id ?? newRR.replyId ?? Date.now(),
        authorName: newRR.authorName || newRR.author_name || newRR.name || postingAsNameComputed.value || 'You',
        authorAvatarUrl: newRR.authorAvatarUrl || newRR.author_avatar_url || newRR.avatarUrl || commenterAvatarSrc.value || defaultAuthorAvatar,
        body: newRR.body || '',
        reactionsSummary: newRR.reactionsSummary || newRR.reactions_summary || null,
        reactionsCount: typeof newRR.reactionsCount === 'number' ? newRR.reactionsCount : (Number(newRR.reactions_count) || 0),
        reactionUsersByType: newRR.reactionUsersByType || newRR.reaction_users_by_type || newRR.reactionUsers || null,
        myReactionType: newRR.myReactionType || newRR.my_reaction_type || null,
      };
      if (replyAttachments.length && !Array.isArray(normalized.attachments)) {
        normalized.attachments = replyAttachments;
      }

      const current = Array.isArray(replyRepliesByParentId.value?.[rid])
        ? [...replyRepliesByParentId.value[rid]]
        : [];

      current.push(normalized);

      replyRepliesByParentId.value = {
        ...(replyRepliesByParentId.value || {}),
        [rid]: current,
      };

      await nextTick();
    scheduleRecalcReplyThreadLine(rid);

    } else {
      // fallback reload
      await fetchReplyRepliesForReply(rid);
    }

    replyReplyTextByReplyId.value = { ...replyReplyTextByReplyId.value, [rid]: '' };
    clearPendingReplyReplyMedia(rid);
    openReplyReplyEmojiForReplyId.value = null;
    openReplyReplyForReplyId.value = null;
  } catch (err) {
    console.error('[NewsFeedFullPostSlug] submitReplyReply error:', err);
  } finally {

    const nextTargets = { ...(replyReplyingToByReplyId.value || {}) };
    delete nextTargets[rid];
    replyReplyingToByReplyId.value = nextTargets;

    const nextRemoved = { ...(replyReplyPrefixRemovedByReplyId.value || {}) };
    delete nextRemoved[rid];
    replyReplyPrefixRemovedByReplyId.value = nextRemoved;

    isSubmittingReplyReply.value = false;
  }
}

// ✅ NEW: after loading replies for a comment, prefetch depth-2 for each reply
const prefetchReplyRepliesForReplies = async (replies) => {
  const list = Array.isArray(replies) ? replies : [];
  for (const r of list) {
    const rid = Number(r?.id);
    if (!rid) continue;

    // fire-and-forget (do not await one-by-one blocking UI)
    // but still respects replyRepliesLoadedByParentId cache
    fetchReplyRepliesForReply(rid);
  }
};



// ─────────────────────────────────────────────
// Reply Reply reactions (SERVER-BACKED, MUTABLE)
// Uses /api/creator-newsfeed/comments/:id/react
// ─────────────────────────────────────────────
const openReplyReplyReactionForId = ref(null);
const hoveredReplyReplyPickerId = ref(null);

let replyReplyReactionOpenTimer = null;
let replyReplyReactionCloseTimer = null;

const handleReplyReplyReactionMouseEnter = (id) => {
  if (replyReplyReactionCloseTimer) {
    clearTimeout(replyReplyReactionCloseTimer);
    replyReplyReactionCloseTimer = null;
  }

  if (openReplyReplyReactionForId.value === id) return;

  if (replyReplyReactionOpenTimer) clearTimeout(replyReplyReactionOpenTimer);
  replyReplyReactionOpenTimer = setTimeout(() => {
    openReplyReplyReactionForId.value = id;
    replyReplyReactionOpenTimer = null;
  }, 200);
};

const handleReplyReplyReactionMouseLeave = (id) => {
  if (replyReplyReactionOpenTimer) {
    clearTimeout(replyReplyReactionOpenTimer);
    replyReplyReactionOpenTimer = null;
  }

  if (openReplyReplyReactionForId.value !== id) return;

  if (replyReplyReactionCloseTimer) clearTimeout(replyReplyReactionCloseTimer);
  replyReplyReactionCloseTimer = setTimeout(() => {
    if (props.inlineSidebar && Number(hoveredReplyReplyPickerId.value) === Number(id)) return;
    if (openReplyReplyReactionForId.value === id) openReplyReplyReactionForId.value = null;
    replyReplyReactionCloseTimer = null;
  }, 250);
};

const handleReplyReplyPickerHoverChange = (id, hovering) => {
  hoveredReplyReplyPickerId.value = hovering ? Number(id) : null;
  if (hovering) {
    handleReplyReplyReactionMouseEnter(id);
  } else {
    handleReplyReplyReactionMouseLeave(id);
  }
};

const findReplyReplyLocation = (replyReplyId) => {
  const rid = Number(replyReplyId);
  const map = replyRepliesByParentId.value || {};
  for (const parentReplyId of Object.keys(map)) {
    const arr = Array.isArray(map[parentReplyId]) ? map[parentReplyId] : [];
    const idx = arr.findIndex((x) => Number(x?.id) === rid);
    if (idx >= 0) return { parentReplyId: Number(parentReplyId), idx };
  }
  return null;
};

const getReplyReplyById = (replyReplyId) => {
  const loc = findReplyReplyLocation(replyReplyId);
  if (!loc) return null;
  const arr = replyRepliesByParentId.value?.[loc.parentReplyId] || [];
  return arr[loc.idx] || null;
};

const getReplyReplyReactionType = (replyReplyId) => {
  const rr = getReplyReplyById(replyReplyId);
  return resolveReactionTypeFromEntity(rr);
};

const getReplyReplyReactionCount = (replyReplyId) => {
  const rr = getReplyReplyById(replyReplyId);
  const total = Number(rr?.reactionsSummary?.total);
  if (Number.isFinite(total)) return total;
  return Number(rr?.reactionsCount) || 0;
};

const getReplyReplyReactionRows = (replyReplyId) => {
  const rr = getReplyReplyById(replyReplyId);
  return getSortedReactionRowsFromEntity(rr);
};

const getReplyReplyTopReactionTypes = (replyReplyId) =>
  getReplyReplyReactionRows(replyReplyId).slice(0, 2).map((row) => row.type);

const getReplyReplyReactionTooltipRows = (replyReplyId) =>
  getReplyReplyReactionRows(replyReplyId);

const getReplyReplyReactionTooltipNames = (replyReplyId) => {
  const rows = getReplyReplyReactionRows(replyReplyId);
  if (rows.length !== 1) return [];
  const rr = getReplyReplyById(replyReplyId);
  const reactionType = rows[0].type;

  const usersByType = rr?.reactionUsersByType || rr?.reaction_users_by_type || {};
  const arr = usersByType?.[reactionType];
  const users = Array.isArray(arr) ? arr : [];

  return users
    .map((u) => normalizeReactionUserName(u))
    .filter((n) => n.length)
    .slice(0, 20);
};

const getReplyReplyReactionTooltipText = (replyReplyId) => {
  const total = getReplyReplyReactionCount(replyReplyId);
  if (total <= 0) return '';
  if (getReplyReplyReactionRows(replyReplyId).length > 1) return '';
  const names = getReplyReplyReactionTooltipNames(replyReplyId);

  if (!names.length) return total === 1 ? '1 person' : `${total} people`;
  if (total <= names.length) return names.join(', ');

  const extra = total - names.length;
  return `${names.join(', ')} and ${extra} more`;
};

const getReplyReplyReactionTooltipExtraCount = (replyReplyId) => {
  const total = getReplyReplyReactionCount(replyReplyId);
  const shown = getReplyReplyReactionTooltipNames(replyReplyId).length;
  const extra = total - shown;
  return extra > 0 ? extra : 0;
};

const isSubmittingReplyReplyReaction = ref(false);

const patchReplyReplyFromReactionResponse = (replyReplyId, data) => {
  const loc = findReplyReplyLocation(replyReplyId);
  if (!loc) return;

  const parentReplyId = loc.parentReplyId;
  const idx = loc.idx;

  const existing = replyRepliesByParentId.value?.[parentReplyId]?.[idx] || {};

  const nextSummary = data?.reactions && typeof data.reactions === 'object'
    ? {
        total: data.reactions.total || 0,
        like: data.reactions.like || 0,
        love: data.reactions.love || 0,
        care: data.reactions.care || 0,
        haha: data.reactions.haha || 0,
        wow: data.reactions.wow || 0,
        sad: data.reactions.sad || 0,
        angry: data.reactions.angry || 0,
      }
    : (existing.reactionsSummary || null);

  const nextMyReactionType =
    typeof data?.myReactionType !== 'undefined'
      ? data.myReactionType
      : existing.myReactionType;

  const nextUsersByType =
    (data?.reactionUsersByType && typeof data.reactionUsersByType === 'object')
      ? data.reactionUsersByType
      : (existing.reactionUsersByType || { like: [], love: [], care: [], haha: [], wow: [], sad: [], angry: [] });

  const next = {
    ...existing,
    reactionsSummary: nextSummary || existing.reactionsSummary || {
      total: Number(existing.reactionsCount) || 0,
      like: 0, love: 0, care: 0, haha: 0, wow: 0, sad: 0, angry: 0,
    },
    reactionsCount:
      (nextSummary && typeof nextSummary.total === 'number')
        ? nextSummary.total
        : (Number(existing.reactionsCount) || 0),
    reactionUsersByType: nextUsersByType,
    myReactionType: nextMyReactionType || null,
  };

  const arr = Array.isArray(replyRepliesByParentId.value?.[parentReplyId])
    ? [...replyRepliesByParentId.value[parentReplyId]]
    : [];

  arr[idx] = next;

  replyRepliesByParentId.value = {
    ...(replyRepliesByParentId.value || {}),
    [parentReplyId]: arr,
  };
};

const handleReplyReplyReactionSelect = async (replyReplyId, type) => {
  openReplyReplyReactionForId.value = null;

  if (!REACTION_META[type]) return;
  if (isSubmittingReplyReplyReaction.value) return;

  isSubmittingReplyReplyReaction.value = true;

  let prevType = null;
  let optimisticApply = () => {};

  try {
    const rrId = Number(replyReplyId);
    prevType = getReplyReplyReactionType(rrId);
    const me = String(postingAsNameComputed.value || 'You');

    optimisticApply = (newType) => {
      const loc = findReplyReplyLocation(rrId);
      if (!loc) return;

      const parentReplyId = loc.parentReplyId;
      const idx = loc.idx;

      const arr = Array.isArray(replyRepliesByParentId.value?.[parentReplyId])
        ? [...replyRepliesByParentId.value[parentReplyId]]
        : [];

      const rr = arr[idx] || {};

      const usersByType = (rr.reactionUsersByType && typeof rr.reactionUsersByType === 'object')
        ? JSON.parse(JSON.stringify(rr.reactionUsersByType))
        : { like: [], love: [], care: [], haha: [], wow: [], sad: [], angry: [] };

      for (const k of REACTION_KEYS) {
        const list = Array.isArray(usersByType[k]) ? usersByType[k] : [];
        usersByType[k] = list.filter((u) => String(u?.name || u) !== me);
      }

      const summary = (rr.reactionsSummary && typeof rr.reactionsSummary === 'object')
        ? { ...rr.reactionsSummary }
        : {
            total: Number(rr.reactionsCount) || 0,
            like: 0, love: 0, care: 0, haha: 0, wow: 0, sad: 0, angry: 0,
          };

      if (prevType && summary[prevType] > 0) summary[prevType] = Number(summary[prevType] || 0) - 1;

      if (newType) {
        summary[newType] = Number(summary[newType] || 0) + 1;
        if (!prevType) summary.total = Number(summary.total || 0) + 1;
      } else {
        if (prevType) summary.total = Math.max(0, Number(summary.total || 0) - 1);
      }

      if (newType) {
        const list = Array.isArray(usersByType[newType]) ? usersByType[newType] : [];
        usersByType[newType] = [{ name: me }, ...list];
      }

      arr[idx] = {
        ...rr,
        myReactionType: newType || null,
        reactionsSummary: summary,
        reactionsCount: Number(summary.total) || 0,
        reactionUsersByType: usersByType,
      };

      replyRepliesByParentId.value = { ...(replyRepliesByParentId.value || {}), [parentReplyId]: arr };
    };

    // toggle off
    if (prevType && prevType === type) {
      optimisticApply(null);

      const resp = await fetch(
        `${apiProxyUrl}/api/creator-newsfeed/comments/${encodeURIComponent(rrId)}/react`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      if (!resp.ok) {
        optimisticApply(prevType || null);
        return;
      }

      let data = {};
      try {
        const text = await resp.text();
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        data = {};
      }

      patchReplyReplyFromReactionResponse(rrId, { ...data, myReactionType: null, reactionType: null });
      return;
    }

    // set reaction
    optimisticApply(type);

    const resp = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/comments/${encodeURIComponent(rrId)}/react`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reactionType: type }),
      }
    );

    if (!resp.ok) {
      optimisticApply(prevType || null);
      return;
    }

    let data = {};
    try {
      const text = await resp.text();
      data = text ? JSON.parse(text) : {};
    } catch (e) {
      data = {};
    }

    patchReplyReplyFromReactionResponse(rrId, { ...data, myReactionType: type, reactionType: type });
  } catch (err) {
    console.error('[NewsFeedFullPostSlug] handleReplyReplyReactionSelect error:', err);
    optimisticApply(prevType || null);
  } finally {
    isSubmittingReplyReplyReaction.value = false;
  }
};

// ─────────────────────────────────────────────
// Reply → ReplyReplies (depth 2) thread line sizing (mirror of comments)
// ─────────────────────────────────────────────

// replyId -> row element (the whole depth-1 reply wrapper)
const replyRowRefs = new Map(); // replyId -> el

// replyId -> depth2 container element (optional, but helps if you want)
const replyRepliesBlockRefs = new Map(); // replyId -> el

// replyId -> last nested replyReply avatar (anchor)
const replyReplyAvatarRefs = new Map(); // replyId -> el

// replyId -> nested composer avatar (anchor)
const replyReplyComposerAvatarRefs = new Map(); // replyId -> el

// ✅ prevents loops (1 recalc per reply per frame)
const _replyThreadLineRafById = new Map(); // replyId -> rafId

// reactive bottom px per replyId
const replyThreadLineBottomById = ref({}); // { [replyId]: '12px' }

const viewReplyRepliesRowRefs = new Map(); // replyId -> el

// Who we are replying to (UI only) for a given parent replyId
const replyReplyingToByReplyId = ref({}); // { [replyId]: { id, name } }


const setViewReplyRepliesRowRef = (replyId, el) => {
  const rid = Number(replyId);
  if (!rid) return;

  if (el) viewReplyRepliesRowRefs.set(rid, el);
  else viewReplyRepliesRowRefs.delete(rid);

  scheduleRecalcReplyThreadLine(rid);
};


const shouldShowReplyRepliesThread = (replyId) => {
  const rid = Number(replyId);
  if (!rid) return false;

  const loadedArr = replyRepliesByParentId.value?.[rid];
  const hasLoadedAny = Array.isArray(loadedArr) && loadedArr.length > 0;

  // If backend gives counts on the reply object, template will pass r into getReplyReplyCountForReply.
  // Here we can still show the spine if list is loaded or composer is open or loading.
  const isOpen = openReplyReplyForReplyId.value === rid;
  const isLoading = !!isReplyRepliesLoadingByParentId.value?.[rid];

  return hasLoadedAny || isOpen || isLoading;
};


const setReplyRowRef = (replyId, el) => {
  const rid = Number(replyId);
  if (!rid) return;

  if (el) replyRowRefs.set(rid, el);
  else replyRowRefs.delete(rid);

  scheduleRecalcReplyThreadLine(rid);
};

const setReplyRepliesBlockRef = (replyId, el) => {
  const rid = Number(replyId);
  if (!rid) return;

  if (el) replyRepliesBlockRefs.set(rid, el);
  else replyRepliesBlockRefs.delete(rid);

  scheduleRecalcReplyThreadLine(rid);
};

const setReplyReplyAvatarRef = (replyId, el) => {
  const rid = Number(replyId);
  if (!rid) return;

  if (el) replyReplyAvatarRefs.set(rid, el);
  else replyReplyAvatarRefs.delete(rid);

  scheduleRecalcReplyThreadLine(rid);
};

const setReplyReplyComposerAvatarRef = (replyId, el) => {
  const rid = Number(replyId);
  if (!rid) return;

  if (el) replyReplyComposerAvatarRefs.set(rid, el);
  else replyReplyComposerAvatarRefs.delete(rid);

  scheduleRecalcReplyThreadLine(rid);
};

const scheduleRecalcReplyThreadLine = (replyId) => {
  const rid = Number(replyId);
  if (!rid || typeof window === 'undefined') return;

  const existing = _replyThreadLineRafById.get(rid);
  if (existing) cancelAnimationFrame(existing);

  _replyThreadLineRafById.set(
    rid,
    requestAnimationFrame(() => {
      _replyThreadLineRafById.delete(rid);
      recalcThreadLineForReply(rid);
    })
  );
};

const recalcThreadLineForReply = async (replyId) => {
  const rid = Number(replyId);
  if (!rid) return;

  await nextTick();

  const rowEl = replyRowRefs.get(rid);
  if (!rowEl) return;

  // anchor priority:
// 1) nested composer avatar
// 2) last nested replyReply avatar
// 3) "View X replies" row (when collapsed)
const composerEl = replyReplyComposerAvatarRefs.get(rid);
const lastAvatarEl = replyReplyAvatarRefs.get(rid);
const viewRowEl = viewReplyRepliesRowRefs.get(rid);

const anchorEl = composerEl || lastAvatarEl || viewRowEl;

  // no anchor: default bottom
  if (!anchorEl) {
    const nextVal = '12px';
    if ((replyThreadLineBottomById.value?.[rid] || '12px') !== nextVal) {
      replyThreadLineBottomById.value = { ...replyThreadLineBottomById.value, [rid]: nextVal };
    }
    return;
  }

  const rowRect = rowEl.getBoundingClientRect();
  const anchorRect = anchorEl.getBoundingClientRect();

  // match your comment tuning
  let bottomPx;

if (anchorEl === viewRowEl) {
  const targetY = anchorRect.top + (anchorRect.height / 2);
  bottomPx = Math.max(8, Math.round(rowRect.bottom - targetY));
} else {
  bottomPx = Math.max(8, Math.round(rowRect.bottom - anchorRect.bottom + 23));
}

  const nextVal = `${bottomPx}px`;

  if ((replyThreadLineBottomById.value?.[rid] || '12px') === nextVal) return;

  replyThreadLineBottomById.value = {
    ...replyThreadLineBottomById.value,
    [rid]: nextVal,
  };
};

const replyThreadLineBottomStyle = (replyId) => {
  const rid = Number(replyId);
  return replyThreadLineBottomById.value?.[rid] || '12px';
};

const isLastReplyReplyForReply = (parentReplyId, replyReplyId) => {
  const rid = Number(parentReplyId);
  const rrid = Number(replyReplyId);
  const arr = replyRepliesByParentId.value?.[rid];
  if (!Array.isArray(arr) || !arr.length) return false;
  const last = arr[arr.length - 1];
  return Number(last?.id) === rrid;
};

const getReplyReplyCountForReply = (reply) => {
  const r = reply || {};

  // If backend provides a count on the reply object, use it
  const v =
    r.replyCount ??
    r.repliesCount ??
    r.replies_count ??
    r.reply_count ??
    0;

  const fromField = Number(v) || 0;

  // If we already loaded the nested list, prefer the real length
  const rid = Number(r?.id);
  const arr = rid ? replyRepliesByParentId.value?.[rid] : null;
  const fromLoaded = Array.isArray(arr) ? arr.length : null;

  return typeof fromLoaded === 'number' ? fromLoaded : fromField;
};


const expandReplyReplies = async (replyId) => {
  const rid = Number(replyId);
  if (!rid) return;

  expandedReplyRepliesIds.value = new Set(expandedReplyRepliesIds.value);
  expandedReplyRepliesIds.value.add(rid);

  await fetchReplyRepliesForReply(rid);
  await nextTick();
  scheduleRecalcReplyThreadLine(rid);
};

// Collapse config
const BODY_PREVIEW_MAX_LINES = 8;

// State maps (so each item can toggle independently)
const showFullCommentBodyById = reactive({});
const showFullReplyBodyById = reactive({});
const showFullReplyReplyBodyById = reactive({});

// Normalize + line counting
function normalizeBodyText(str) {
  return (str || '').toString().replace(/\r\n/g, '\n').trimEnd();
}

function getLineCount(str) {
  const s = normalizeBodyText(str);
  if (!s) return 0;
  return s.split('\n').length;
}

function isBodyLongByLines(str, maxLines = BODY_PREVIEW_MAX_LINES) {
  return getLineCount(str) > maxLines;
}

function getPreviewTextByLines(str, maxLines = BODY_PREVIEW_MAX_LINES) {
  const s = normalizeBodyText(str);
  if (!s) return '';
  const lines = s.split('\n');
  if (lines.length <= maxLines) return s;
  return lines.slice(0, maxLines).join('\n');
}

const openReplyReplyBoxFromReplyReply = async (parentReplyId, replyReplyObj) => {
  const rid = Number(parentReplyId);
  if (!rid) return;

  const name = String(replyReplyObj?.authorName || '').trim();

  // store "replying to" target (optional UI/mention)
  if (name) {
    replyReplyingToByReplyId.value = {
      ...(replyReplyingToByReplyId.value || {}),
      [rid]: { id: Number(replyReplyObj?.id) || null, name },
    };
  } else {
    // clear target if no name
    const next = { ...(replyReplyingToByReplyId.value || {}) };
    delete next[rid];
    replyReplyingToByReplyId.value = next;

    replyReplyPrefixRemovedByReplyId.value = {
  ...(replyReplyPrefixRemovedByReplyId.value || {}),
  [rid]: false,
};

  }

  // open the existing depth-2 composer for this parent reply (NO new depth)
  await openReplyReplyBox(null, rid);

  await nextTick();
  scheduleRecalcReplyThreadLine(rid);
};

// Tracks whether the user has removed the bold name prefix for a given parent replyId
const replyReplyPrefixRemovedByReplyId = ref({}); // { [replyId]: true/false }

const replyReplyPrefixIsActive = (replyId) => {
  const rid = Number(replyId);
  const targetName = String(replyReplyingToByReplyId.value?.[rid]?.name || '').trim();
  if (!targetName) return false;
  return !replyReplyPrefixRemovedByReplyId.value?.[rid];
};

const replyReplyComposerPlaceholder = (replyId) => {
  const rid = Number(replyId);
  const name = String(replyReplyingToByReplyId.value?.[rid]?.name || '').trim();

  // If user removed the token, show: "Reply to Full Name"
  if (name && replyReplyPrefixRemovedByReplyId.value?.[rid]) {
    return `Reply to ${name}`;
  }

//   // Otherwise normal placeholder while token is shown
//   return 'Write a reply…';
};

// Backspace removes the bold name token when textarea is empty
const handleReplyReplyComposerKeydown = (replyId, e) => {
  if (!e) return;
  const rid = Number(replyId);

  // Only care if we currently have an active prefix token
  if (!replyReplyPrefixIsActive(rid)) return;

  const currentText = String(replyReplyTextByReplyId.value?.[rid] || '');
  const isBackspace = e.key === 'Backspace';

  // If textarea is empty and user hits backspace -> remove prefix token
  if (isBackspace && currentText.length === 0) {
    e.preventDefault();
    replyReplyPrefixRemovedByReplyId.value = {
      ...(replyReplyPrefixRemovedByReplyId.value || {}),
      [rid]: true,
    };
  }
};


// Who we are replying to (UI only) for a given commentId
const replyReplyingToByCommentId = ref({}); // { [commentId]: { id, name } }

// Tracks whether the user removed the blue name token for a given commentId
const replyPrefixRemovedByCommentId = ref({}); // { [commentId]: true/false }

const replyPrefixIsActive = (commentId) => {
  const cid = Number(commentId);
  const targetName = String(replyReplyingToByCommentId.value?.[cid]?.name || '').trim();
  if (!targetName) return false;
  return !replyPrefixRemovedByCommentId.value?.[cid];
};

const replyComposerPlaceholder = (commentId) => {
  const cid = Number(commentId);
  const name = String(replyReplyingToByCommentId.value?.[cid]?.name || '').trim();

  // If user removed the token, show: "Reply to Full Name"
  if (name && replyPrefixRemovedByCommentId.value?.[cid]) {
    return `Reply to ${name}`;
  }

//   // Otherwise normal placeholder
//   return `Reply as ${postingAsNameComputed.value}`;
};

// Backspace removes the blue name token when textarea is empty
const handleReplyComposerKeydown = (commentId, e) => {
  if (!e) return;
  const cid = Number(commentId);

  if (!replyPrefixIsActive(cid)) return;

  const currentText = String(replyTextByCommentId.value?.[cid] || '');
  const isBackspace = e.key === 'Backspace';

  if (isBackspace && currentText.length === 0) {
    e.preventDefault();
    replyPrefixRemovedByCommentId.value = {
      ...(replyPrefixRemovedByCommentId.value || {}),
      [cid]: true,
    };
  }
};

function onReplyTextareaKeydown(commentId, e) {
  // ENTER without shift => submit
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    submitReply(commentId)
    return
  }

  // SHIFT+ENTER => newline (optionally stop bubbling)
  if (e.key === 'Enter' && e.shiftKey) {
    e.stopPropagation()
    // do NOT preventDefault so it inserts a newline
  }

  // keep your existing behavior (autosize, other shortcuts, etc.)
  handleReplyComposerKeydown(commentId, e)
}

const setReplyingToForCommentIfMissing = (commentId) => {
  const cid = Number(commentId);
  if (!cid) return;

  // If already set, don't overwrite it
  const existing = replyReplyingToByCommentId.value?.[cid];
  if (existing?.name) return;

  const c = getCommentById(cid); // you already have this helper
  const name = String(c?.authorName || c?.author_name || '').trim();
  if (!name) return;

  replyReplyingToByCommentId.value = {
    ...(replyReplyingToByCommentId.value || {}),
    [cid]: { id: cid, name },
  };
};


const autoResizeInlineTextarea = (event) => {
  const el = event?.target
  if (!el) return

  // Reset height to shrink if user deletes text
  el.style.height = 'auto'
  el.style.height = `${el.scrollHeight}px`
}

// Main comment composer has text?
const hasCommentText = computed(() => {
  return String(commentText.value || '').trim().length > 0;
});

// Reply (comment-level) has text?
const hasReplyText = (commentId) => {
  return (
    String(replyTextByCommentId.value?.[commentId] || '').trim().length > 0 ||
    !!pendingReplyMediaByCommentId.value?.[commentId]?.file
  );
};

// Nested reply-to-reply has text?
const hasReplyReplyText = (replyId) => {
  return (
    String(replyReplyTextByReplyId.value?.[replyId] || '').trim().length > 0 ||
    !!pendingReplyReplyMediaByReplyId.value?.[replyId]?.file
  );
};

const editTarget = ref(null); // { id, type, parentCommentId?, parentReplyId? }
const editDraftBody = ref('');
const editOriginalBody = ref('');
const editErrorMessage = ref('');
const isSubmittingEdit = ref(false);
const activeEditTextareaEl = ref(null);
const pendingInlineEditMedia = ref(null); // { file, type, previewUrl }
const inlineEditMediaRemoved = ref(false);
const inlineEditMediaInputEl = ref(null);

const setActiveEditTextareaRef = (el) => {
  if (!el) return;
  activeEditTextareaEl.value = el;
};

const isEditingItem = (type, id) => {
  if (!editTarget.value) return false;
  return (
    String(editTarget.value.type) === String(type) &&
    Number(editTarget.value.id) === Number(id)
  );
};

const getInlineEditTargetEntity = () => {
  if (!editTarget.value) return null;
  const targetId = Number(editTarget.value.id);
  const targetType = String(editTarget.value.type || '');
  if (!targetId) return null;

  if (targetType === 'comment') return getCommentById(targetId);
  if (targetType === 'reply') return getReplyById(targetId);
  if (targetType === 'reply-reply') return getReplyReplyById(targetId);
  return null;
};

const inlineEditExistingMediaItems = computed(() => {
  const entity = getInlineEditTargetEntity();
  return entity ? getCommentMediaItems(entity) : [];
});

const inlineEditMediaPreviewItem = computed(() => {
  if (pendingInlineEditMedia.value?.previewUrl) {
    return {
      type: pendingInlineEditMedia.value.type,
      src: pendingInlineEditMedia.value.previewUrl,
    };
  }
  if (inlineEditMediaRemoved.value) return null;
  return inlineEditExistingMediaItems.value[0] || null;
});

const hasInlineEditMediaChanges = computed(() => {
  return !!pendingInlineEditMedia.value?.file || inlineEditMediaRemoved.value;
});

const canSubmitInlineEdit = computed(() => {
  return (
    String(editDraftBody.value || '').trim().length > 0 ||
    hasInlineEditMediaChanges.value
  );
});

const setInlineEditMediaInputRef = (el) => {
  inlineEditMediaInputEl.value = el || null;
};

const openInlineEditMediaPicker = () => {
  if (isSubmittingEdit.value) return;
  inlineEditMediaInputEl.value?.click();
};

const clearPendingInlineEditMedia = () => {
  const pending = pendingInlineEditMedia.value;
  if (pending?.previewUrl && pending.previewUrl.startsWith('blob:')) {
    URL.revokeObjectURL(pending.previewUrl);
  }
  pendingInlineEditMedia.value = null;
};

const handleInlineEditMediaPicked = (event) => {
  const file = event?.target?.files?.[0];
  if (!file) return;

  if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
    event.target.value = '';
    return;
  }

  clearPendingInlineEditMedia();
  pendingInlineEditMedia.value = {
    file,
    type: file.type.startsWith('video/') ? 'video' : 'image',
    previewUrl: URL.createObjectURL(file),
  };
  inlineEditMediaRemoved.value = false;
  event.target.value = '';
};

const clearInlineEditMediaSelection = () => {
  if (pendingInlineEditMedia.value?.file) {
    clearPendingInlineEditMedia();
    return;
  }
  if (inlineEditExistingMediaItems.value.length) {
    inlineEditMediaRemoved.value = true;
  }
};

const undoInlineEditMediaRemoval = () => {
  inlineEditMediaRemoved.value = false;
};

const focusActiveEditTextarea = async () => {
  await nextTick();
  const el = activeEditTextareaEl.value;
  if (!el) return;
  el.focus();
  try {
    const end = (el.value || '').length;
    el.setSelectionRange(end, end);
  } catch (err) {
    // no-op
  }
  autoResizeInlineTextarea({ target: el });
};

const clearInlineEditState = () => {
  clearPendingInlineEditMedia();
  inlineEditMediaRemoved.value = false;
  if (inlineEditMediaInputEl.value) inlineEditMediaInputEl.value.value = '';
  editTarget.value = null;
  editDraftBody.value = '';
  editOriginalBody.value = '';
  editErrorMessage.value = '';
  activeEditTextareaEl.value = null;
};

const cancelInlineEdit = () => {
  if (isSubmittingEdit.value) return;
  clearInlineEditState();
};

const onEditTextareaKeydown = (e) => {
  if (e.key === 'Escape') {
    e.preventDefault();
    cancelInlineEdit();
    return;
  }
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    submitInlineEdit();
  }
};

const getCommentBodyForEdit = (commentId) => {
  const c = getCommentById(commentId);
  return c ? String(c.body || '') : '';
};

const getReplyBodyForEdit = (replyId) => {
  const parentCommentId = findParentCommentIdForReply(replyId);
  if (!parentCommentId) return { body: '', parentCommentId: null };

  const idx = findReplyIndex(parentCommentId, replyId);
  if (idx < 0) return { body: '', parentCommentId };

  const reply = repliesByParentId.value?.[parentCommentId]?.[idx] || null;
  return {
    body: reply ? String(reply.body || '') : '',
    parentCommentId,
  };
};

const getReplyReplyBodyForEdit = (replyReplyId) => {
  const parentReplyId = findParentReplyIdForReplyReply(replyReplyId);
  if (!parentReplyId) return { body: '', parentReplyId: null };

  const arr = Array.isArray(replyRepliesByParentId.value?.[parentReplyId])
    ? replyRepliesByParentId.value[parentReplyId]
    : [];
  const idx = arr.findIndex((rr) => Number(rr?.id) === Number(replyReplyId));
  if (idx < 0) return { body: '', parentReplyId };

  const rr = arr[idx] || null;
  return {
    body: rr ? String(rr.body || '') : '',
    parentReplyId,
  };
};

const handleCommentReplyMenuEdit = ({ id, type }) => {
  console.log('[Comment/Reply Menu] edit', { id, type });
  const targetId = Number(id);
  if (!targetId) return;

  clearPendingInlineEditMedia();
  inlineEditMediaRemoved.value = false;
  editErrorMessage.value = '';

  if (type === 'comment') {
    const body = getCommentBodyForEdit(targetId);
    editTarget.value = { id: targetId, type: 'comment', parentCommentId: null, parentReplyId: null };
    editDraftBody.value = body;
    editOriginalBody.value = body;
    focusActiveEditTextarea();
    return;
  }

  if (type === 'reply') {
    const { body, parentCommentId } = getReplyBodyForEdit(targetId);
    if (!parentCommentId) return;

    editTarget.value = { id: targetId, type: 'reply', parentCommentId, parentReplyId: null };
    editDraftBody.value = body;
    editOriginalBody.value = body;
    focusActiveEditTextarea();
    return;
  }

  const { body, parentReplyId } = getReplyReplyBodyForEdit(targetId);
  if (!parentReplyId) return;

  editTarget.value = { id: targetId, type: 'reply-reply', parentCommentId: null, parentReplyId };
  editDraftBody.value = body;
  editOriginalBody.value = body;
  focusActiveEditTextarea();
};

const patchCommentById = (commentId, patch = {}) => {
  const idx = findCommentIndexById(commentId);
  if (idx < 0) return;

  const list = Array.isArray(comments.value) ? [...comments.value] : [];
  const existing = list[idx] || {};
  list[idx] = {
    ...existing,
    ...patch,
  };
  comments.value = list;
};

const patchReplyById = (parentCommentId, replyId, patch = {}) => {
  const idx = findReplyIndex(parentCommentId, replyId);
  if (idx < 0) return;

  const list = Array.isArray(repliesByParentId.value?.[parentCommentId])
    ? [...repliesByParentId.value[parentCommentId]]
    : [];
  const existing = list[idx] || {};
  list[idx] = {
    ...existing,
    ...patch,
  };

  repliesByParentId.value = {
    ...(repliesByParentId.value || {}),
    [parentCommentId]: list,
  };
};

const patchReplyReplyById = (parentReplyId, replyReplyId, patch = {}) => {
  const list = Array.isArray(replyRepliesByParentId.value?.[parentReplyId])
    ? [...replyRepliesByParentId.value[parentReplyId]]
    : [];
  const idx = list.findIndex((rr) => Number(rr?.id) === Number(replyReplyId));
  if (idx < 0) return;

  const existing = list[idx] || {};
  list[idx] = {
    ...existing,
    ...patch,
  };

  replyRepliesByParentId.value = {
    ...(replyRepliesByParentId.value || {}),
    [parentReplyId]: list,
  };
};

const submitInlineEdit = async () => {
  if (isSubmittingEdit.value) return;
  if (!editTarget.value) return;

  const trimmedBody = String(editDraftBody.value || '').trim();
  const original = String(editOriginalBody.value || '').trim();
  const mediaChanged = hasInlineEditMediaChanges.value;
  if (trimmedBody === original && !mediaChanged) {
    clearInlineEditState();
    return;
  }

  isSubmittingEdit.value = true;
  editErrorMessage.value = '';

  const targetId = Number(editTarget.value.id);
  const targetType = String(editTarget.value.type || '');

  try {
    let nextAttachments = null;
    if (pendingInlineEditMedia.value?.file) {
      const uploaded = await uploadCommentMediaFile(pendingInlineEditMedia.value.file);
      if (uploaded === null) {
        editErrorMessage.value = 'Could not upload media. Please try again.';
        return;
      }
      nextAttachments = uploaded;
    } else if (inlineEditMediaRemoved.value) {
      nextAttachments = [];
    }

    const hasBody = trimmedBody.length > 0;
    const hasMediaAfterEdit =
      Array.isArray(nextAttachments)
        ? nextAttachments.length > 0
        : inlineEditExistingMediaItems.value.length > 0;

    if (!hasBody && !hasMediaAfterEdit) {
      editErrorMessage.value = 'Comment cannot be empty.';
      return;
    }

    const requestPayload = { body: trimmedBody };
    if (Array.isArray(nextAttachments)) {
      requestPayload.attachments = nextAttachments;
      requestPayload.attachment = nextAttachments;
      requestPayload.attachment_json = JSON.stringify(nextAttachments);
    }

    const resp = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/comments/${encodeURIComponent(targetId)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestPayload),
      }
    );

    let payload = null;
    const text = await resp.text().catch(() => '');
    if (text) {
      try {
        payload = JSON.parse(text);
      } catch (err) {
        payload = { message: text };
      }
    }

    if (!resp.ok) {
      editErrorMessage.value =
        payload?.error || payload?.message || 'Failed to save comment. Please try again.';
      return;
    }

    const patchedComment =
      payload?.comment && typeof payload.comment === 'object'
        ? payload.comment
        : null;
    const localPatch = {
      body: trimmedBody,
      ...(Array.isArray(nextAttachments) ? { attachments: nextAttachments } : {}),
    };

    if (targetType === 'comment') {
      patchCommentById(targetId, patchedComment || localPatch);
      showFullCommentBodyById[targetId] = false;
    } else if (targetType === 'reply') {
      const parentCommentId = Number(editTarget.value.parentCommentId);
      patchReplyById(parentCommentId, targetId, patchedComment || localPatch);
      showFullReplyBodyById[targetId] = false;
    } else if (targetType === 'reply-reply') {
      const parentReplyId = Number(editTarget.value.parentReplyId);
      patchReplyReplyById(parentReplyId, targetId, patchedComment || localPatch);
      showFullReplyReplyBodyById[targetId] = false;
    }

    clearInlineEditState();
  } catch (err) {
    editErrorMessage.value = 'Network error while saving. Please retry.';
  } finally {
    isSubmittingEdit.value = false;
  }
};

const decrementCommentReplyCount = (commentId) => {
  const idx = findCommentIndexById(commentId);
  if (idx < 0) return;

  const list = Array.isArray(comments.value) ? [...comments.value] : [];
  const c = list[idx] || {};
  const next = Math.max(0, getCommentReplyCount(c) - 1);

  list[idx] = {
    ...c,
    replyCount: next,
    repliesCount: typeof c.repliesCount !== 'undefined' ? next : c.repliesCount,
    replies_count: typeof c.replies_count !== 'undefined' ? next : c.replies_count,
    reply_count: typeof c.reply_count !== 'undefined' ? next : c.reply_count,
  };

  comments.value = list;
};

const removeMapKey = (mapRef, key) => {
  const next = { ...(mapRef.value || {}) };
  delete next[key];
  mapRef.value = next;
};

const removeFromSetRef = (setRef, key) => {
  const next = new Set(setRef.value || []);
  next.delete(key);
  setRef.value = next;
};

const findParentCommentIdForReply = (replyId) => {
  const map = repliesByParentId.value || {};
  const target = Number(replyId);

  for (const key of Object.keys(map)) {
    const commentId = Number(key);
    const list = Array.isArray(map[key]) ? map[key] : [];
    const idx = list.findIndex((r) => Number(r?.id) === target);
    if (idx >= 0) return commentId;
  }

  return null;
};

const findParentReplyIdForReplyReply = (replyReplyId) => {
  const map = replyRepliesByParentId.value || {};
  const target = Number(replyReplyId);

  for (const key of Object.keys(map)) {
    const parentReplyId = Number(key);
    const list = Array.isArray(map[key]) ? map[key] : [];
    const idx = list.findIndex((rr) => Number(rr?.id) === target);
    if (idx >= 0) return parentReplyId;
  }

  return null;
};

const handleCommentReplyMenuDelete = ({ id, type }) => {
  console.log('[Comment/Reply Menu] delete', { id, type });
  const targetId = Number(id);
  if (!targetId) return;
  if (isEditingItem(type, targetId)) {
    clearInlineEditState();
  }

  // Top-level comment
  if (type === 'comment') {
    const existing = Array.isArray(comments.value) ? comments.value : [];
    const nextComments = existing.filter((c) => Number(c?.id) !== targetId);

    if (nextComments.length !== existing.length) {
      comments.value = nextComments;
      localCommentsCount.value = Math.max(0, Number(localCommentsCount.value || 0) - 1);
    }

    removeMapKey(repliesByParentId, targetId);
    removeMapKey(latestReplyByParentId, targetId);
    removeMapKey(isRepliesLoadingByCommentId, targetId);
    removeMapKey(replyTextByCommentId, targetId);
    clearPendingReplyMedia(targetId);
    removeMapKey(replyReplyingToByCommentId, targetId);
    removeMapKey(replyPrefixRemovedByCommentId, targetId);
    removeMapKey(threadLineBottomById, targetId);

    removeFromSetRef(expandedRepliesIds, targetId);

    commentRowRefs.delete(targetId);
    replyAvatarRefs.delete(targetId);
    lastReplyAvatarIdByComment.delete(targetId);
    replyComposerAvatarRefs.delete(targetId);
    viewRepliesRowRefs.delete(targetId);

    if (Number(openReplyForCommentId.value) === targetId) {
      openReplyForCommentId.value = null;
    }
    if (Number(openReplyEmojiForCommentId.value) === targetId) {
      openReplyEmojiForCommentId.value = null;
    }
    return;
  }

  // Reply (depth 1)
  if (type === 'reply') {
    const parentCommentId = findParentCommentIdForReply(targetId);
    if (!parentCommentId) return;

    const current = Array.isArray(repliesByParentId.value?.[parentCommentId])
      ? [...repliesByParentId.value[parentCommentId]]
      : [];
    const next = current.filter((r) => Number(r?.id) !== targetId);

    repliesByParentId.value = {
      ...(repliesByParentId.value || {}),
      [parentCommentId]: next,
    };

    decrementCommentReplyCount(parentCommentId);

    removeMapKey(replyRepliesByParentId, targetId);
    removeMapKey(isReplyRepliesLoadingByParentId, targetId);
    removeMapKey(replyRepliesLoadedByParentId, targetId);
    removeMapKey(replyReplyTextByReplyId, targetId);
    clearPendingReplyReplyMedia(targetId);
    removeMapKey(replyReplyingToByReplyId, targetId);
    removeMapKey(replyReplyPrefixRemovedByReplyId, targetId);
    removeMapKey(replyThreadLineBottomById, targetId);

    removeFromSetRef(expandedReplyRepliesIds, targetId);

    replyRowRefs.delete(targetId);
    replyRepliesBlockRefs.delete(targetId);
    replyReplyAvatarRefs.delete(targetId);
    viewReplyRepliesRowRefs.delete(targetId);

    if (Number(openReplyReplyForReplyId.value) === targetId) {
      openReplyReplyForReplyId.value = null;
    }
    if (Number(openReplyReplyEmojiForReplyId.value) === targetId) {
      openReplyReplyEmojiForReplyId.value = null;
    }

    scheduleRecalcThreadLine(parentCommentId);
    return;
  }

  // Reply-reply (depth 2)
  const parentReplyId = findParentReplyIdForReplyReply(targetId);
  if (!parentReplyId) return;

  const current = Array.isArray(replyRepliesByParentId.value?.[parentReplyId])
    ? [...replyRepliesByParentId.value[parentReplyId]]
    : [];
  const next = current.filter((rr) => Number(rr?.id) !== targetId);

  replyRepliesByParentId.value = {
    ...(replyRepliesByParentId.value || {}),
    [parentReplyId]: next,
  };

  scheduleRecalcReplyThreadLine(parentReplyId);
};

const onCommentReactionUpdated = (payload) => {
  const currentPostId = Number(props.post?.id || 0);
  const payloadPostId = Number(payload?.postId || 0);
  if (currentPostId > 0 && payloadPostId > 0 && currentPostId !== payloadPostId) return;

  const commentId = Number(payload?.commentId || 0);
  if (!Number.isFinite(commentId) || commentId <= 0) return;

  const reactionKey = `react:${commentId}:${String(payload?.createdAt || '')}:${String(payload?.authorUserType || '')}:${Number(payload?.authorUserId || 0)}`;
  if (seenWsCommentReactionKeys.has(reactionKey)) return;
  seenWsCommentReactionKeys.add(reactionKey);

  const patchData = {
    reactions: payload?.reactions,
    reactionUsersByType: payload?.reactionUsersByType,
  };

  if (findCommentIndexById(commentId) >= 0) {
    patchCommentFromReactionResponse(commentId, patchData);
    return;
  }

  const parentCommentId = findParentCommentIdForReply(commentId);
  if (parentCommentId) {
    patchReplyFromReactionResponse(parentCommentId, commentId, patchData);
    return;
  }

  const replyReplyLocation = findReplyReplyLocation(commentId);
  if (replyReplyLocation) {
    patchReplyReplyFromReactionResponse(commentId, patchData);
  }
};




onBeforeUnmount(() => {
  if (replyReplyReactionOpenTimer) clearTimeout(replyReplyReactionOpenTimer);
  if (replyReplyReactionCloseTimer) clearTimeout(replyReplyReactionCloseTimer);
});


onBeforeUnmount(() => {
  if (replyReactionOpenTimer) clearTimeout(replyReactionOpenTimer);
  if (replyReactionCloseTimer) clearTimeout(replyReactionCloseTimer);
});



onMounted(() => {
  window.addEventListener('resize', handleResizeForThreadLines);
  unsubscribeCommentVideoReady = websocketStore.subscribe(
    'newsfeed:commentVideoReady',
    onCommentVideoReady
  );
  unsubscribeCommentCreated = websocketStore.subscribe(
    'newsfeed:commentCreated',
    onCommentCreated
  );
  unsubscribeCommentUpdated = websocketStore.subscribe(
    'newsfeed:commentUpdated',
    onCommentUpdated
  );
  unsubscribeCommentDeleted = websocketStore.subscribe(
    'newsfeed:commentDeleted',
    onCommentDeleted
  );
  unsubscribeCommentReactionUpdated = websocketStore.subscribe(
    'newsfeed:commentReactionUpdated',
    onCommentReactionUpdated
  );
  unsubscribePostReactionUpdated = websocketStore.subscribe(
    'newsfeed:postReactionUpdated',
    onPostReactionUpdated
  );
  unsubscribePostReactionToast = websocketStore.subscribe(
    'newsfeed:postReactionToast',
    onPostReactionUpdated
  );
});

onBeforeUnmount(() => {
    for (const raf of _threadLineRafById.values()) cancelAnimationFrame(raf);
_threadLineRafById.clear();

  window.removeEventListener('resize', handleResizeForThreadLines);

  for (const raf of _replyThreadLineRafById.values()) cancelAnimationFrame(raf);
_replyThreadLineRafById.clear();
  if (typeof unsubscribeCommentVideoReady === 'function') {
    unsubscribeCommentVideoReady();
    unsubscribeCommentVideoReady = null;
  }
  if (typeof unsubscribeCommentCreated === 'function') {
    unsubscribeCommentCreated();
    unsubscribeCommentCreated = null;
  }
  if (typeof unsubscribeCommentUpdated === 'function') {
    unsubscribeCommentUpdated();
    unsubscribeCommentUpdated = null;
  }
  if (typeof unsubscribeCommentDeleted === 'function') {
    unsubscribeCommentDeleted();
    unsubscribeCommentDeleted = null;
  }
  if (typeof unsubscribeCommentReactionUpdated === 'function') {
    unsubscribeCommentReactionUpdated();
    unsubscribeCommentReactionUpdated = null;
  }
  if (typeof unsubscribePostReactionUpdated === 'function') {
    unsubscribePostReactionUpdated();
    unsubscribePostReactionUpdated = null;
  }
  if (typeof unsubscribePostReactionToast === 'function') {
    unsubscribePostReactionToast();
    unsubscribePostReactionToast = null;
  }

});


// keep local reaction state in sync if parent replaces props.post
watch(
  () => props.post?.reactionsSummary,
  (val) => {
    if (val && typeof val === 'object') {
      localReactionsSummary.value = { ...val };
      localReactionsCount.value = Number(val.total) || 0;
    }
  }
);

watch(
  () => props.post?.reactionNamesByType,
  (val) => {
    const source =
      (val && typeof val === 'object')
        ? val
        : props.post?.reactionUsersByType;

    if (source && typeof source === 'object') {
      localReactionNamesByType.value = JSON.parse(JSON.stringify(source));
    } else {
      localReactionNamesByType.value = { like: [], love: [], care: [], haha: [], wow: [], sad: [], angry: [] };
    }
  }
);

watch(
  () => props.post?.reactionUsersByType,
  (val) => {
    if (val && typeof val === 'object') {
      localReactionNamesByType.value = JSON.parse(JSON.stringify(val));
    }
  }
);

onBeforeUnmount(() => {
  if (reactionOpenTimer) clearTimeout(reactionOpenTimer);
  if (reactionCloseTimer) clearTimeout(reactionCloseTimer);
  clearPendingCommentMedia();
  Object.keys(pendingReplyMediaByCommentId.value || {}).forEach((id) => clearPendingReplyMedia(id));
  Object.keys(pendingReplyReplyMediaByReplyId.value || {}).forEach((id) => clearPendingReplyReplyMedia(id));
});

onBeforeUnmount(() => {
  for (const timer of commentMediaSkeletonFallbackTimers.values()) clearTimeout(timer);
  commentMediaSkeletonFallbackTimers.clear();
});


const close = () => emit('close');
const noop = () => {};

</script>



<style scoped>
.nf-comment-fade-in {
  animation: nf-comment-fade-in 220ms ease-out both;
}

@keyframes nf-comment-fade-in {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.nf-media-shell {
  position: relative;
  display: inline-block;
  width: fit-content;
  max-width: 100%;
  vertical-align: top;
}

.nf-media-shell--loading::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(90deg, #eef2f7 0%, #e3e8ef 50%, #eef2f7 100%);
  background-size: 220% 100%;
  animation: nf-media-shimmer 1.1s linear infinite;
  z-index: 2;
  pointer-events: none;
}

@keyframes nf-media-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -20% 0; }
}

.nf-media-content-hidden {
  opacity: 0;
}

.nf-comment-video-frame {
  width: 100%;
  min-width: 300px;
  max-width: 520px;
  transition: opacity 220ms ease;
}

.inline-edit-textarea,
.inline-edit-textarea:focus,
.inline-edit-textarea:focus-visible,
.inline-edit-textarea:active {
  outline: none !important;
  box-shadow: none !important;
  border: 0 !important;
  -webkit-appearance: none;
  appearance: none;
}

.inline-edit-textarea::-moz-focus-inner {
  border: 0;
}

/* ─────────────────────────────────────────────
   Creator layout scrollbar (right column only)
   ─────────────────────────────────────────── */

/* Firefox base */
.app-scroll {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  /* try to keep layout width stable */
  scrollbar-gutter: stable;
}

/* WebKit base (Chrome / Edge / Safari) */
.app-scroll::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

/* Hide old-school buttons/arrows (where they exist) */
.app-scroll::-webkit-scrollbar-button {
  display: none;
  width: 0;
  height: 0;
  background: transparent;
}

/* Track fully invisible so it looks “on top” of content */
.app-scroll::-webkit-scrollbar-track {
  background: transparent;
}

/* Thumb invisible by default */
.app-scroll::-webkit-scrollbar-thumb {
  background-color: transparent;
  border-radius: 999px;
}

/* On hover over the scroll area: fade thumb in */
.app-scroll:hover {
  scrollbar-color: rgba(145, 150, 156, 0.541) transparent; /* Firefox */
}

.app-scroll:hover::-webkit-scrollbar-thumb {
  background-color: rgba(140, 143, 148, 0.479);
}

/* Slightly stronger when directly hovering the thumb */
.app-scroll:hover::-webkit-scrollbar-thumb:hover {
  background-color: rgba(203, 213, 225, 0.65);
}

/* LIKE icon in action bar (sprite-based) */
.newsfeed-like-sprite {
  display: inline-block;
  width: 22px;
  height: 22px;

  /* path to your sprite strip */
  background-image: url('@/assets/media/images/newsfeed-icons.png');
  background-repeat: no-repeat;
  background-size: 27px auto;

  /* 35th icon down (approx) – tweak this if it's a bit off */
  /* Each icon row is ~34px tall: 34 * 34 = 1156 */
  background-position: 0 -775px;
}

/* COMMENT icon in action bar (sprite-based) */
.newsfeed-comment-sprite {
  display: inline-block;
  width: 22px;
  height: 22px;

  /* path to your sprite strip */
  background-image: url('@/assets/media/images/newsfeed-icons.png');
  background-repeat: no-repeat;
  background-size: 27px auto;

  /* 35th icon down (approx) – tweak this if it's a bit off */
  /* Each icon row is ~34px tall: 34 * 34 = 1156 */
  background-position: 0 -525px;
}

/* SEND icon in action bar (sprite-based) */
.newsfeed-send-sprite {
  display: inline-block;
  width: 22px;
  height: 22px;

  /* path to your sprite strip */
  background-image: url('@/assets/media/images/newsfeed-icons.png');
  background-repeat: no-repeat;
  background-size: 27px auto;

  /* 35th icon down (approx) – tweak this if it's a bit off */
  /* Each icon row is ~34px tall: 34 * 34 = 1156 */
  background-position: 0 -888px;
}

/* COMMENT SEND icon in comment area (sprite-based) */
.newsfeed-comment-send-sprite {
  display: inline-block;
  width: 22px;
  height: 22px;

  /* path to your sprite strip */
  background-image: url('@/assets/media/images/newsfeed-icons-2.png');
  background-repeat: no-repeat;
  background-size: 27px auto;

  /* 35th icon down (approx) – tweak this if it's a bit off */
  /* Each icon row is ~34px tall: 34 * 34 = 1156 */
  background-position: 0 -350px;
}

.newsfeed-like-sprite--active {
  /* Start from a guess near your blue thumb;
     tweak this Y value until you hit the exact frame */

     display: inline-block;
  width: 22px;
  height: 22px;

  background-image: url('@/assets/media/images/newsfeed-icons.png');
  background-repeat: no-repeat;
  background-size: 27px auto;

  background-position: 0 -686px;
}
</style>

